import jsPDF from 'jspdf';
import 'jspdf/dist/jspdf.es.min.js';
import './pdfWorkerConfig'; // CRITICAL: Import worker configuration to initialize PDF.js worker


import { format, eachDayOfInterval, eachMonthOfInterval, startOfYear, endOfYear, startOfMonth, endOfMonth, getDate, getMonth, isLeapYear, startOfWeek, endOfWeek, addWeeks, isSameYear, getDayOfYear } from 'date-fns';
import { getIcon } from './iconLibrary';

import { createPlannerAliasManager } from './iconAliasManager';
import { fontService } from '../services/fontService';
import { getQuoteByDayOfYear } from './quoteLibrary';

import { convertSvgToPng } from './convertSvgToPng';
import { getPdfBackgroundImage } from '../services/galleryService';
 
const nameFontSize=55;


const footerText = `Copyright © 2026 StillFlow LLC. All Rights Reserved | Created on stillflow.ink`;
// ==================== ICON QUALITY CONFIGURATION ====================
/**
 * Configurable icon quality settings for PDF generation
 * 
 * Controls the resolution (pixel size) of icons used throughout the planner.
 * Higher resolution = better quality but larger file size and slower generation.
 * 
 * Quality Levels:
 * - VERY_HIGH: Maximum quality, largest file size (~32MB for full planner)
 * - HIGH: Excellent quality, balanced file size (~20-22MB for full planner)
 * - MEDIUM: Good quality, standard file size (~17MB for full planner) **DEFAULT**
 * - LOW: Acceptable quality, smallest file size (~12-14MB for full planner)
 * 
 * To change quality: Modify the ICON_QUALITY constant below
 * Example: const ICON_QUALITY = 'HIGH';
 */


// Quality level definitions with pixel resolutions
const ICON_QUALITY_LEVELS = {
  VERY_HIGH: {
    coverIcon: 600,   // Cover page calendar watermark
    dailyIcons: 64    // Daily page icons (navigation, trackers, weather, mood)
  },
  HIGH: {
    coverIcon: 100,
    dailyIcons: 32
  },
  MEDIUM: {
    coverIcon: 50,
    dailyIcons: 24
  },
  LOW: {
    coverIcon: 30,
    dailyIcons: 10
  }
};

const getStillFlowLogo = (logoColor='#ffffffff') => {
  let lightLogoSVG = `<?xml version="1.0" standalone="no"?>
    <!DOCTYPE svg PUBLIC "-//W3C//DTD SVG 20010904//EN"
    "http://www.w3.org/TR/2001/REC-SVG-20010904/DTD/svg10.dtd">
    <svg version="1.0" xmlns="http://www.w3.org/2000/svg"
    width="1536.000000pt" height="1024.000000pt" viewBox="0 0 1536.000000 1024.000000"
    preserveAspectRatio="xMidYMid meet">
    <metadata>
    StillFlow LLC Logo.
    </metadata>
    <g transform="translate(0.000000,1024.000000) scale(0.100000,-0.100000)"
    fill="${logoColor}" stroke="none">
    <path d="M6305 8255 c-379 -61 -665 -244 -793 -507 -60 -122 -80 -199 -89
    -328 -9 -151 27 -300 108 -438 52 -88 208 -239 324 -314 154 -100 175 -109
    289 -119 404 -37 896 -131 1316 -253 102 -29 187 -52 189 -50 8 7 -32 89 -71
    148 -90 133 -239 252 -445 355 -120 60 -228 104 -520 211 -373 138 -499 199
    -597 291 -115 108 -164 233 -152 389 18 243 171 415 441 496 162 48 377 34
    555 -36 224 -88 432 -298 580 -586 28 -54 56 -115 62 -136 10 -34 15 -38 45
    -38 40 0 38 -18 14 213 -28 260 -51 492 -51 515 0 16 -21 27 -117 61 -131 46
    -309 92 -454 116 -134 24 -515 29 -634 10z"/>
    <path d="M7677 8163 c-2 -5 -3 -19 -3 -33 1 -24 4 -25 86 -33 160 -14 233 -46
    273 -120 l22 -42 5 -909 5 -909 85 -24 c157 -44 394 -103 412 -103 17 0 18 16
    18 246 l0 246 308 -5 c237 -3 319 -8 359 -20 181 -53 265 -153 306 -364 l20
    -103 39 0 38 0 0 535 0 535 -34 0 -34 0 -21 -87 c-40 -165 -111 -276 -206
    -325 -89 -46 -125 -51 -457 -55 l-318 -5 0 731 0 731 283 1 c441 2 529 -4 649
    -41 212 -66 392 -279 523 -620 31 -79 35 -85 64 -88 25 -3 31 0 31 16 0 20
    -40 435 -65 682 -8 80 -15 151 -15 158 0 9 -244 12 -1184 12 -652 0 -1187 -3
    -1189 -7z"/>
    <path d="M5410 6410 c-363 -23 -757 -98 -1043 -200 -65 -24 -116 -44 -115 -46
    2 -2 37 6 78 16 112 29 308 66 460 87 210 28 787 25 1038 -5 459 -56 865 -158
    1111 -279 96 -47 125 -68 190 -132 121 -121 168 -245 157 -416 -14 -234 -139
    -418 -351 -520 -121 -58 -196 -76 -347 -82 -150 -7 -255 7 -388 49 -357 114
    -647 395 -865 836 -59 121 -73 142 -92 142 -13 0 -23 -5 -23 -10 0 -13 76
    -656 93 -780 l12 -94 80 -34 c422 -177 924 -263 1290 -222 330 38 583 145 773
    324 179 170 276 373 288 606 3 52 7 100 9 107 3 9 18 8 57 -6 29 -10 95 -28
    146 -41 l92 -22 2 -172 c3 -226 -1 -413 -8 -456 -21 -125 -127 -193 -316 -204
    l-83 -4 0 -29 0 -28 673 -3 672 -2 0 28 0 29 -109 11 c-167 17 -240 53 -282
    139 -23 46 -24 58 -27 321 -3 261 -3 274 15 268 24 -7 248 -41 388 -58 148
    -18 777 -18 940 0 370 40 758 130 1053 244 57 22 102 42 100 44 -2 2 -33 -6
    -69 -16 -190 -59 -502 -115 -779 -141 -193 -18 -608 -15 -825 5 -529 50 -923
    134 -1655 352 -212 63 -421 126 -465 139 -663 197 -1338 289 -1875 255z"/>
    <path d="M6510 4139 c-23 -10 -106 -28 -192 -43 -56 -10 -48 -32 13 -38 59 -6
    70 -15 78 -64 3 -20 4 -275 1 -566 -6 -598 2 -551 -87 -564 -26 -4 -42 -11
    -40 -18 3 -8 63 -11 202 -11 139 0 199 3 202 11 2 7 -15 14 -42 18 -84 13 -80
    -25 -81 671 -2 659 1 626 -54 604z"/>
    <path d="M7030 4139 c-19 -8 -211 -49 -227 -49 -2 0 -3 -7 -3 -15 0 -10 11
    -15 35 -15 55 0 84 -19 95 -61 7 -24 10 -229 8 -564 l-3 -527 -23 -18 c-12
    -10 -40 -21 -62 -25 -25 -4 -40 -12 -40 -21 0 -12 34 -14 200 -14 153 0 200 3
    200 13 0 6 -21 19 -47 27 -32 10 -52 23 -60 40 -10 19 -13 168 -13 633 0 572
    -1 607 -17 606 -10 0 -29 -5 -43 -10z"/>
    <path d="M8607 4135 c-21 -8 -66 -19 -100 -25 -108 -19 -117 -21 -117 -36 0
    -9 11 -14 29 -14 40 0 88 -19 98 -38 10 -21 17 -939 8 -1039 -4 -39 -12 -79
    -18 -88 -7 -9 -34 -21 -60 -28 -93 -24 -57 -32 151 -35 170 -2 202 0 202 12 0
    9 -15 17 -40 21 -25 4 -47 16 -60 32 -20 25 -20 40 -20 639 0 688 7 628 -73
    599z"/>
    <path d="M4425 4100 c-201 -43 -325 -173 -325 -339 0 -184 100 -279 375 -356
    269 -76 346 -144 332 -293 -14 -157 -131 -247 -322 -247 -76 0 -99 4 -145 26
    -106 49 -165 125 -205 262 -16 55 -27 68 -40 48 -3 -6 0 -82 7 -170 l12 -160
    40 -15 c92 -35 182 -49 306 -50 138 0 219 16 302 61 215 116 271 401 111 555
    -52 50 -140 89 -312 138 -203 58 -253 86 -292 162 -27 53 -25 140 4 198 25 50
    94 103 155 120 64 17 175 12 238 -11 100 -38 142 -90 174 -217 8 -32 15 -42
    28 -40 15 3 17 18 15 138 l-3 135 -87 28 c-72 24 -110 30 -208 33 -66 2 -138
    -1 -160 -6z"/>
    <path d="M7332 4068 c2 -6 27 -17 54 -23 66 -15 71 -19 84 -63 15 -55 13
    -1016 -3 -1062 -11 -32 -17 -36 -69 -49 -36 -8 -58 -19 -58 -27 0 -12 40 -14
    225 -14 189 0 225 2 225 14 0 10 -16 17 -47 22 -28 4 -56 16 -70 30 -23 23
    -23 25 -23 274 l0 250 180 0 c239 0 259 -8 277 -117 3 -19 12 -33 19 -33 11 0
    14 34 14 181 0 162 -2 180 -16 177 -11 -2 -21 -21 -29 -52 -21 -83 -29 -86
    -252 -86 l-193 0 0 260 0 260 219 0 c300 0 309 -4 351 -167 6 -26 17 -43 25
    -43 13 0 15 24 15 140 l0 140 -466 0 c-355 0 -465 -3 -462 -12z"/>
    <path d="M5915 4061 c-38 -16 -58 -49 -59 -96 -1 -36 4 -50 27 -72 37 -37 103
    -39 141 -4 81 76 -6 214 -109 172z"/>
    <path d="M5311 3926 c-7 -8 -32 -24 -56 -35 l-44 -21 -3 -112 -3 -112 -70 -36
    c-92 -46 -93 -64 -2 -61 l67 2 0 -283 c0 -292 5 -344 37 -387 40 -53 69 -66
    157 -69 97 -5 162 18 216 73 39 41 26 51 -31 25 -52 -23 -119 -26 -158 -5 -55
    28 -61 65 -61 370 l0 275 125 0 125 0 0 24 c0 13 3 31 6 40 5 14 -10 16 -125
    16 l-131 0 0 155 c0 145 -1 155 -19 155 -10 0 -24 -6 -30 -14z"/>
    <path d="M5910 3639 c-58 -15 -117 -28 -132 -28 -21 -1 -28 -6 -28 -21 0 -15
    7 -20 29 -20 36 0 83 -17 93 -33 4 -7 8 -150 8 -318 0 -304 0 -306 -22 -326
    -13 -11 -40 -24 -60 -27 -24 -4 -38 -12 -38 -21 0 -13 32 -15 200 -15 166 0
    200 2 200 14 0 9 -16 17 -41 21 -49 9 -79 35 -80 73 0 15 -1 184 -2 376 -1
    224 -6 350 -12 351 -5 1 -57 -11 -115 -26z"/>
    <path d="M9233 3641 c-128 -35 -238 -126 -291 -239 -25 -52 -27 -68 -27 -177
    0 -117 1 -123 34 -189 42 -87 99 -144 182 -184 214 -105 493 -40 620 144 102
    148 94 368 -18 509 -37 47 -126 109 -187 129 -80 27 -227 30 -313 7z m236 -65
    c120 -61 190 -220 178 -406 -13 -197 -95 -298 -253 -308 -65 -4 -80 -2 -129
    22 -125 62 -195 219 -182 409 7 104 28 164 78 220 54 62 108 86 192 87 53 0
    78 -5 116 -24z"/>
    <path d="M9850 3623 c0 -7 16 -19 35 -29 26 -12 41 -31 64 -78 30 -63 283
    -656 294 -688 9 -26 54 -23 70 5 8 12 63 131 123 264 59 133 109 241 110 240
    3 -3 210 -490 217 -509 4 -11 17 -18 31 -18 32 0 31 -3 201 395 74 174 142
    330 151 346 8 17 30 37 49 46 19 9 35 21 35 27 0 15 -272 14 -277 -1 -2 -6 13
    -14 34 -18 44 -8 73 -30 73 -56 0 -36 -195 -529 -209 -529 -3 0 -25 44 -48 98
    -23 53 -74 171 -113 261 -39 90 -70 173 -68 185 2 15 15 25 51 35 26 8 47 21
    47 28 0 12 -32 14 -182 11 -185 -3 -226 -13 -157 -37 49 -17 65 -36 99 -117
    l31 -75 -72 -162 c-39 -89 -79 -178 -89 -196 l-17 -35 -16 30 c-20 40 -185
    427 -202 475 -19 52 -11 67 42 80 24 7 42 17 40 23 -6 16 -347 15 -347 -1z"/>
    </g>
    </svg>`;

  
  return lightLogoSVG;
};





// ⚙️ CONFIGURATION: Set your desired quality level here
const ICON_QUALITY = 'LOW'; // Options: 'VERY_HIGH', 'HIGH', 'MEDIUM', 'LOW'

// Get configured icon sizes based on quality level
const ICON_SIZES = ICON_QUALITY_LEVELS?.[ICON_QUALITY] || ICON_QUALITY_LEVELS?.MEDIUM;

console.log(`🎨 Icon Quality: ${ICON_QUALITY}`);
console.log(`   - Cover icon: ${ICON_SIZES?.coverIcon}px`);
console.log(`   - Daily icons: ${ICON_SIZES?.dailyIcons}px`);
console.log(`   - Estimated file size impact: ${
  ICON_QUALITY === 'VERY_HIGH' ? '~32MB (highest)' :
  ICON_QUALITY === 'HIGH' ? '~20-22MB (balanced)' :
  ICON_QUALITY === 'MEDIUM' ? '~17MB (standard)' :
  '~12-14MB (smallest)'
}`);

// ====================================================================

// Configure the background text size and position.
let backgroundText = "";
const backgroundTextX = 150;
const backgroundTextY = 220;
const backgroundTextSize = 150;

//Size of the header text
const headerTextSize = 12;

// Right header position helper 
const rightHeaderY = 5;

// Week header (right side of page) Y positioning helper.
const weekTextY = 20; // UPDATED: Changed from 25 to 20 to align with date header
/**
 * Theme color mappings from UI selection to RGB values
 */
const getThemeColors = (themeId) => {
  const themes = {
    classic: {
      primary: { r: 30, g: 58, b: 95 },
      secondary: { r: 74, g: 144, b: 164 },
      accent: { r: 226, g: 232, b: 240 }
    },
    warm: {
      primary: { r: 217, g: 119, b: 6 },
      secondary: { r: 245, g: 158, b: 11 },
      accent: { r: 254, g: 243, b: 199 }
    },
    nature: {
      primary: { r: 5, g: 150, b: 105 },
      secondary: { r: 16, g: 185, b: 129 },
      accent: { r: 209, g: 250, b: 229 }
    },
    elegant: {
      primary: { r: 124, g: 58, b: 237 },
      secondary: { r: 167, g: 139, b: 250 },
      accent: { r: 237, g: 233, b: 254 }
    },
    coral: {
      primary: { r: 255, g: 107, b: 107 },
      secondary: { r: 255, g: 142, b: 142 },
      accent: { r: 255, g: 229, b: 229 }
    },
    monochrome: {
      primary: { r: 55, g: 65, b: 81 },
      secondary: { r: 107, g: 114, b: 128 },
      accent: { r: 243, g: 244, b: 246 }
    }
  };
  
  return themes?.[themeId] || themes?.classic;
};

/**
 * Paper size mappings from UI selection to jsPDF format
 */
const getPaperSize = (sizeId) => {
  const sizes = {
    'a4': 'a4',
    'a5': 'a5',
    'letter': 'letter',
    'pocket': [105, 148], // Custom size in mm
    'tabloid': 'tabloid'
  };
  
  return sizes?.[sizeId] || 'letter';
};

/**
 * Font family mappings - UPDATED to use database fonts directly
 * Now accepts fontName parameter which can be title font or content font
 */
const getFontFamily = (fontName, fontType = 'content') => {
  // If no font specified, use default
  if (!fontName) {
    return fontType === 'title' ? 'Roboto' : 'Roboto';
  }
  
  // Convert font name to proper case for jsPDF
  // Database stores: lato, opensans, roboto, smoochsans, bantham, beaurivage, forum, islandmoments
  // jsPDF expects: Lato, OpenSans, Roboto, SmoochSans, Bantham, Beaurivage, Forum, IslandMoments
  
  const fontMap = {
    // Content fonts (content_usage=true)
    'lato': 'lato',
    'opensans': 'opensans',
    'roboto': 'roboto',
    'smoochsans': 'smoochsans',
    'notosans': 'notosans',
    // Title fonts (content_usage=false)
    'bantham': 'bantham',
    'beaurivage': 'beaurivage',
    'forum': 'forum',
    'islandmoments': 'islandmoments',
    'thenautigal': 'thenautigal',
    'windsong': 'windsong',
    'caveat': 'caveat',
    'serif': 'times',
    'mono': 'courier',
    'tangerine': 'tangerine',
    'doto': 'doto',
    'fuzzybubbles': 'fuzzybubbles',
    'inter': 'roboto',
    'inriaserif': 'inriaserif',
    'gluten': 'gluten',
    'mountainsofchristmas':'mountainsofchristmas'
  };
  
  return fontMap?.[fontName?.toLowerCase()] || 'helvetica';
};

/**
 * Pre-calculates all page numbers before PDF generation WITH CONDITIONAL LOGIC
 * CRITICAL CHANGE: Now respects monthPlanner, weekPlanner, and dailyPlanner flags
 * UPDATED: Added tasks page references after schedule pages and before journal pages
 */
const calculatePageReferences = (year, includeYearView, selectedHabits = [], layoutConfig = {}) => {
  console.log(`🔢 Calculating page references for ${year} with CONDITIONAL GENERATION`);
  console.log(`📋 Layout Configuration:`, JSON.stringify(layoutConfig));
  
  
  const pageLinks = new Map();
  let currentPage = 1;
  
  // Extract layout flags (use layout as primary source)
  const monthPlannerEnabled = layoutConfig?.monthPlanner || false;
  const weekPlannerEnabled = layoutConfig?.weekPlanner || false;
  const dailyPlannerEnabled = layoutConfig?.dailyPlanner?.enabled || false;
  
  // Extract daily page type flags
  const scheduleEnabled = dailyPlannerEnabled && (layoutConfig?.dailyPlanner?.schedule || false);
  const taskEnabled = dailyPlannerEnabled && (layoutConfig?.dailyPlanner?.task || false);
  const combinationEnabled = dailyPlannerEnabled && (layoutConfig?.dailyPlanner?.combination || false);
  const journalEnabled = dailyPlannerEnabled && (layoutConfig?.dailyPlanner?.journal?.enabled || false);
  //const lifeHackEnabled = dailyPlannerEnabled && (layoutConfig?.dailyPlanner?.lifeHack?.enabled || false); //wasn't working because was not in dailyPlanner hiearchy. See line below
  const lifeHackEnabled = dailyPlannerEnabled && (layoutConfig?.lifeHack?.enabled || false);
  
  // Validate: If dailyPlanner is enabled, at least one daily page type must be selected
  if (dailyPlannerEnabled && !scheduleEnabled && !taskEnabled && !combinationEnabled && !journalEnabled && !lifeHackEnabled) {
    const errorMsg = '❌ VALIDATION ERROR: dailyPlanner.enabled is true but no daily page types are selected';
    console.error(errorMsg);
    throw new Error(errorMsg);
  }
  
  console.log(`✅ Monthly pages: ${monthPlannerEnabled ? 'ENABLED' : 'DISABLED'}`);
  console.log(`✅ Weekly pages: ${weekPlannerEnabled ? 'ENABLED' : 'DISABLED'}`);
  console.log(`✅ Daily pages: ${dailyPlannerEnabled ? 'ENABLED' : 'DISABLED'}`);
  if (dailyPlannerEnabled) {
    console.log(`   - Schedule: ${scheduleEnabled || combinationEnabled ? 'ENABLED' : 'DISABLED'}`);
    console.log(`   - Tasks: ${taskEnabled ? 'ENABLED' : 'DISABLED'}`);
    console.log(`   - Journal: ${journalEnabled ? 'ENABLED' : 'DISABLED'}`);
    console.log(`   - LifeHack: ${lifeHackEnabled ? 'ENABLED' : 'DISABLED'}`);
  }
  
  // Page 1: Title Page (ALWAYS)
  pageLinks?.set('title', currentPage);
  console.log(`Page ${currentPage}: Cover`);
  currentPage++;
  
  // Page 2: Index Page (ALWAYS)
  pageLinks?.set('index', currentPage);
  console.log(`Page ${currentPage}: Index`);
  currentPage++;
  
    // Page 2: Index Page (ALWAYS)
  pageLinks?.set('instruction', currentPage);
  console.log(`Page ${currentPage}: Instruction`);
  currentPage++;

  // Habit Tracker Pages (before year overview): One page per selected habit (ALWAYS IF HABITS CONFIGURED)
  const enabledHabits = (selectedHabits || [])?.filter(h => h?.enabled && h?.name);
  enabledHabits?.forEach((habit, index) => {
    pageLinks?.set(`habit-${index}`, currentPage);
    console.log(`Page ${currentPage}: Habit tracker ${index + 1}`);
    currentPage++;
  });
  
  // Year Overview Page (CONDITIONAL - if included)
  if (includeYearView) {
    pageLinks?.set('year-overview', currentPage);
    console.log(`Page ${currentPage}: Year overview`);
    currentPage++;
  }
  
  // Calculate all weeks for the year with Sunday start (US format)
  const yearStart = new Date(year, 0, 1);
  const yearEnd = new Date(year, 11, 31);
  
  // Build complete week data structure
  const allWeeks = [];
  let currentWeekStart = startOfWeek(yearStart, { weekStartsOn: 0 }); // Sunday
  let weekNumber = 1;
  
  while (currentWeekStart <= yearEnd || weekNumber <= 53) {
    const weekEnd = endOfWeek(currentWeekStart, { weekStartsOn: 0 });
    
    // Include week if any day falls in target year
    if (isSameYear(currentWeekStart, yearStart) || isSameYear(weekEnd, yearStart)) {
      // Determine which month this week belongs to based on Sunday
      const sundayDate = currentWeekStart;
      let assignedMonth;
      
      // Special case: Week 1 always assigned to January (month 0)
      if (weekNumber === 1) {
        assignedMonth = 0;
      } else {
        // Assign to month where Sunday falls
        assignedMonth = sundayDate?.getMonth();
        
        // If Sunday is in previous/next year, assign to appropriate boundary month
        if (sundayDate?.getFullYear() < year) {
          assignedMonth = 0; // Assign to January if Sunday before year
        } else if (sundayDate?.getFullYear() > year) {
          assignedMonth = 11; // Assign to December if Sunday after year
        }
      }
      
      allWeeks?.push({
        weekNumber,
        start: currentWeekStart,
        end: weekEnd,
        assignedMonth, // Month index (0-11)
        sunday: sundayDate
      });
    }
    
    currentWeekStart = addWeeks(currentWeekStart, 1);
    weekNumber++;
    
    if (weekNumber > 54) break; // Safety limit
  }
  
  // Group weeks by assigned month
  const weeksByMonth = {};
  for (let m = 0; m < 12; m++) {
    weeksByMonth[m] = allWeeks?.filter(w => w?.assignedMonth === m);
  }
  
  // CONDITIONAL LOGIC: Generate page references for organizational structure (month → week pages)
  let monthPageCount = 0;
  let weekPageCount = 0;
  
  if (monthPlannerEnabled || weekPlannerEnabled) {
    console.log('📝 Generating organizational structure (conditional: month and/or week pages)...');
    
    for (let monthIndex = 0; monthIndex < 12; monthIndex++) {
      // Month page (CONDITIONAL)
      if (monthPlannerEnabled) {
        pageLinks?.set(`month-${monthIndex}`, currentPage);
        console.log(`Page ${currentPage}: ${format(new Date(year, monthIndex, 1), 'MMMM')} month`);
        currentPage++;
        monthPageCount++;
      }
      
      // Get weeks assigned to this month
      const monthWeeks = weeksByMonth?.[monthIndex] || [];
      
      // Week pages (CONDITIONAL)
      if (weekPlannerEnabled) {
        for (const weekData of monthWeeks) {
          pageLinks?.set(`week-${weekData?.weekNumber}`, currentPage);
          console.log(`Page ${currentPage}: Week ${weekData?.weekNumber}`);
          currentPage++;
          weekPageCount++;
        }
      }
    }
  } else {
    console.log('⏭️  Skipping monthly and weekly pages (both disabled)');
  }
  
  // CONDITIONAL LOGIC: Generate daily page references for ALL calendar days (Jan 1 - Dec 31)
  // NEW ORDER: Schedule → Tasks → Journal → Life Hack
  let dailyPageCount = 0;
  
  if (dailyPlannerEnabled) {
    console.log('📅 Generating daily page references (conditional based on daily page types)...');
    const allYearDays = eachDayOfInterval({ start: yearStart, end: yearEnd });
    
    for (const day of allYearDays) {
      let monthIndex = getMonth(day);
      const dayOfMonth = getDate(day);
      
      // Daily Schedule Page (CONDITIONAL: schedule OR combination)
      if (scheduleEnabled || combinationEnabled) {
        pageLinks?.set(`day-schedule-${monthIndex}-${dayOfMonth}`, currentPage);
        currentPage++;
        dailyPageCount++;
      }
      
      // Daily Tasks Page (CONDITIONAL)
      if (taskEnabled) {
        pageLinks?.set(`day-tasks-${monthIndex}-${dayOfMonth}`, currentPage);
        currentPage++;
        dailyPageCount++;
      }
      
      // Daily Journal Page (CONDITIONAL)
      if (journalEnabled) {
        pageLinks?.set(`day-journal-${monthIndex}-${dayOfMonth}`, currentPage);
        currentPage++;
        dailyPageCount++;
      }
      
      // Daily Life Hack Page (CONDITIONAL)
      if (lifeHackEnabled) {
        pageLinks?.set(`day-lifehack-${monthIndex}-${dayOfMonth}`, currentPage);
        currentPage++;
        dailyPageCount++;
      }
    }
    
    console.log(`✅ Generated ${dailyPageCount} daily pages for ${allYearDays?.length} calendar days`);
  } else {
    console.log('⏭️  Skipping ALL daily pages (dailyPlanner disabled)');
  }
  
  // Calculate totals
  const totalDays = eachDayOfInterval({ start: yearStart, end: yearEnd })?.length;
  const totalPages = currentPage - 1;
  
  console.log(`📊 Page calculation complete:`);
  console.log(`   Total pages: ${totalPages}`);
  console.log(`   Habit pages: ${enabledHabits?.length}`);
  console.log(`   Month pages: ${monthPageCount}`);
  console.log(`   Week pages: ${weekPageCount}`);
  console.log(`   Daily pages: ${dailyPageCount}`);
  console.log(`   Days: ${totalDays} (${isLeapYear(yearStart) ? 'LEAP YEAR' : 'Regular year'})`);
  
  return {
    pageLinks,
    totalPages,
    totalDays,
    totalWeeks: allWeeks?.length,
    weeks: allWeeks,
    weeksByMonth,
    isLeapYear: isLeapYear(yearStart),
    habitPages: enabledHabits?.length,
    // Return configuration for reference
    layoutConfig: {
      monthPlannerEnabled,
      weekPlannerEnabled,
      dailyPlannerEnabled,
      scheduleEnabled,
      taskEnabled,
      combinationEnabled,
      journalEnabled,
      lifeHackEnabled
    }
  };
};

/**
 * Generates dynamic time slot array based on format and range
 * UPDATED: Added support for 30-minute markings
 * @param {string} format - Time format: '24h', '12h-short', or '12h-long'
 * @param {number} startHour - Start hour (0-23)
 * @param {number} endHour - End hour (0-24)
 * @param {boolean} include30MinMarks - Whether to include 30-minute markings (optional)
 * @returns {object[]} Array of time slot objects with time and isHalfHour properties
 */
const generateTimeSlots = (format, startHour, endHour, include30MinMarks = false) => {
  const slots = [];
  
  // Ensure we have at least 8 hours
  const actualEndHour = Math.max(endHour, startHour + 8);
  
  for (let hour = startHour; hour <= actualEndHour; hour++) {
    let timeString = '';
    
    switch (format) {
      case '24h':
        timeString = `${hour?.toString()?.padStart(2, '0')}:00`;
        break;
        
      case '12h-short':
        const hour12Short = hour > 12 ? hour - 12 : (hour === 0 ? 12 : hour);
        const periodShort = hour < 12 ? 'AM' : 'PM';
        timeString = `${hour12Short} ${periodShort}`;
        break;
        
      case '12h-long':
      default:
        const hour12Long = hour > 12 ? hour - 12 : (hour === 0 ? 12 : hour);
        const periodLong = hour < 12 ? 'AM' : 'PM';
        timeString = `${hour12Long}:00 ${periodLong}`;
        break;
    }
    
    // Add full hour slot
    slots?.push({ time: timeString, isHalfHour: false });
    
    // Add 30-minute marking if enabled and not the last hour
    if (include30MinMarks && hour < actualEndHour) {
      slots?.push({ time: '', isHalfHour: true });
    }
  }
  
  return slots;
};


/**
 * Main PDF generation function with CONDITIONAL PAGE GENERATION
 * CRITICAL CHANGE: Now respects layoutConfig for conditional generation
 * UPDATED: Added optional progress logging with PDF_PROGRESS prefix
 */
export const generatePlannerPDF = async ({ plannerData, specifications, includeYearView = true, mode = 'preview' }) => {
  console.log('PDF_PROGRESS: Initializing PDF generator');
  console.log(`Planner Data: ${JSON.stringify(plannerData)}`);
  
  try {
    // Extract year from personalization or fallback
    const year = plannerData?.personalization?.year || plannerData?.year || new Date()?.getFullYear();

    //Extract background watermark text from personalization or fallback.
    
     backgroundText = plannerData?.personalization?.backgroundText || " ";

    // CRITICAL: Parse layout configuration (use layout as primary source)
    let layoutConfig = {};
    try {
      if (typeof plannerData?.layout === 'string') {
        layoutConfig = JSON.parse(plannerData?.layout);
      } else if (typeof plannerData?.layout === 'object') {
        layoutConfig = plannerData?.layout;
      } else {
        const errorMsg = '❌ CRITICAL ERROR: Invalid layout configuration format';
        console.error(errorMsg, plannerData?.layout);
        throw new Error(errorMsg);
      }
    } catch (parseError) {
      const errorMsg = `❌ CRITICAL ERROR: Failed to parse layout configuration: ${parseError?.message}`;
      console.error(errorMsg);
      throw new Error(errorMsg);
    }
    
    console.log('PDF_PROGRESS: Parsing layout configuration');
    
    // Get paper size from UI selection
    const paperSize = getPaperSize(plannerData?.size || specifications?.size);
    
    // Get theme colors from UI selection
    const themeColors = getThemeColors(plannerData?.theme || specifications?.theme);
    
    // Get font families from UI selection
    const titleFont = getFontFamily(plannerData?.personalization?.titleFont || plannerData?.personalization?.font, 'title');
    const contentFont = getFontFamily(plannerData?.personalization?.font, 'content');
    console.log(`🎨 Selected fonts - Content: ${contentFont}, Title: ${titleFont}`);
    
    // Create font config object to pass to page generators
    const fontConfig = {
      title: titleFont,
      content: contentFont
    };
    
    // Extract time format and range from plannerData
    const timeFormat = plannerData?.timeFormat || '12h-long';
    const timeRange = plannerData?.timeRange || { start: 6, end: 18 };
    
    // FIXED: Extract journal configuration from correct path (plannerData.layout.dailyPlanner.journal)
    const journalConfig = layoutConfig?.dailyPlanner?.journal || null;
    
    // DEBUG: Log journal configuration to verify extraction
    console.log('📝 Journal Configuration:', JSON.stringify(journalConfig));
    if (journalConfig) {
      console.log('   - Type:', journalConfig?.type);
      console.log('   - Headings:', journalConfig?.headings);
    }
    
    // Extract selected habits
    const selectedHabits = (plannerData?.habits || [])?.filter(h => h?.enabled && h?.name);

    // Step 1: Pre-calculate all page references WITH CONDITIONAL LOGIC
    const { 
      pageLinks, 
      totalPages, 
      totalDays, 
      totalWeeks, 
      weeks, 
      weeksByMonth, 
      isLeapYear: isLeap, 
      habitPages,
      layoutConfig: parsedLayoutConfig 
    } = calculatePageReferences(year, includeYearView, selectedHabits, layoutConfig);
    
    console.log(`PDF_PROGRESS: Calculated ${totalPages} total pages for generation`);
    
    const isPreviewMode = mode === 'preview';
    if (isPreviewMode) {
      console.log(`🎨 Generating ${year} planner PREVIEW with CONDITIONAL GENERATION`);
    } else {
      console.log(`🎨 Generating ${year} planner FULL MODE with CONDITIONAL GENERATION: ${totalPages} total pages`);
    }
    
    // Step 2: Create PDF with single pass
    const pdf = new jsPDF('p', 'mm', paperSize);
    console.log(`Papersize: ${paperSize}`);
    
    // CRITICAL: Clear font cache before each PDF generation (fresh load)
    console.log('🔄 Clearing font cache for fresh load');
    fontService?.clearCache();
    
    // CRITICAL: Load ONLY selected fonts from database with weight checking
    console.log(`🎨 Loading selected fonts: ${contentFont}, ${titleFont}`);
    const fontsRegistered = await fontService?.registerSelectedFonts(pdf, contentFont, titleFont);
    
    if (!fontsRegistered) {
      console.warn('⚠️ Custom fonts not registered, falling back to jsPDF defaults');
    } else {
      console.log('✅ Custom fonts registered successfully');
      console.log('📋 Available fonts:', pdf?.getFontList());
    }
    
    // NEW: Initialize icon alias manager for optimization
    const iconAliasManager = await createPlannerAliasManager(pdf, themeColors, ICON_SIZES);
    console.log(`✅ Icon aliasing initialized: ${iconAliasManager?.aliases?.size} aliases ready`);
    
    // Generate pages based on mode WITH CONDITIONAL LOGIC
    if (isPreviewMode) {
      // PREVIEW MODE: Generate sample pages with CONDITIONAL LOGIC
      await generateCoverPage(pdf, plannerData, specifications, year, themeColors, fontConfig, iconAliasManager);
      console.log(`✅ Page ${pdf?.internal?.getNumberOfPages()}: Cover page`);

      // Generate index page (always in preview)
      pdf?.addPage();
      await generateIndexPage(pdf, plannerData, year, pageLinks, themeColors, fontConfig, selectedHabits, parsedLayoutConfig, iconAliasManager, mode);
      console.log(`✅ Page ${pdf?.internal?.getNumberOfPages()}: Index page`);
      
      generateBackgroundText(pdf, themeColors, fontConfig, "SAMPLE", 150, 250, 175, 45);


      // Generate Instruction Page
      pdf?.addPage();
      await generateInstructionPage(pdf, plannerData, year, pageLinks, themeColors, fontConfig, selectedHabits, parsedLayoutConfig, iconAliasManager, mode);
      console.log(`✅ Page ${pdf?.internal?.getNumberOfPages()}: Instruction page`);
      
      generateBackgroundText(pdf, themeColors, fontConfig, "SAMPLE", 150, 250, 175, 45);
        
      // Generate first habit tracker page if habits are configured
      if (selectedHabits?.length > 0) {
        pdf?.addPage();
        await generateSingleHabitTrackerPage(pdf, selectedHabits?.[0], 0, year, pageLinks, themeColors, fontConfig, mode);
        console.log(`✅ Page ${pdf?.internal?.getNumberOfPages()}: First habit tracker`);

         generateBackgroundText(pdf, themeColors, fontConfig,  "SAMPLE", 150, 250,  175, 45);
      }
      
      if (includeYearView) {
        pdf?.addPage();
        await generateYearOverviewPage(pdf, year, pageLinks, themeColors, fontConfig, mode);
        console.log(`✅ Page ${pdf?.internal?.getNumberOfPages()}: Year overview`);
        
        generateBackgroundText(pdf, themeColors, fontConfig,  "SAMPLE", 150, 250,  175, 45);
      }
      
      // Generate first month page (January) - CONDITIONAL
      if (parsedLayoutConfig?.monthPlannerEnabled) {
        const yearStart = startOfYear(new Date(year, 0, 1));
        const firstMonth = yearStart;
        pdf?.addPage();
        await generateSingleMonthPage(pdf, firstMonth, 0, year, pageLinks, themeColors, fontConfig, mode);
        console.log(`✅ Page ${pdf?.internal?.getNumberOfPages()}: January month page`);

        generateBackgroundText(pdf, themeColors, fontConfig,  "SAMPLE", 150, 250,  175, 45);
      }
      
      // Generate first week page (Week 1) - CONDITIONAL
      if (parsedLayoutConfig?.weekPlannerEnabled && weeks?.length > 0) {
        pdf?.addPage();
        await generateSingleWeekPage(pdf, year, weeks?.[0], pageLinks, themeColors, fontConfig, mode, iconAliasManager);
        console.log(`✅ Page ${pdf?.internal?.getNumberOfPages()}: Week 1 overview`);

        generateBackgroundText(pdf, themeColors, fontConfig,  "SAMPLE", 150, 250,  175, 45);
      }
      
      // Generate January 1st daily pages - CONDITIONAL
      if (parsedLayoutConfig?.dailyPlannerEnabled) {
        const yearStart = startOfYear(new Date(year, 0, 1));
        const firstDay = yearStart;
        const allDays = [firstDay];
		
		// Schedule-Only page - CONDITIONAL
        if (parsedLayoutConfig?.scheduleEnabled) {
          pdf?.addPage();
          await generateSingleScheduleOnlyPage(pdf, firstDay, 0, allDays, year, pageLinks, themeColors, fontConfig, mode, timeFormat, timeRange, iconAliasManager);
          console.log(`✅ Page ${pdf?.internal?.getNumberOfPages()}: Jan 1 schedule-only`);

          generateBackgroundText(pdf, themeColors, fontConfig,  "SAMPLE", 150, 250,  175, 45);
        }
        
        // Schedule page and Tasks Combination - CONDITIONAL
        if (parsedLayoutConfig?.combinationEnabled) {
          pdf?.addPage();
          await generateSingleDailySchedulePage(pdf, firstDay, 0, allDays, year, pageLinks, themeColors, fontConfig, mode, timeFormat, timeRange, iconAliasManager);
          console.log(`✅ Page ${pdf?.internal?.getNumberOfPages()}: Jan 1 schedule`);

          generateBackgroundText(pdf, themeColors, fontConfig,  "SAMPLE", 150, 250,  175, 45);
        }      
        
        
        // Tasks page - CONDITIONAL
        if (parsedLayoutConfig?.taskEnabled) {
          pdf?.addPage();
          await generateSingleTasksPage(pdf, firstDay, 0, allDays, year, pageLinks, themeColors, fontConfig, mode, iconAliasManager);
          console.log(`✅ Page ${pdf?.internal?.getNumberOfPages()}: Jan 1 tasks`);

          generateBackgroundText(pdf, themeColors, fontConfig,  "SAMPLE", 150, 250,  175, 45);
        }
        
        // Journal page - CONDITIONAL
        if (parsedLayoutConfig?.journalEnabled) {
          pdf?.addPage();
          await generateSingleDailyJournalPage(pdf, firstDay, 0, allDays, year, pageLinks, themeColors, fontConfig, mode, iconAliasManager, journalConfig);
          console.log(`✅ Page ${pdf?.internal?.getNumberOfPages()}: Jan 1 journal`);

          generateBackgroundText(pdf, themeColors, fontConfig,  "SAMPLE", 150, 250,  175, 45);
        }
        console.log('LIFE HACK ENABLED??????????????????' + parsedLayoutConfig?.lifeHackEnabled);
        // Life hack page - CONDITIONAL
        if (parsedLayoutConfig?.lifeHackEnabled) {
          pdf?.addPage();
          await generateSingleLifeHackPage(pdf, firstDay, 0, allDays, year, pageLinks, themeColors, fontConfig, mode, iconAliasManager);
          console.log(`✅ Page ${pdf?.internal?.getNumberOfPages()}: Jan 1 life hack`);

          generateBackgroundText(pdf, themeColors, fontConfig,  "SAMPLE", 150, 250,  175, 45);
        }
      }
      
      console.log(`✅ Preview PDF generation complete: ${pdf?.internal?.getNumberOfPages()} pages`);
      
    } else {
      // FULL MODE: Generate all pages with CONDITIONAL LOGIC
      console.log('PDF_PROGRESS: Generating cover page');
      await generateCoverPage(pdf, plannerData, specifications, year, themeColors, fontConfig, iconAliasManager);
      console.log(`PDF_PROGRESS: Cover page complete`);
      
      // Generate index page
      pdf?.addPage();
      console.log('PDF_PROGRESS: Generating index page');
      await generateIndexPage(pdf, plannerData, year, pageLinks, themeColors, fontConfig, selectedHabits, parsedLayoutConfig, iconAliasManager, mode);
      console.log(`PDF_PROGRESS: Index page complete`);

      // Generate Instruction Page
      pdf?.addPage();
      console.log('PDF_PROGRESS: Generating instruction page');
      await generateInstructionPage(pdf, plannerData, year, pageLinks, themeColors, fontConfig, selectedHabits, parsedLayoutConfig, iconAliasManager, mode);
      console.log(`PDF_PROGRESS: Instruction page complete`);
      
      // Generate habit tracker pages
      if (selectedHabits?.length > 0) {
        console.log(`PDF_PROGRESS: Generating ${selectedHabits?.length} habit tracker pages`);
        for (let i = 0; i < selectedHabits?.length; i++) {
          pdf?.addPage();
          await generateSingleHabitTrackerPage(pdf, selectedHabits?.[i], i, year, pageLinks, themeColors, fontConfig, mode);
        }
        console.log(`PDF_PROGRESS: Habit tracker pages complete`);
      }
      
      // Year overview
      if (includeYearView) {
        pdf?.addPage();
        console.log('PDF_PROGRESS: Generating year overview');
        await generateYearOverviewPage(pdf, year, pageLinks, themeColors, fontConfig, mode);
        console.log(`PDF_PROGRESS: Year overview complete`);
      }
      
      // STEP 1: Generate month and week pages
      if (parsedLayoutConfig?.monthPlannerEnabled || parsedLayoutConfig?.weekPlannerEnabled) {
        console.log('PDF_PROGRESS: Generating monthly and weekly pages');
        
        for (let monthIndex = 0; monthIndex < 12; monthIndex++) {
          const monthDate = new Date(year, monthIndex, 1);
          const monthName = format(monthDate, 'MMMM');
          
          if (parsedLayoutConfig?.monthPlannerEnabled) {
            pdf?.addPage();
            await generateSingleMonthPage(pdf, monthDate, monthIndex, year, pageLinks, themeColors, fontConfig, mode);
          }
          
          const monthWeeks = weeksByMonth?.[monthIndex] || [];
          
          if (parsedLayoutConfig?.weekPlannerEnabled) {
            for (const weekData of monthWeeks) {
              pdf?.addPage();
              await generateSingleWeekPage(pdf, year, weekData, pageLinks, themeColors, fontConfig, mode, iconAliasManager);
            }
          }
          
          console.log(`PDF_PROGRESS: ${monthName} organizational pages complete`);
        }
      }
      
      // STEP 2: Generate ALL daily pages
      if (parsedLayoutConfig?.dailyPlannerEnabled) {
        console.log('PDF_PROGRESS: Generating daily pages');
        const allYearDays = eachDayOfInterval({ start: new Date(year, 0, 1), end: new Date(year, 11, 31) });
        
        for (let dayIndex = 0; dayIndex < allYearDays?.length; dayIndex++) {
          const day = allYearDays?.[dayIndex];
          
          // Schedule-Only page
          if (parsedLayoutConfig?.scheduleEnabled) {
            pdf?.addPage();
            await generateSingleScheduleOnlyPage(pdf, day, dayIndex, allYearDays, year, pageLinks, themeColors, fontConfig, mode, timeFormat, timeRange, iconAliasManager);
          }
          
          // Schedule/Tasks Combination page
          if (parsedLayoutConfig?.combinationEnabled) {
            pdf?.addPage();
            await generateSingleDailySchedulePage(pdf, day, dayIndex, allYearDays, year, pageLinks, themeColors, fontConfig, mode, timeFormat, timeRange, iconAliasManager);
          }
          
          // Tasks page
          if (parsedLayoutConfig?.taskEnabled) {
            pdf?.addPage();
            await generateSingleTasksPage(pdf, day, dayIndex, allYearDays, year, pageLinks, themeColors, fontConfig, mode, iconAliasManager);
          }
          
          // Journal page
          if (parsedLayoutConfig?.journalEnabled) {
            pdf?.addPage();
            await generateSingleDailyJournalPage(pdf, day, dayIndex, allYearDays, year, pageLinks, themeColors, fontConfig, mode, iconAliasManager, journalConfig);
          }
          
          // Life Hack page
          if (parsedLayoutConfig?.lifeHackEnabled) {
            pdf?.addPage();
            await generateSingleLifeHackPage(pdf, day, dayIndex, allYearDays, year, pageLinks, themeColors, fontConfig, mode, iconAliasManager);
          }
          
          // Progress logging every 31 days or at end
          if ((dayIndex + 1) % 31 === 0 || dayIndex === allYearDays?.length - 1) {
            console.log(`PDF_PROGRESS: Generated ${dayIndex + 1} of ${allYearDays?.length} days`);
          }
        }
        
        console.log(`PDF_PROGRESS: Daily pages complete - ${allYearDays?.length} calendar days`);
      }
      
      console.log(`PDF_PROGRESS: PDF generation complete - ${pdf?.internal?.getNumberOfPages()} pages created`);
    }
    
    // Return PDF blob only
    return {
      blob: pdf?.output('blob')
    };
    
  } catch (error) {
    console.error('❌ PDF generation error:', error);
    throw error;
  }
};

/**
 * Helper function to set font with weight fallback
 * FIXED: Now properly integrates with fontService weight checking
 */
const setFontWithFallback = (pdf, fontName, requestedWeight) => {
  // Get font info with intelligent fallback from fontService
  const fontInfo = fontService?.getFontWithFallback(fontName, requestedWeight);
  
    // CRITICAL: Suppress jsPDF's internal warning by checking if font+weight combo exists
  try {
    // Try to set the font - jsPDF will throw if combo doesn't exist
    pdf?.setFont(fontInfo?.fontName, fontInfo?.weight);
  } catch (fontError) {
    // If setting font fails, log and fall back to default
    //console.warn(`⚠️ Font setting failed for ${fontInfo?.fontName} ${fontInfo?.weight}, using Roboto normal`);
    try{
      pdf?.setFont(fontInfo?.fontName, 'normal');
    } catch(fontError){
      pdf?.setFont('helvetica', 'normal');
    }
  }
};

/**
 * Generates the cover page with UI-selected colors, font, and customizations
 * UPDATED: Use iconAliasManager for watermark icon
 * UPDATED: Add background image from Supabase storage based on user selection
 */
const generateCoverPage = async (pdf, plannerData, specifications, year, themeColors, fontConfig, iconAliasManager) => {
  const pageWidth = pdf?.internal?.pageSize?.getWidth();
  const pageHeight = pdf?.internal?.pageSize?.getHeight();
  
  // Set background color from theme (using primary color)
  const bgColor = themeColors?.primary;
  pdf?.setFillColor(bgColor?.r, bgColor?.g, bgColor?.b);
  pdf?.rect(0, 0, pageWidth, pageHeight, 'F');

  // UPDATED: Fetch and add background image from Supabase storage based on user selection
  try {
    // Get the selected background image from user's personalization settings
    const selectedBackgroundImage = plannerData?.personalization?.coverBackgroundImage;
    
    // Only load background if user selected an image (not null/"No Image")
    if (selectedBackgroundImage) {
      console.log(`🎨 Loading selected background image: ${selectedBackgroundImage}`);
      const backgroundImageUrl = await getPdfBackgroundImage(selectedBackgroundImage);
      
      if (backgroundImageUrl) {
        // Fetch the image as a blob
        const response = await fetch(backgroundImageUrl);
        const blob = await response?.blob();
        
        // Convert blob to base64 for jsPDF
        const reader = new FileReader();
        const imageDataUrl = await new Promise((resolve) => {
          reader.onloadend = () => resolve(reader?.result);
          reader?.readAsDataURL(blob);
        });
        
        // Add background image covering entire page
        pdf?.addImage(imageDataUrl, 'JPEG', 0, 0, pageWidth, pageHeight, undefined, 'FAST');
        console.log(`✅ PDF cover background image '${selectedBackgroundImage}' added successfully`);
      } else {
        console.warn(`⚠️ PDF background image '${selectedBackgroundImage}' not found, using solid color background`);
      }
    } else {
      console.log('ℹ️ No background image selected by user, using solid color background');
    }
  } catch (error) {
    console.error('❌ Failed to add PDF background image:', error);
    console.warn('⚠️ Falling back to solid color background');
  }

  // REMOVED: Old local image code
  // let imageRatio = 1792/2400;
  // let imageWidth = pageHeight * imageRatio;
  // let imageHeight = pageHeight;
  // pdf?.addImage('/public/assets/images/feature-05-man1-1766970665396.png', 'JPEG', imageWidth, imageHeight, 1,1);
  
  // Add large calendar icon watermark using ALIAS
  /*
  try {
    const secondaryColor = themeColors?.secondary;
    
    // FIXED: Changed getAliasKey to getCacheKey
    const aliasKey = iconAliasManager?.getCacheKey('calendar', ICON_SIZES?.coverIcon, secondaryColor, bgColor);
    
    // Calculate centered position for watermark
    const iconSize = Math.min(pageWidth, pageHeight) * 0.1;
    let iconX = (pageWidth - iconSize) / 2;
    let iconY = (pageHeight - iconSize) / 2 - 15;
    
    // Use aliased icon - INSTANT, no generation needed
    iconAliasManager?.addToPage(aliasKey, iconX, iconY, iconSize, iconSize);
  } catch (error) {
    console.warn('Failed to add calendar icon watermark:', error);
  }
  */
  // Title - use TITLE FONT for name with fallback
 
  pdf?.setFontSize(nameFontSize);
  setFontWithFallback(pdf, fontConfig?.title, 'bold');
  const title = plannerData?.personalization?.name || plannerData?.name || `My Personal Planner ${year}`;
  const titleWidth = pdf?.getTextWidth(title);
  pdf?.setTextColor(50 , 50, 50);
  pdf?.text(title, ((pageWidth - titleWidth) / 2) +0.4, 65 +0.4);
  pdf?.setTextColor(255, 255, 255);
  pdf?.text(title, (pageWidth - titleWidth) / 2, 65);
  
  // Subtitle - use CONTENT FONT with fallback
  pdf?.setFontSize(14);
  setFontWithFallback(pdf, fontConfig?.content, 'normal');
  const subtitle = `A Custom Planner for`;
  const subtitleWidth = pdf?.getTextWidth(subtitle);
  pdf?.setTextColor(100, 100, 100);
  pdf?.text(subtitle, ((pageWidth - subtitleWidth) / 2)+0.2, 20+0.2);
  pdf?.setTextColor(255, 255, 255);
  pdf?.text(subtitle, (pageWidth - subtitleWidth) / 2, 20);
  
  
  // Decorative lines
  pdf?.setDrawColor(255, 255, 255);
  pdf?.setLineWidth(0.7);
  pdf?.line(30, 110, pageWidth - 30, 110);
  
  // Year display - use TITLE FONT with fallback
  pdf?.setFontSize(125);
  setFontWithFallback(pdf, fontConfig?.title, 'bold');
  const yearWidth = pdf?.getTextWidth(year?.toString());
  pdf?.setTextColor(50 , 50, 50);
  pdf?.text(year?.toString(), ((pageWidth - yearWidth) / 2)+0.4, 150+ 0.4);

  pdf?.setTextColor(255 , 255, 255);
  pdf?.text(year?.toString(), (pageWidth - yearWidth) / 2, 150);
  
  // Custom cover quote from UI (if provided)
  if (plannerData?.personalization?.quote) {
    pdf?.setFontSize(13);
    setFontWithFallback(pdf, fontConfig?.content, 'italic');
    const quote = `${plannerData?.personalization?.quote}`;
    
    // Split quote into multiple lines if too long
    let maxWidth = pageWidth - 60;
    const quoteLines = pdf?.splitTextToSize(quote, maxWidth);
    
    // Position quote with adequate bottom margin
    const lineHeight = 6;
    const totalQuoteHeight = quoteLines?.length * lineHeight;
    const quoteStartY = pageHeight - 25 - totalQuoteHeight;
    
    quoteLines?.forEach((line, index) => {
      const lineWidth = pdf?.getTextWidth(line);
      pdf?.setTextColor(0 , 0, 0);
      pdf?.text(line, ((pageWidth - lineWidth) / 2)+0.3, quoteStartY + (index * lineHeight)+0.3);
      pdf?.setTextColor(255 , 255, 255);
      pdf?.text(line, (pageWidth - lineWidth) / 2, quoteStartY + (index * lineHeight));
    });
  }
  let pngWidth = pageWidth/6;
  let heightRatio = 1024/1536;
  let pngHeight = pngWidth*heightRatio;
  //Logo
  const logoWhite = getStillFlowLogo();
  const logoDark = getStillFlowLogo('#000000ff');
  const pngDataDark = await convertSvgToPng(logoDark, 250, 250);
  const pngDataLight = await convertSvgToPng(logoWhite, 250, 250);
  
  pdf?.addImage(pngDataDark?.imageData, pngDataDark?.format, 
      (pageWidth/2) - ((pngWidth*1.5)/2)+0.3, 
      ((pageHeight)-(pngHeight*1.5)-45)+0.3, // old Y
      pngWidth*1.5, 
      pngHeight*1.5);
  
  pdf?.addImage(pngDataLight?.imageData, pngDataLight?.format, 
      (pageWidth/2) - ((pngWidth*1.5)/2), 
      ((pageHeight)-(pngHeight*1.5)-45), // old Y
      pngWidth*1.5, 
      pngHeight*1.5);
  
  /*
  pdf?.rect(
      (pageWidth/2) - ((pngWidth*1.5)/2) +19, 
      ((pageHeight/2)-(pngHeight*1.5)/2), 
      pngWidth*1.5-20, 
      pngHeight*1.5);
  */

  
  // Footer - use CONTENT FONT with fallback
  pdf?.setFontSize(8.5);
  setFontWithFallback(pdf, fontConfig?.content, 'normal');
  
  const footerWidth = pdf?.getTextWidth(footerText);
  pdf?.text(footerText, (pageWidth - footerWidth) / 2, pageHeight - 20);
};

/**
 * Generates year overview page with clickable month links and theme colors
 * UPDATED: Use setFontWithFallback for all font operations
 */
const generateYearOverviewPage = async (pdf, year, pageLinks, themeColors, fontConfig, mode = 'full') => {
  const pageWidth = pdf?.internal?.pageSize?.getWidth();
  const pageHeight = pdf?.internal?.pageSize?.getHeight();
  
  // Add vertical watermark with year
  pdf?.saveGraphicsState();
  const accentColor = themeColors?.accent;
  pdf?.setTextColor(accentColor?.r, accentColor?.g, accentColor?.b);
  pdf?.setFontSize(240);
  setFontWithFallback(pdf, fontConfig?.title, 'bold');
  
  const watermarkX = (pageWidth / 2) +50 ;
  const watermarkY = (pageHeight / 2) +100;
  pdf?.text(year?.toString(), watermarkX, watermarkY, {
    angle: 90,
    align: 'center'
  });
  pdf?.restoreGraphicsState();
  
  // Year calendar grid
  const paddingPercent = 0.05;
  const horizontalPadding = pageWidth * paddingPercent;
  const verticalPadding = pageHeight * paddingPercent;
  const topMargin = 13;
  
  const monthWidth = (pageWidth - (2 * horizontalPadding)) / 3;
  const monthHeight = ((pageHeight - (2 * verticalPadding)-topMargin) / 4);
  
  const yearStart = startOfYear(new Date(year, 0, 1));
  const yearEnd = endOfYear(new Date(year, 11, 31));
  const months = eachMonthOfInterval({ start: yearStart, end: yearEnd });

  //year header
  const titleColor = themeColors?.primary;
  pdf?.setTextColor(titleColor?.r, titleColor?.g, titleColor?.b);
  pdf?.setFontSize(35);
  setFontWithFallback(pdf, fontConfig?.title, 'bold');
  let yearString = year?.toString();
  let yearWidth = pdf?.getTextWidth(yearString);
  const yearX = (pageWidth/2) -(yearWidth/2);
  pdf?.text(yearString, yearX, verticalPadding);
    
  months?.forEach((month, monthIndex) => {
    const col = monthIndex % 3;
    const row = Math.floor(monthIndex / 3);
    const x = horizontalPadding + (col * monthWidth);
    const y = verticalPadding + (row * monthHeight) + topMargin;
    
    // Month header with fallback
    //const titleColor = themeColors?.primary;
    pdf?.setTextColor(titleColor?.r, titleColor?.g, titleColor?.b);
    pdf?.setFontSize(11);
    setFontWithFallback(pdf, fontConfig?.title, 'bold');
    const monthName = format(month, 'MMMM');
    const monthTextWidth = pdf?.getTextWidth(monthName);
    const monthTextX = x + (monthWidth - monthTextWidth) / 2;
    const monthTextY = y + 5;
    
    const monthPageNum = pageLinks?.get(`month-${monthIndex}`);
    if (monthPageNum && mode !== 'preview') {
      pdf?.link(monthTextX - 2, monthTextY - 10, monthTextWidth + 4, 12, { pageNumber: monthPageNum });
    }
    
    pdf?.text(monthName, monthTextX, monthTextY);
    
    generateMiniCalendar(pdf, month, monthIndex, x + 5, y + 10, monthWidth - 5, monthHeight - 15, pageLinks, themeColors, fontConfig, true, mode, false);
    
    const borderColor = themeColors?.secondary;
    pdf?.setDrawColor(borderColor?.r, borderColor?.g, borderColor?.b);
    pdf?.setLineWidth(0.3);
    pdf?.rect(x, y, monthWidth, monthHeight);
  });
  
  // Navigation instructions at bottom
  pdf?.setFontSize(8.5);
  setFontWithFallback(pdf, fontConfig?.content, 'normal');
  pdf?.setTextColor(100, 100, 100);
  const instructions = footerText;
  const instructionsWidth = pdf?.getTextWidth(instructions);
  pdf?.text(instructions, (pageWidth - instructionsWidth) / 2, pageHeight - 10);
};

/**
 * Generates mini calendar for year overview with day links and theme colors
 * UPDATED: Use setFontWithFallback for all font operations
 */
const generateMiniCalendar = (pdf, month, monthIndex, x, y, width, height, pageLinks, themeColors, fontConfig, enlargeFont = false, mode = 'full', addMonthName=false, currentDay=null) => {
  const monthStart = startOfMonth(month);
  const monthEnd = endOfMonth(month);
  const days = eachDayOfInterval({ start: monthStart, end: monthEnd });
  const monthName = format(month, 'MMMM');

  const daysOfWeek = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
  const cellWidth = width / 7;
  const cellHeight = height / 7;
  
  const baseFontSize = enlargeFont ? 10 : 5;
  pdf?.setFontSize(baseFontSize);
  setFontWithFallback(pdf, fontConfig?.content, 'normal');
  
  // Day headers with theme color
  const headerColor = enlargeFont ? themeColors?.primary : themeColors?.secondary;
  const secondaryColor = themeColors?.secondary;
  const accentColor = themeColors?.accent;
  pdf?.setTextColor(headerColor?.r, headerColor?.g, headerColor?.b);

  if(addMonthName){
    const monthTextWidth = pdf?.getTextWidth(monthName);
     const monthTextCenter = x +(width / 2) - (monthTextWidth/2);
     pdf?.text(monthName, monthTextCenter, y-6);
  }

  daysOfWeek?.forEach((day, index) => {
    const cellX = x + (index * cellWidth);
    const headerY = y + cellHeight - (enlargeFont ? 4 : 2);
    const dayWidth = pdf?.getTextWidth(day);
    pdf?.text(day, cellX + cellWidth / 2 - dayWidth / 2, headerY+1.5);
  });
  
  // Calendar days
  const dayFontSize = enlargeFont ? 10 : 5;
  pdf?.setFontSize(dayFontSize);
  
  days?.forEach((day) => {
    const dayOfMonth = getDate(day);
    const dayOfWeek = day?.getDay();
    
    const startDayOfWeek = monthStart?.getDay();
    const daysSinceStart = day?.getDate() - 1;
    const weekOfMonth = Math.floor((daysSinceStart + startDayOfWeek) / 7) + 1;
    
    const cellX = x + (dayOfWeek * cellWidth);
    const cellY = y + (weekOfMonth * cellHeight);
    
    const dayText = dayOfMonth?.toString();
    const dayTextWidth = pdf?.getTextWidth(dayText);
    const dayTextX = cellX + cellWidth / 2 - dayTextWidth / 2;
    const dayTextY = cellY + cellHeight / 2 + (enlargeFont ? 3 : 2);
    
    const dayPageNum = pageLinks?.get(`day-schedule-${monthIndex}-${dayOfMonth}`);
    if (dayPageNum && mode !== 'preview') {
      pdf?.link(cellX, cellY, cellWidth, cellHeight, { pageNumber: dayPageNum });
    }
    //###############################
     // Highlight current day
    if (format(currentDay, 'yyyy-MM-dd') === format(day, 'yyyy-MM-dd')) {
      pdf?.setFillColor(secondaryColor?.r, secondaryColor?.g, secondaryColor?.b);
      pdf?.circle(cellX + (cellWidth / 2), cellY + (cellHeight / 2)+(0.3*cellHeight) , cellWidth/2.3, 'F');
      
      pdf?.setTextColor(accentColor?.r, accentColor?.g, accentColor?.b);
      pdf?.text(dayText, dayTextX, dayTextY);
    } else {
      pdf?.setTextColor(headerColor?.r, headerColor?.g, headerColor?.b);
      pdf?.text(dayText, dayTextX, dayTextY);
    }




 
  });
};

/**
 * Generates full month calendar with clickable day links and theme colors
 * UPDATED: Use setFontWithFallback for all font operations
 */
const generateFullMonthCalendar = (pdf, month, monthIndex, x, y, width, height, pageLinks, themeColors, fontConfig, mode = 'full') => {
  const monthStart = startOfMonth(month);
  const monthEnd = endOfMonth(month);
  const days = eachDayOfInterval({ start: monthStart, end: monthEnd });
  
  const cellWidth = width / 7;
  const cellHeight = (height * 1.1) / 6;
  
  const daysOfWeek = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  
  pdf?.setFontSize(10);
  setFontWithFallback(pdf, fontConfig?.title, 'bold');
  
  // Day headers with light background from theme
  const headerBg = themeColors?.secondary;
  
  daysOfWeek?.forEach((day, index) => {
    const headerX = x + (index * cellWidth);
    const headerY = y - 5;
    
    pdf?.setFillColor(headerBg?.r, headerBg?.g, headerBg?.b);
    pdf?.rect(headerX, headerY - 8, cellWidth, 12, 'F');
    
    pdf?.setTextColor(255,255,255);
    const dayWidth = pdf?.getTextWidth(day?.substring(0, 3));
    pdf?.text(day?.substring(0, 3), headerX + (cellWidth - dayWidth) / 2, headerY);
  });
  
  // Grid lines with theme color
  const gridColor = themeColors?.secondary;
  pdf?.setDrawColor(gridColor?.r, gridColor?.g, gridColor?.b);
  pdf?.setLineWidth(0.3);
  
  for (let i = 0; i <= 7; i++) {
    pdf?.line(x + (i * cellWidth), y, x + (i * cellWidth), y + (cellHeight * 6));
  }
  
  for (let i = 0; i <= 6; i++) {
    pdf?.line(x, y + (i * cellHeight), x + width, y + (i * cellHeight));
  }
  
  // Fill in days
  setFontWithFallback(pdf, fontConfig?.content, 'normal');
  pdf?.setFontSize(12);
  
  days?.forEach((day) => {
    const dayOfMonth = getDate(day);
    const dayOfWeek = day?.getDay();
    const startDayOfWeek = monthStart?.getDay();
    const daysSinceStart = day?.getDate() - 1;
    const weekOfMonth = Math.floor((daysSinceStart + startDayOfWeek) / 7);
    
    const cellX = x + (dayOfWeek * cellWidth);
    const cellY = y + (weekOfMonth * cellHeight);
    
    const dayPageNum = pageLinks?.get(`day-schedule-${monthIndex}-${dayOfMonth}`);
    if (dayPageNum && mode !== 'preview') {
      pdf?.setTextColor(0, 0, 255);
      pdf?.link(cellX, cellY, cellWidth, cellHeight, { pageNumber: dayPageNum });
    }
    
    const dayColor = themeColors?.primary;
    pdf?.setTextColor(dayColor?.r, dayColor?.g, dayColor?.b);
    const dayNumWidth = pdf?.getTextWidth(dayOfMonth?.toString());
    pdf?.text(dayOfMonth?.toString(), cellX + cellWidth - dayNumWidth - 2, cellY + 5);
    
    // Note lines
    const noteColor = themeColors?.accent;
    pdf?.setDrawColor(noteColor?.r, noteColor?.g, noteColor?.b);
    pdf?.setLineWidth(0.1);
    for (let line = 0; line < 3; line++) {
      const lineY = cellY + 20 + (line * 8);
      if (lineY < cellY + cellHeight - 5) {
        pdf?.line(cellX + 3, lineY, cellX + cellWidth - 3, lineY);
      }
    }
  });
  const pageHeight = pdf?.internal?.pageSize?.getHeight();
  const pageWidth = pdf?.internal?.pageSize?.getWidth();
  pdf?.setFontSize(8.5);
  const footerWidth = pdf?.getTextWidth(footerText);
  pdf?.text(footerText, (pageWidth - footerWidth) / 2, pageHeight - 10);

};




/**
 * Generates 7 day sections for week overview
 * Sunday and Saturday at 50% height, Monday-Friday at 100% height
 * NEW FUNCTION
 * UPDATED: Disabled all links in preview mode
 * UPDATED: Changed day header format to "Monday, January 30"
 */
const generateWeekDaySections = (pdf, weekData, plannerYear, x, y, width, height, pageLinks, themeColors, fontConfig, mode = 'full') => {
  const titleColor = themeColors?.primary;
  const secondaryColor = themeColors?.secondary;
  const lineColor = themeColors?.secondary;
  const pageHeight = pdf?.internal?.pageSize?.getHeight();

  const days = eachDayOfInterval({ start: weekData?.start, end: weekData?.end });
  const daysOfWeek = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  
  // Calculate section heights
  const weekdayHeight = (pageHeight -y -19) / 6.5;
  const weekendHeight = weekdayHeight * 0.6;
  
  let currentY = y;
  
  days?.forEach((day, index) => {
    const dayName = daysOfWeek?.[day?.getDay()];
    const dayOfMonth = getDate(day);
    let monthIndex = getMonth(day);
    const monthName = format(day, 'MMMM');
    const dayYear = day?.getFullYear();
    
    // Determine section height
    const isWeekend = index === 0 || index === 6;
    const sectionHeight = isWeekend ? weekendHeight : weekdayHeight;
    
    // Check if date is within planner year
    const isInPlannerYear = dayYear === plannerYear;
    
    // Day header - UPDATED FORMAT: "Monday, January 30"
    pdf?.setFontSize(12);
    setFontWithFallback(pdf, fontConfig?.title, 'bold');
    
    if (isInPlannerYear) {
      pdf?.setTextColor(titleColor?.r, titleColor?.g, titleColor?.b);
    } else {
      pdf?.setTextColor(secondaryColor?.r, secondaryColor?.g, secondaryColor?.b);
    }
    
    const dayHeader = `${dayName}, ${monthName} ${dayOfMonth}`;
    
    if (isInPlannerYear && mode !== 'preview') {
      const dayPageNum = pageLinks?.get(`day-schedule-${monthIndex}-${dayOfMonth}`);
      if (dayPageNum) {
        const headerWidth = pdf?.getTextWidth(dayHeader);
        pdf?.link(x, currentY - 10, headerWidth + 10, 12, { pageNumber: dayPageNum });
      }
    }
    
    pdf?.text(dayHeader, x, currentY);
    
    // Horizontal line below header
    pdf?.setDrawColor(lineColor?.r, lineColor?.g, lineColor?.b);
    pdf?.setLineWidth(0.3);
    pdf?.line(x, currentY + 2, x + width, currentY + 2);
    
    // Section lines for notes
    pdf?.setLineWidth(0.1);
    const lineSpacing = 4;
    const numLines = Math.floor((sectionHeight - 10) / lineSpacing);
    
    for (let i = 0; i < numLines; i++) {
      const lineY = currentY + 8 + (i * lineSpacing);
      if (lineY < currentY + sectionHeight - 2) {
        pdf?.line(x + 5, lineY, x + width - 5, lineY);
      }
    }
    
    currentY += sectionHeight;
  });
  //const pageHeight = pdf?.internal?.pageSize?.getHeight();
  const pageWidth = pdf?.internal?.pageSize?.getWidth();
  pdf?.setFontSize(8.5);
  const footerWidth = pdf?.getTextWidth(footerText);
  pdf?.text(footerText, (pageWidth - footerWidth) / 2, pageHeight - 10);
};

/**
 * Generates footer with previous/next week navigation
 * NEW FUNCTION
 * UPDATED: Disabled all links in preview mode
 * FIXED: Increase icon pixel resolution from 16 to 64 for crisp rendering
 */
const generateWeekFooter = async (pdf, currentWeek, allWeeks, pageLinks, pageWidth, pageHeight, themeColors, fontConfig, mode = 'full', iconAliasManager) => {
  const footerY = pageHeight - 10;
  const titleColor = themeColors?.primary;
  const iconSize = 5;
  const bgColor = themeColors?.accent;
  
  pdf?.setFontSize(10);
  setFontWithFallback(pdf, fontConfig?.content, 'normal');
  
  const currentIndex = allWeeks?.findIndex(w => w?.weekNumber === currentWeek?.weekNumber);
  
  // Previous Week Link - USE ALIAS
  if (currentIndex > 0) {
    const prevWeek = allWeeks?.[currentIndex - 1];
    const prevWeekText = 'Previous Week';
    const prevPageNum = pageLinks?.get(`week-${prevWeek?.weekNumber}`);
    
    if (prevPageNum) {
      let iconX = 20;
      let iconY = footerY - 4;
      const textX = iconX + iconSize + 2;
      
      pdf?.setTextColor(titleColor?.r, titleColor?.g, titleColor?.b);
      
      // FIXED: Changed getAliasKey to getCacheKey
      const aliasKey = iconAliasManager?.getCacheKey('arrow-left-circle', ICON_SIZES?.dailyIcons, titleColor, bgColor);
      iconAliasManager?.addToPage(aliasKey, iconX, iconY, iconSize, iconSize);
      
      const prevTextWidth = pdf?.getTextWidth(prevWeekText);
      if (mode !== 'preview') {
        pdf?.link(iconX, footerY - 8, iconSize + prevTextWidth + 6, 10, { pageNumber: prevPageNum });
      }
      pdf?.text(prevWeekText, textX, footerY);
    }
  }
  
  // Next Week Link - USE ALIAS
  if (currentIndex < allWeeks?.length - 1) {
    const nextWeek = allWeeks?.[currentIndex + 1];
    const nextWeekText = 'Next Week';
    const nextPageNum = pageLinks?.get(`week-${nextWeek?.weekNumber}`);
    
    if (nextPageNum) {
      pdf?.setTextColor(titleColor?.r, titleColor?.g, titleColor?.b);
      const nextTextWidth = pdf?.getTextWidth(nextWeekText);
      
      const textX = pageWidth - 20 - nextTextWidth - iconSize - 2;
      let iconX = pageWidth - 20 - iconSize;
      let iconY = footerY - 4;
      
      if (mode !== 'preview') {
        pdf?.link(textX, footerY - 8, nextTextWidth + iconSize + 6, 10, { pageNumber: nextPageNum });
      }
      pdf?.text(nextWeekText, textX, footerY);
      
      // FIXED: Changed getAliasKey to getCacheKey
      const aliasKey = iconAliasManager?.getCacheKey('arrow-right-circle', ICON_SIZES?.dailyIcons, titleColor, bgColor);
      iconAliasManager?.addToPage(aliasKey, iconX, iconY, iconSize, iconSize);
    }
  }
};

/**
 * Generates footer with previous/next day schedule navigation links
 * UPDATED: Replaced "<" and ">" text with arrow icons with matching background
 * UPDATED: Disabled all links in preview mode
 * FIXED: Increase icon pixel resolution from 16 to 64 for crisp rendering
 */
const generateDailyScheduleFooter = async (pdf, currentDay, dayIndex, allDays, pageLinks, pageWidth, pageHeight, themeColors, fontConfig, mode = 'full', iconAliasManager) => {
  const footerY = pageHeight - 10;
  const titleColor = themeColors?.primary;
  const iconSize = 5;
  const bgColor = themeColors?.accent;
  
  pdf?.setFontSize(10);
  setFontWithFallback(pdf, fontConfig?.content, 'normal');
  
  // Previous Day Link - USE ALIAS
  if (dayIndex > 0) {
    const prevDay = allDays?.[dayIndex - 1];
    const prevDayText = 'Previous Day';
    const prevMonthIndex = getMonth(prevDay);
    const prevDayOfMonth = getDate(prevDay);
    const prevPageNum = pageLinks?.get(`day-schedule-${prevMonthIndex}-${prevDayOfMonth}`);
    
    if (prevPageNum) {
      let iconX = 20;
      let iconY = footerY - 4;
      const textX = iconX + iconSize + 2;
      
      pdf?.setTextColor(titleColor?.r, titleColor?.g, titleColor?.b);
      
      // FIXED: Changed getAliasKey to getCacheKey
      const aliasKey = iconAliasManager?.getCacheKey('arrow-left-circle', ICON_SIZES?.dailyIcons, titleColor, bgColor);
      iconAliasManager?.addToPage(aliasKey, iconX, iconY, iconSize, iconSize);
      
      const prevTextWidth = pdf?.getTextWidth(prevDayText);
      if (mode !== 'preview') {
        pdf?.link(iconX, footerY - 8, iconSize + prevTextWidth + 6, 10, { pageNumber: prevPageNum });
      }
      pdf?.text(prevDayText, textX, footerY);
    }
  }
  
  // Next Day Link - USE ALIAS
  if (dayIndex < allDays?.length - 1) {
    const nextDay = allDays?.[dayIndex + 1];
    const nextDayText = 'Next Day';
    const nextMonthIndex = getMonth(nextDay);
    const nextDayOfMonth = getDate(nextDay);
    const nextPageNum = pageLinks?.get(`day-schedule-${nextMonthIndex}-${nextDayOfMonth}`);
    
    if (nextPageNum) {
      pdf?.setTextColor(titleColor?.r, titleColor?.g, titleColor?.b);
      const nextTextWidth = pdf?.getTextWidth(nextDayText);
      
      const textX = pageWidth - 20 - nextTextWidth - iconSize - 2;
      let iconX = pageWidth - 20 - iconSize;
      let iconY = footerY - 4;
      
      if (mode !== 'preview') {
        pdf?.link(textX, footerY - 8, nextTextWidth + iconSize + 6, 10, { pageNumber: nextPageNum });
      }
      pdf?.text(nextDayText, textX, footerY);
      
      // FIXED: Changed getAliasKey to getCacheKey
      const aliasKey = iconAliasManager?.getCacheKey('arrow-right-circle', ICON_SIZES?.dailyIcons, titleColor, bgColor);
      iconAliasManager?.addToPage(aliasKey, iconX, iconY, iconSize, iconSize);
    }
  }
};

/**
 * Generates daily page content structure with theme colors
 * UPDATED: Use dynamic time slot generation based on timeFormat and timeRange
 * FIXED: Removed duplicate line drawing (now handled by header function)
 */
const generateDailyPageContent = (pdf, day, dayIndex, allDays, x, y, width, height, pageLinks, themeColors, fontConfig, timeFormat = '12h-long', timeRange = { start: 6, end: 18 }) => {
  const titleColor = themeColors?.primary;
  const secondaryColor = themeColors?.secondary;
  const accentColor = themeColors?.accent;



  const dayOfYear = getDayOfYear(day);
  const quote = getQuoteByDayOfYear(dayOfYear, ['motivational']);
  
  const quoteStartY = y-15;
  const pageWidth = pdf?.internal?.pageSize?.getWidth();
  
   
  pdf?.setTextColor(titleColor?.r, titleColor?.g, titleColor?.b);
  pdf?.setFontSize(10);
  setFontWithFallback(pdf, fontConfig?.content, 'italic');
  
  const maxQuoteWidth = width - 20;
  const quoteLines = pdf?.splitTextToSize(`"${quote?.text}"`, maxQuoteWidth);
  
  let currentQuoteY = quoteStartY + 8;
  quoteLines?.forEach((line, index) => {
    const lineWidth = pdf?.getTextWidth(line);
    const lineX = x + (width - lineWidth) / 2;
    pdf?.text(line, lineX, currentQuoteY + (index * 4));
  });
  
  pdf?.setFontSize(8);
  const authorText = `- ${quote?.author}`;
  const authorWidth = pdf?.getTextWidth(authorText);
  const authorX = x + (width - authorWidth) / 2;
  const authorY = currentQuoteY + (quoteLines?.length * 4) + 2;
  pdf?.text(authorText, authorX, authorY);
  
  const contentStartY = authorY + 5;
  const adjustedHeight = height - (contentStartY - y);

  const scheduleColumnWidth = width * 0.70;
  const rightColumnWidth = width * 0.30;
  const rightColumnX = x + scheduleColumnWidth + 5;
  
  // LEFT COLUMN: Schedule section
  pdf?.setTextColor(titleColor?.r, titleColor?.g, titleColor?.b);
  pdf?.setFontSize(headerTextSize);
  setFontWithFallback(pdf, fontConfig?.title, 'bold');
  pdf?.text('Schedule', x, contentStartY);
  
  const timeSlots = generateTimeSlots(timeFormat, timeRange?.start, timeRange?.end, false);
  
  setFontWithFallback(pdf, fontConfig?.content, 'normal');
  pdf?.setFontSize(8);
  
  const lineColor = themeColors?.secondary;
  pdf?.setDrawColor(lineColor?.r, lineColor?.g, lineColor?.b);
  pdf?.setLineWidth(0.2);
  
  const lineHeight = 9;
  
  timeSlots?.forEach((time, index) => {
    const slotY = y + 15 + (index * lineHeight);
    pdf?.text(time?.time, x+11, slotY-4, {
      align: 'right'
    });
    const schedLineY = 3;
    pdf?.line(x + 8, slotY - schedLineY, x + scheduleColumnWidth - 5, slotY - schedLineY);
  });

  const scheduleEndY = y + 15 + (timeSlots?.length * lineHeight) - 10;
  
  // RIGHT COLUMN: Tasks + Mini Calendar
  let rightColumnY = y;
  
  pdf?.setFontSize(headerTextSize);
  pdf?.setTextColor(titleColor?.r, titleColor?.g, titleColor?.b);
  setFontWithFallback(pdf, fontConfig?.title, 'bold');
  pdf?.text('Tasks', rightColumnX, contentStartY);
  
  setFontWithFallback(pdf, fontConfig?.content, 'normal');
  pdf?.setFontSize(6.4);

  const taskLineSpace = 9;  
  const taskLineCount = Math.floor(163/taskLineSpace);

  for (let i = 0; i < taskLineCount; i++) {
    const goalY = contentStartY + 10 + (i * taskLineSpace);
    pdf?.setLineWidth(0.2);
    pdf?.roundedRect(rightColumnX, goalY-5, 3, 3, 0.5, 0.5, "S");
    pdf?.setLineWidth(0.1);
    pdf?.line(rightColumnX + 6, goalY - 2, rightColumnX + rightColumnWidth - 5, goalY - 2);
  }
  
  // MINI CALENDAR
  const miniCalendarY = rightColumnY + 10 + (taskLineCount * taskLineSpace) + 8;
  const miniCalendarWidth = rightColumnWidth ;
  
  // Get current month for mini calendar
  const currentMonth = startOfMonth(day);
  const monthEnd = endOfMonth(day);
  const monthDays = eachDayOfInterval({ start: currentMonth, end: monthEnd });
  
  setFontWithFallback(pdf, fontConfig?.title, 'bold');
  pdf?.setFontSize(10);
  const miniCalTitle = format(day, 'MMMM yyyy');
  const miniCalTitleWidth = pdf?.getTextWidth(miniCalTitle);
  pdf?.text(miniCalTitle, rightColumnX + (miniCalendarWidth - miniCalTitleWidth) / 2, miniCalendarY);
  
  // Mini calendar grid
  const daysOfWeek = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
  const cellWidth = miniCalendarWidth / 7;
  const cellHeight = 5;
  
  setFontWithFallback(pdf, fontConfig?.content, 'normal');
  pdf?.setFontSize(8);
  
  // Day headers
  daysOfWeek?.forEach((dayName, index) => {
    const cellX = rightColumnX + (index * cellWidth);
    const headerY = miniCalendarY + 9;
    const dayWidth = pdf?.getTextWidth(dayName);
    pdf?.text(dayName, cellX + cellWidth / 2 - dayWidth / 2, headerY, {align: 'center'});
  });
  
  // Calendar days
  const startDayOfWeek = currentMonth?.getDay();
  
  monthDays?.forEach((calDay) => {
    const dayOfMonth = getDate(calDay);
    const dayOfWeek = calDay?.getDay();
    const daysSinceStart = calDay?.getDate() - 1;
    const weekOfMonth = Math.floor((daysSinceStart + startDayOfWeek) / 7) + 1;
    
    const cellX = rightColumnX + (dayOfWeek * cellWidth);
    const cellY = miniCalendarY + 6 + (weekOfMonth * cellHeight);
    
    // Highlight current day
    if (format(calDay, 'yyyy-MM-dd') === format(day, 'yyyy-MM-dd')) {
      pdf?.setFillColor(secondaryColor?.r, secondaryColor?.g, secondaryColor?.b);
      pdf?.circle(cellX + cellWidth / 2, cellY + cellHeight / 2 + 0.4, 1.5, 'F');
      pdf?.setTextColor(accentColor?.r, accentColor?.g, accentColor?.b);
    } else {
      pdf?.setTextColor(titleColor?.r, titleColor?.g, titleColor?.b);
    }
    
    const dayText = dayOfMonth?.toString();
    const dayTextWidth = pdf?.getTextWidth(dayText);
    pdf?.text(dayText, cellX + cellWidth / 2 - dayTextWidth / 2, cellY + cellHeight / 2 + 1);
  });
  
  // BOTTOM SECTION: Notes
  const notesStartY = scheduleEndY + 6;
  pdf?.setTextColor(titleColor?.r, titleColor?.g, titleColor?.b);
  setFontWithFallback(pdf, fontConfig?.title, 'bold');
  pdf?.text('Notes:', x+4, notesStartY);

  pdf?.setFillColor(255, 255, 255);
  
  const notesBoxWidth = pdf?.internal?.pageSize?.getWidth() - 100;
  const notesBoxHeight = pdf?.internal?.pageSize?.getHeight() - notesStartY - 19;
  const notesBoxX = x + 4;
  const notesBoxY = notesStartY +3;
  
  pdf?.roundedRect(notesBoxX, notesBoxY, notesBoxWidth, notesBoxHeight,3,3, "S");
    
  const notesColor = themeColors?.accent;
  pdf?.setDrawColor(notesColor?.r, notesColor?.g, notesColor?.b);
  pdf?.setLineWidth(0.1);
};


/**
 * Generates daily tasks page content with theme colors
 * NEW FUNCTION
 */
const generateTasksPageContent = async (pdf, day, dayIndex, allDays, x, y, width, height, pageLinks, themeColors, fontConfig, iconAliasManager) => {
  const titleColor = themeColors?.primary;
  const lineColor = themeColors?.secondary;
  const accentColor = themeColors?.accent;
  const bgColor = themeColors?.accent;
  
  // Get quote for this day (same quote as schedule/journal pages)
  const dayOfYear = getDayOfYear(day);
  const quote = getQuoteByDayOfYear(dayOfYear, ['motivational']);
  
  // Calculate quote position
  const quoteStartY = y - 15;
  const pageWidth = pdf?.internal?.pageSize?.getWidth();
  
  // Quote text
  pdf?.setTextColor(titleColor?.r, titleColor?.g, titleColor?.b);
  pdf?.setFontSize(10);
  setFontWithFallback(pdf, fontConfig?.content, 'italic');
  
  const maxQuoteWidth = width - 20;
  const quoteLines = pdf?.splitTextToSize(`"${quote?.text}"`, maxQuoteWidth);
  
  let currentQuoteY = quoteStartY + 8;
  quoteLines?.forEach((line, index) => {
    const lineWidth = pdf?.getTextWidth(line);
    const lineX = x + (width - lineWidth) / 2;
    pdf?.text(line, lineX, currentQuoteY + (index * 4));
  });
  
  // Author text
  pdf?.setFontSize(8);
  const authorText = `- ${quote?.author}`;
  const authorWidth = pdf?.getTextWidth(authorText);
  const authorX = x + (width - authorWidth) / 2;
  const authorY = currentQuoteY + (quoteLines?.length * 4) + 2;
  pdf?.text(authorText, authorX, authorY);
  
  // Adjust content start position
  const contentStartY = authorY + 10;
  const adjustedHeight = height - (contentStartY - y);
  
  // Task list section (top 2/3 of page) - 20 lines × 2 columns
  let currentY = contentStartY;
  const taskSectionHeight = adjustedHeight * 0.75;
  
  // Header
  pdf?.setTextColor(titleColor?.r, titleColor?.g, titleColor?.b);
  pdf?.setFontSize(12);
  setFontWithFallback(pdf, fontConfig?.title, 'bold');
  pdf?.text('Today\'s Tasks', x, currentY);
  
  currentY += 8;
  
  // Two columns of task lines (85% journal line spacing = 4.25mm)
  const columnWidth = width / 2;
  const lineSpacing = 9;
  const linesPerColumn = 15;
  const checkboxSize = 3.5;
  
  pdf?.setFontSize(8);
  setFontWithFallback(pdf, fontConfig?.content, 'normal');
  pdf?.setDrawColor(lineColor?.r, lineColor?.g, lineColor?.b);
  
  for (let i = 0; i < linesPerColumn; i++) {
    const lineY = currentY + (i * lineSpacing);
    
    // Left column
    const leftX = x;
    // Checkbox
    pdf?.setLineWidth(0.2);
    pdf?.roundedRect(leftX, lineY - checkboxSize, checkboxSize, checkboxSize, 0.5, 0.5, 'S');
    
    // ABCD priority circles (using circled letters)
    pdf?.setFontSize(7);
    setFontWithFallback(pdf, fontConfig?.content, 'normal');
    const priorityX = leftX + checkboxSize + 2;
    const priorityLetters = ['A', 'B', 'C', 'D'];
    priorityLetters?.forEach((letter, idx) => {
      pdf?.text(letter, priorityX + (idx * 3), lineY);
    });
    
    // Task line
    pdf?.setLineWidth(0.1);
    pdf?.line(priorityX + 12, lineY, leftX + columnWidth - 5, lineY);
    
    // Right column
    const rightX = x + columnWidth + 5;
    // Checkbox
    pdf?.setLineWidth(0.2);
    pdf?.roundedRect(rightX, lineY - checkboxSize, checkboxSize, checkboxSize, 0.5, 0.5, 'S');
    
    // ABCD priority circles
    const rightPriorityX = rightX + checkboxSize + 2;
    priorityLetters?.forEach((letter, idx) => {
      pdf?.text(letter, rightPriorityX + (idx * 3), lineY);
    });
    
    // Task line
    pdf?.setLineWidth(0.1);
    pdf?.line(rightPriorityX + 12, lineY, rightX + columnWidth - 10, lineY);
  }
  
  // Eisenhower Matrix + Instructions section (bottom 1/3 of page)
  currentY = contentStartY + taskSectionHeight + 5;
  const instructionSectionHeight = adjustedHeight - taskSectionHeight - 5;
  
  // Two columns: Matrix on left (45%), Instructions on right (55%)
  const matrixColumnWidth = width * 0.45;
  const instructionColumnWidth = width * 0.55;
  
  // LEFT: Eisenhower Matrix (50mm × 50mm)
  const matrixSize = 60;
  const matrixX = x+2;
  const matrixY = currentY;
  
  pdf?.setTextColor(titleColor?.r, titleColor?.g, titleColor?.b);
  pdf?.setFontSize(14);
  setFontWithFallback(pdf, fontConfig?.title, 'bold');
  //pdf?.text('Eisenhower Matrix', matrixX-2, matrixY);
  
  // Draw matrix grid
  const cellSize = matrixSize / 2;
  const gridStartY = matrixY + 10;
  
  pdf?.setDrawColor(lineColor?.r, lineColor?.g, lineColor?.b);
  pdf?.setLineWidth(0.5);
  
  // Draw grid lines
  // Vertical line
  pdf?.line(matrixX + cellSize, gridStartY, matrixX + cellSize, gridStartY + matrixSize);
  // Horizontal line
  pdf?.line(matrixX, gridStartY + cellSize, matrixX + matrixSize, gridStartY + cellSize);
  // Border
  pdf?.rect(matrixX, gridStartY, matrixSize, matrixSize);
  
  // Axis labels
  pdf?.setFontSize(8);
  setFontWithFallback(pdf, fontConfig?.content, 'bold');
  
  // Top axis (URGENT / NOT URGENT)
  const topAxisY = gridStartY - 2;
  pdf?.text('URGENT', matrixX + cellSize / 2 - 10, topAxisY);
  pdf?.text('NOT URGENT', matrixX + cellSize + 5, topAxisY);
  
  // Left axis (IMPORTANT / NOT IMPORTANT)
  pdf?.saveGraphicsState();
  pdf?.text('IMPORTANT', matrixX - 2, gridStartY + 20, { angle: 90 });
  pdf?.text('NOT IMPORTANT', matrixX - 2, gridStartY + cellSize + 30, { angle: 90 });
  pdf?.restoreGraphicsState();
  
  // Quadrant labels and colors
  pdf?.setFontSize(7);
  setFontWithFallback(pdf, fontConfig?.content, 'normal');
  
  // Quadrant A (Do) - top left - light red
  pdf?.setFillColor(accentColor?.r,accentColor?.g, accentColor?.b);
  pdf?.rect(matrixX + 1, gridStartY + 1, cellSize - 2, cellSize - 2, 'F');
  pdf?.setTextColor(titleColor?.r, titleColor?.g, titleColor?.b);
  pdf?.text('"A" DO', matrixX + cellSize / 2 - 5, gridStartY + cellSize / 2);
  
  // Quadrant B (Schedule) - top right - light green
  pdf?.setFillColor(200,200,200);
  pdf?.rect(matrixX + cellSize + 1, gridStartY + 1, cellSize - 2, cellSize - 2, 'F');
  pdf?.setTextColor(titleColor?.r, titleColor?.g, titleColor?.b);
  pdf?.text('"B" SCHEDULE', matrixX + cellSize + 5, gridStartY + cellSize / 2);
  
  // Quadrant C (Delegate) - bottom left - light yellow
  pdf?.setFillColor(200,200,200);
  pdf?.rect(matrixX + 1, gridStartY + cellSize + 1, cellSize - 2, cellSize - 2, 'F');
  pdf?.setTextColor(titleColor?.r, titleColor?.g, titleColor?.b);
  pdf?.text('"C" DELEGATE', matrixX + 3, gridStartY + cellSize + cellSize / 2);
  
  // Quadrant D (Delete) - bottom right - light gray
  pdf?.setFillColor(accentColor?.r,accentColor?.g, accentColor?.b);
  pdf?.rect(matrixX + cellSize + 1, gridStartY + cellSize + 1, cellSize - 2, cellSize - 2, 'F');
  pdf?.setTextColor(titleColor?.r, titleColor?.g, titleColor?.b);
  pdf?.text('"D" DELETE', matrixX + cellSize + 7, gridStartY + cellSize + cellSize / 2);
  
  // RIGHT: Instructions
  const instructionX = x + matrixColumnWidth + 10;
  const instructionY = matrixY;
  
  pdf?.setFontSize(14);
  setFontWithFallback(pdf, fontConfig?.title, 'bold');
  //pdf?.text('How to Use', instructionX, instructionY);
  
  pdf?.setFontSize(9);
  setFontWithFallback(pdf, fontConfig?.content, 'normal');
  
  const instructions = [
    '1. Circle the priority (A B C D) for each task',
    '',
    '2. Focus on Quadrant "B" (non-urgent+ important):',
    '   • Planning, growth, prevention',
    '   • Aim to spend most of your time here',
    '',
    '3. Number "A" & "B" tasks in order:',
    '   • This creates your "what\'s next" list',
    '   • Tackle A tasks first (urgent)',
    '   • Then move to "B" tasks',
    '',
    '4. Minimize "C" & "D":',
    '   • "C": Delegate if possible',
    '   • "D": Consider eliminating'
  ];
  
  instructions?.forEach((line, index) => {
    //pdf?.text(line, instructionX, instructionY + 8 + (index * 3.5));
    pdf?.text(line, instructionX, gridStartY + (index * 3.5));
  });
};

/**
 * Generates footer for tasks page with navigation
 * NEW FUNCTION
 */
const generateTasksFooter = async (pdf, currentDay, dayIndex, allDays, pageLinks, pageWidth, pageHeight, themeColors, fontConfig, mode = 'full', iconAliasManager) => {
  const footerY = pageHeight - 10;
  const titleColor = themeColors?.primary;
  const iconSize = 5;
  const bgColor = themeColors?.accent;
  
  pdf?.setFontSize(10);
  setFontWithFallback(pdf, fontConfig?.content, 'normal');
  
  // Previous Page Link (points to current day's schedule page)
  let monthIndex = getMonth(currentDay);
  const dayOfMonth = getDate(currentDay);
  const prevPageNum = pageLinks?.get(`day-schedule-${monthIndex}-${dayOfMonth}`);
  
  if (prevPageNum) {
    const prevPageText = 'Previous Page';
    let iconX = 20;
    let iconY = footerY - 4;
    const textX = iconX + iconSize + 2;
    
    pdf?.setTextColor(titleColor?.r, titleColor?.g, titleColor?.b);
    
    const aliasKey = iconAliasManager?.getCacheKey('arrow-left-circle', ICON_SIZES?.dailyIcons, titleColor, bgColor);
    iconAliasManager?.addToPage(aliasKey, iconX, iconY, iconSize, iconSize);
    
    const prevTextWidth = pdf?.getTextWidth(prevPageText);
    if (mode !== 'preview') {
      pdf?.link(iconX, footerY - 8, iconSize + prevTextWidth + 6, 10, { pageNumber: prevPageNum });
    }
    pdf?.text(prevPageText, textX, footerY);
  }
  
  // Next Page Link (points to current day's journal page)
  const nextPageNum = pageLinks?.get(`day-journal-${monthIndex}-${dayOfMonth}`);
  
  if (nextPageNum) {
    const nextPageText = 'Next Page';
    pdf?.setTextColor(titleColor?.r, titleColor?.g, titleColor?.b);
    const nextTextWidth = pdf?.getTextWidth(nextPageText);
    
    const textX = pageWidth - 20 - nextTextWidth - iconSize - 2;
    let iconX = pageWidth - 20 - iconSize;
    let iconY = footerY - 4;
    
    if (mode !== 'preview') {
      pdf?.link(textX, footerY - 8, nextTextWidth + iconSize + 6, 10, { pageNumber: nextPageNum });
    }
    pdf?.text(nextPageText, textX, footerY);
    
    const aliasKey = iconAliasManager?.getCacheKey('arrow-right-circle', ICON_SIZES?.dailyIcons, titleColor, bgColor);
    iconAliasManager?.addToPage(aliasKey, iconX, iconY, iconSize, iconSize);
  }
};

/**
 * Generates all daily journal pages with lined writing areas
 * UPDATED: Replaced all direct pdf.setFont() with setFontWithFallback()
 */
/**
 * Generates journal page content with dynamic sections based on user configuration
 * UPDATED: Support both no_headings and subject_headings with custom headings
 * FIXED: Handle variable number of sections (2-4) with equal spacing
 */
const generateJournalPageContent = (pdf, day, dayIndex, allDays, x, y, width, height, pageLinks, themeColors, fontConfig, journalConfig = null) => {
  const titleColor = themeColors?.primary;
  const lineColor = themeColors?.secondary;
  const accentColor = themeColors?.accent;
  
  // Get quote for this day (same quote as schedule page)
  const dayOfYear = getDayOfYear(day);
  const quote = getQuoteByDayOfYear(dayOfYear, ['motivational']);
  
  // Calculate quote position
  const quoteStartY = y-15;
  const pageWidth = pdf?.internal?.pageSize?.getWidth();
  const pageHeight = pdf?.internal?.pageSize?.getHeight();
  
  // Quote text
  pdf?.setTextColor(titleColor?.r, titleColor?.g, titleColor?.b);
  pdf?.setFontSize(10);
  setFontWithFallback(pdf, fontConfig?.content, 'italic');
  
  const maxQuoteWidth = width - 20;
  const quoteLines = pdf?.splitTextToSize(`"${quote?.text}"`, maxQuoteWidth);
  
  let currentQuoteY = quoteStartY + 8;
  quoteLines?.forEach((line, index) => {
    const lineWidth = pdf?.getTextWidth(line);
    const lineX = x + (width - lineWidth) / 2;
    pdf?.text(line, lineX, currentQuoteY + (index * 4));
  });
  
  // Author text
  pdf?.setFontSize(8);
  const authorText = `- ${quote?.author}`;
  const authorWidth = pdf?.getTextWidth(authorText);
  const authorX = x + (width - authorWidth) / 2;
  const authorY = currentQuoteY + (quoteLines?.length * 4) + 2;
  pdf?.text(authorText, authorX, authorY);
  
  // Adjust content start position
  const contentStartY = authorY + 10;
  //const adjustedHeight = height - (contentStartY - y);
  const adjustedHeight = pageHeight - contentStartY - 20;
  
  // FIXED: Check journal configuration with debug logging
  // console.log('📄 Generating journal page content...');
    
  const journalType = journalConfig?.type || 'no_headings';
  const customHeadings = journalConfig?.headings || [];

  
  const lineSpacing = 6.9;
  
  if (journalType === 'no_headings') {
    // OPTION 1: Continuous lines (no sections)
    console.log('   ✓ Using no_headings layout (continuous lines)');
    const totalLines = Math.floor(adjustedHeight / lineSpacing);
    
    pdf?.setDrawColor(lineColor?.r, lineColor?.g, lineColor?.b);
    pdf?.setLineWidth(0.2);
    
    for (let i = 0; i < totalLines; i++) {
      const lineY = contentStartY + (i * lineSpacing);
      pdf?.line(x, lineY, x + width, lineY);
    }
  } else {
    // OPTION 2: Subject headings with custom text
    console.log('   ✓ Using subject_headings layout');
    
    // FIXED: Filter out empty headings and ensure minimum 2
    const validHeadings = customHeadings?.filter(h => h?.trim()?.length > 0);
    console.log('   - Valid headings after filtering:', validHeadings);
    
    const headingsToUse = validHeadings?.length >= 2 ? validHeadings : ['Meditation Thought', 'Wins Today', 'Areas for Improvement'];
    console.log('   - Headings to use:', headingsToUse);
    
    // FIXED: Limit to 4 headings maximum
    const finalHeadings = headingsToUse?.slice(0, 4);
    const numSections = finalHeadings?.length;
    
    console.log('   - Final headings:', finalHeadings);
    console.log('   - Number of sections:', numSections);
    
    // FIXED: Calculate equal section heights with minimum 3 lines per section
    const minLinesPerSection = 3;
    const minSectionHeight = minLinesPerSection * lineSpacing + 15; // +15 for heading
    
    const sectionHeight = Math.max(
      minSectionHeight,
      adjustedHeight / numSections
    );
    
    // Draw each section
    for (let sectionIndex = 0; sectionIndex < numSections; sectionIndex++) {
      const sectionY = contentStartY + (sectionIndex * sectionHeight);
      const heading = finalHeadings?.[sectionIndex];
      
      // FIXED: Heading with proper positioning and truncation
      pdf?.setTextColor(titleColor?.r, titleColor?.g, titleColor?.b);
      pdf?.setFontSize(headerTextSize + 1);
      setFontWithFallback(pdf, fontConfig?.title, 'bold');
      
      // FIXED: Split heading to 2 lines if longer than page width
      const headingMaxWidth = width - 10;
      const headingLines = pdf?.splitTextToSize(heading, headingMaxWidth);
      
      // Draw heading (max 2 lines)
      headingLines?.slice(0, 2)?.forEach((line, lineIdx) => {
        pdf?.text(line, x, sectionY + (lineIdx * 5)  + 12);
        ///---///
      });
      
      // Calculate available space for lines
      const headingHeight = Math.min(headingLines?.length, 2) * 5;
      const availableHeight = sectionHeight - headingHeight - 5;
      const linesInSection = Math.floor(availableHeight / lineSpacing);
      
      // Draw lines
      pdf?.setDrawColor(lineColor?.r, lineColor?.g, lineColor?.b);
      pdf?.setLineWidth(0.2);
      
      for (let i = 0; i < linesInSection; i++) {
        const lineY = sectionY + headingHeight + 10 + (i * lineSpacing);
        // FIXED: Don't draw section dividers, just continuous lines
        pdf?.line(x, lineY, x + width, lineY);
      }
    }
  }
};

/**
 * Generates footer with previous/next day journal navigation links
 * UPDATED: Next link now points to tasks page instead of life_hack page
 * FIXED: Increase icon pixel resolution from 16 to 64 for crisp rendering
 */
const generateDailyJournalFooter = async (pdf, currentDay, dayIndex, allDays, pageLinks, pageWidth, pageHeight, themeColors, fontConfig, mode = 'full', iconAliasManager) => {
  const footerY = pageHeight - 10;
  const titleColor = themeColors?.primary;
  const iconSize = 5;
  const bgColor = themeColors?.accent;
  
  pdf?.setFontSize(10);
  setFontWithFallback(pdf, fontConfig?.content, 'normal');
  
  // Previous Day Link (points to current day's tasks page)
  let monthIndex = getMonth(currentDay);
  const dayOfMonth = getDate(currentDay);
  const prevPageNum = pageLinks?.get(`day-tasks-${monthIndex}-${dayOfMonth}`);
  
  if (prevPageNum) {
    let iconX = 20;
    let iconY = footerY - 4;
    const textX = iconX + iconSize + 2;
    const prevPageText = 'Previous Page';
    
    pdf?.setTextColor(titleColor?.r, titleColor?.g, titleColor?.b);
    
    const aliasKey = iconAliasManager?.getCacheKey('arrow-left-circle', ICON_SIZES?.dailyIcons, titleColor, bgColor);
    iconAliasManager?.addToPage(aliasKey, iconX, iconY, iconSize, iconSize);
    
    const prevTextWidth = pdf?.getTextWidth(prevPageText);
    if (mode !== 'preview') {
      pdf?.link(iconX, footerY - 8, iconSize + prevTextWidth + 6, 10, { pageNumber: prevPageNum });
    }
    pdf?.text(prevPageText, textX, footerY);
  }
  
  // Next Page Link (points to current day's life_hack page)
  const nextPageNum = pageLinks?.get(`day-lifehack-${monthIndex}-${dayOfMonth}`);
  
  if (nextPageNum) {
    const nextPageText = 'Next Page';
    pdf?.setTextColor(titleColor?.r, titleColor?.g, titleColor?.b);
    const nextTextWidth = pdf?.getTextWidth(nextPageText);
    
    const textX = pageWidth - 20 - nextTextWidth - iconSize - 2;
    let iconX = pageWidth - 20 - iconSize;
    let iconY = footerY - 4;
    
    if (mode !== 'preview') {
      pdf?.link(textX, footerY - 8, nextTextWidth + iconSize + 6, 10, { pageNumber: nextPageNum });
    }
    pdf?.text(nextPageText, textX, footerY);
    
    const aliasKey = iconAliasManager?.getCacheKey('arrow-right-circle', ICON_SIZES?.dailyIcons, titleColor, bgColor);
    iconAliasManager?.addToPage(aliasKey, iconX, iconY, iconSize, iconSize);
  }
};

/**
 * Generates life hack page content with 4 modular sections
 * NEW FUNCTION
 * FIXED: Removed duplicate line drawing (now handled by header function)
 */
const generateLifeHackPageContent = async (pdf, day, dayIndex, allDays, x, y, width, height, pageLinks, themeColors, fontConfig, iconAliasManager) => {
  const titleColor = themeColors?.primary;
  const lineColor = themeColors?.secondary;
  const accentColor = themeColors?.accent;
  const bgColor = themeColors?.accent;
  
  // Get quote for this day (same quote as schedule/journal pages)
  const dayOfYear = getDayOfYear(day);
  const quote = getQuoteByDayOfYear(dayOfYear, ['motivational']);
  
  // Calculate quote position
  const quoteStartY = y - 15;
  const pageWidth = pdf?.internal?.pageSize?.getWidth();
  
  // REMOVED: Duplicate line drawing - now handled by generateDailyPageHeader
  // Old code: pdf?.line(x, quoteStartY, x + width, quoteStartY);
  
  // Quote text
  pdf?.setTextColor(titleColor?.r, titleColor?.g, titleColor?.b);
  pdf?.setFontSize(10);
  setFontWithFallback(pdf, fontConfig?.content, 'italic');
  
  const maxQuoteWidth = width - 20;
  const quoteLines = pdf?.splitTextToSize(`"${quote?.text}"`, maxQuoteWidth);
  
  let currentQuoteY = quoteStartY + 8;
  quoteLines?.forEach((line, index) => {
    const lineWidth = pdf?.getTextWidth(line);
    const lineX = x + (width - lineWidth) / 2;
    pdf?.text(line, lineX, currentQuoteY + (index * 4));
  });
  
  // Author text
  pdf?.setFontSize(8);
  const authorText = `- ${quote?.author}`;
  const authorWidth = pdf?.getTextWidth(authorText);
  const authorX = x + (width - authorWidth) / 2;
  const authorY = currentQuoteY + (quoteLines?.length * 4) + 2;
  pdf?.text(authorText, authorX, authorY);
  
  // Adjust content start position
  const contentStartY = authorY + 10;
  const adjustedHeight = height - (contentStartY - y);
  
  // Section 1: Trackers (Water, Weather, Mood) - Top 15% of page
  let currentY = contentStartY;
  const section1Height = adjustedHeight * 0.15;
  
  // Water Intake Tracker (icons in a row)
  pdf?.setTextColor(titleColor?.r, titleColor?.g, titleColor?.b);
  pdf?.setFontSize(10);
  setFontWithFallback(pdf, fontConfig?.title, 'bold');
  pdf?.text('Water Intake:', x, currentY);
  
  const iconSize = 5;
  const iconSpacing = 8;
  // FIXED: Changed getAliasKey to getCacheKey
  const waterAliasKey = iconAliasManager?.getCacheKey('glass-water', ICON_SIZES?.dailyIcons, titleColor, bgColor);
  
  for (let i = 0; i < 8; i++) {
    // FIXED: Increase pixel size from 16 to 64 for crisp rendering
    const glassWaterIconPng = await getIcon('glass-water', titleColor, ICON_SIZES?.dailyIcons, bgColor);
    let iconX = x + 35 + (i * iconSpacing);
    let iconY = currentY - 4;
    // REVERTED: Use addImage for PNG data
    pdf?.addImage(glassWaterIconPng, 'PNG', iconX, iconY, iconSize, iconSize);
  }
  
  currentY += 10;
  
  // Weather Recorder - UPDATED with grouped icons
  pdf?.text('Weather:', x, currentY);
  
  // Temperature icons (group 1)
  const temperatureIconNames = ['thermometer-sun', 'thermometer-snowflake'];
  const temperatureAliasKeys = temperatureIconNames?.map(name => 
    iconAliasManager?.getCacheKey(name, ICON_SIZES?.dailyIcons, titleColor, bgColor)
  );
  
  let currentIconX = x + 35;
  for (let i = 0; i < temperatureAliasKeys?.length; i++) {
    let iconX = currentIconX + (i * iconSpacing);
    let iconY = currentY - 4;
    iconAliasManager?.addToPage(temperatureAliasKeys?.[i], iconX, iconY, iconSize, iconSize);
  }

  currentIconX +=10; 

  // Sunshine icons (group 2) - continue from temperature icons
  const sunshineIconNames = ['sun', 'sun-dim', 'moon'];
  const sunshineAliasKeys = sunshineIconNames?.map(name => 
    iconAliasManager?.getCacheKey(name, ICON_SIZES?.dailyIcons, titleColor, bgColor)
  );

  currentIconX +=10;  
  
  currentIconX = x + 35 + (temperatureIconNames?.length * iconSpacing);
  for (let i = 0; i < sunshineAliasKeys?.length; i++) {
    let iconX = currentIconX + (i * iconSpacing);
    let iconY = currentY - 4;
    iconAliasManager?.addToPage(sunshineAliasKeys?.[i], iconX, iconY, iconSize, iconSize);
  }

  currentIconX +=10;  
  
  // Precipitation icons (group 3) - continue from sunshine icons
  const precipitationIconNames = ['cloud', 'cloud-rain', 'cloud-snow', 'cloud-drizzle', 'snowflake', 'zap'];
  const precipitationAliasKeys = precipitationIconNames?.map(name => 
    iconAliasManager?.getCacheKey(name, ICON_SIZES?.dailyIcons, titleColor, bgColor)
  );

  
  currentIconX = x + 35 + (temperatureIconNames?.length * iconSpacing) + (sunshineIconNames?.length * iconSpacing);
  for (let i = 0; i < precipitationAliasKeys?.length; i++) {
    let iconX = currentIconX + (i * iconSpacing);
    let iconY = currentY - 4;
    iconAliasManager?.addToPage(precipitationAliasKeys?.[i], iconX, iconY, iconSize, iconSize);
  }
  
  // Weather description line
  pdf?.setDrawColor(lineColor?.r, lineColor?.g, lineColor?.b);
  pdf?.setLineWidth(0.2);
  pdf?.line(x + 35, currentY + 10, x + width - 5, currentY + 10);
  currentY += 7;
  pdf?.line(x + 35, currentY + 10, x + width - 5, currentY + 10);
  
  currentY += 20;
  
  // Mood Tracker
  pdf?.text('Mood:', x, currentY);
  
  const moodIconNames = ['smile', 'meh', 'frown'];
  const moodAliasKeys = moodIconNames?.map(name =>
    // FIXED: Changed getAliasKey to getCacheKey
    iconAliasManager?.getCacheKey(name, ICON_SIZES?.dailyIcons, titleColor, bgColor)
  );
  
  for (let i = 0; i < moodAliasKeys?.length; i++) {
    let iconX = x + 35 + (i * iconSpacing);
    let iconY = currentY - 4;
    iconAliasManager?.addToPage(moodAliasKeys?.[i], iconX, iconY, iconSize, iconSize);
  }
  
  currentY += 15;
  
  // Section 2: Daily Goals (10 goals in 2 columns) - 25% of page
  const section2Height = adjustedHeight * 0.25;
  pdf?.setTextColor(titleColor?.r, titleColor?.g, titleColor?.b);
  pdf?.setFontSize(12);
  setFontWithFallback(pdf, fontConfig?.title, 'bold');
  pdf?.text('Daily Goals', x, currentY);
  
  currentY += 8;
  
  // Two columns of goals
  const columnWidth = width / 2;
  pdf?.setFontSize(9);
  setFontWithFallback(pdf, fontConfig?.content, 'normal');
  
  for (let i = 0; i < 5; i++) {
    const goalY = currentY + (i * 8);
    
    // Left column (goals 1-5)
    pdf?.setLineWidth(0.2);
    pdf?.roundedRect(x, goalY - 5, 3, 3, 0.5, 0.5, 'S');
    pdf?.text(`${i + 1}.`, x + 5, goalY - 2);
    pdf?.setLineWidth(0.1);
    pdf?.line(x + 12, goalY - 2, x + columnWidth - 10, goalY - 2);
    
    // Right column (goals 6-10)
    pdf?.setLineWidth(0.2);
    pdf?.roundedRect(x + columnWidth, goalY - 5, 3, 3, 0.5, 0.5, 'S');
    pdf?.text(`${i + 6}.`, x + columnWidth + 5, goalY - 2);
    pdf?.setLineWidth(0.1);
    pdf?.line(x + columnWidth + 12, goalY - 2, x + width - 5, goalY - 2);
  }
  
  currentY += section2Height;
  
  // Section 3: Gratitude - 30% of page
  const section3Height = adjustedHeight * 0.30;
  pdf?.setTextColor(titleColor?.r, titleColor?.g, titleColor?.b);
  pdf?.setFontSize(12);
  setFontWithFallback(pdf, fontConfig?.title, 'bold');
  pdf?.text('Gratitude', x, currentY);
  
  currentY += 8;
  
  // Lines for gratitude
  const gratitudeLines = Math.floor(section3Height / 6);
  for (let i = 0; i < gratitudeLines; i++) {
    const lineY = currentY + (i * 6);
    pdf?.line(x, lineY, x + width, lineY);
  }
  
  currentY += section3Height + 5;
  
  
  // Section 4: Meal Planner - Remaining space
  pdf?.setTextColor(titleColor?.r, titleColor?.g, titleColor?.b);
  pdf?.setFontSize(12);
  setFontWithFallback(pdf, fontConfig?.title, 'bold');
  pdf?.text('Meal Planner', x, currentY);
  
  currentY += 5;
  
  // Meal sections
  const meals = ['Breakfast:', 'Lunch:', 'Dinner:'];
  const mealSpacing = ((adjustedHeight - (currentY - contentStartY)) / 3) + 10;
  
  pdf?.setFontSize(10);
  setFontWithFallback(pdf, fontConfig?.title, 'bold');
  
  meals?.forEach((meal, index) => {
    const mealY = currentY + (index * mealSpacing);
    pdf?.text(meal, x, mealY);
    
    // Lines for meal planning
    const numLines = Math.floor((mealSpacing - 10) / 6);
    for (let i = 0; i < numLines; i++) {
      const lineY = mealY + 5 + (i * 6);
      pdf?.line(x + 5, lineY, x + width - 5, lineY);
    }
  });
};

/**
 * Generates a single month page (for preview mode)
 * UPDATED: Use setFontWithFallback for all font operations
 */
const generateSingleMonthPage = async (pdf, month, monthIndex, year, pageLinks, themeColors, fontConfig, mode = 'full') => {
  const pageWidth = pdf?.internal?.pageSize?.getWidth();
  const pageHeight = pdf?.internal?.pageSize?.getHeight();
  
  const bgColor = themeColors?.accent;  
  pdf?.setFillColor(bgColor?.r, bgColor?.g, bgColor?.b);
  pdf?.rect(0, 0, pageWidth, pageHeight, 'F');

  generateBackgroundText(pdf, themeColors, fontConfig, backgroundText);

  const leftMargin = 15;
  const rightMargin = 12;
  const calendarWidth = pageWidth - leftMargin - rightMargin;
  
  const titleColor = themeColors?.primary;
  pdf?.setTextColor(titleColor?.r, titleColor?.g, titleColor?.b);
  pdf?.setFontSize(22);
  setFontWithFallback(pdf, fontConfig?.title, 'bold');
  
  const monthName = format(month, 'MMMM');
  const yearText = year?.toString();
  const monthTitle = `${monthName} ${yearText}`;
  const titleWidth = pdf?.getTextWidth(monthTitle);
  const titleX = (pageWidth - titleWidth) / 2;
  
  pdf?.text(monthName, titleX, 25);
  
  const monthNameWidth = pdf?.getTextWidth(monthName);
  const spaceWidth = pdf?.getTextWidth(' ');
  const yearX = titleX + monthNameWidth + spaceWidth;
  const yearWidth = pdf?.getTextWidth(yearText);
  
  pdf?.setTextColor(titleColor?.r, titleColor?.g, titleColor?.b); 
  const yearOverviewPage = pageLinks?.get('year-overview');
  if (yearOverviewPage && mode !== 'preview') {
    pdf?.link(yearX, 25 - 15, yearWidth, 20, { pageNumber: yearOverviewPage });
  }
  pdf?.text(yearText, yearX, 25);
  
  pdf?.setTextColor(titleColor?.r, titleColor?.g, titleColor?.b);
  
  generateFullMonthCalendar(pdf, month, monthIndex, leftMargin, 40, calendarWidth, 150, pageLinks, themeColors, fontConfig, mode);
  
  pdf?.setFontSize(12);
  setFontWithFallback(pdf, fontConfig?.title, 'bold');
  pdf?.text('Monthly Goals:', leftMargin, 219);
  
  const lineColor = themeColors?.secondary;
  pdf?.setDrawColor(lineColor?.r, lineColor?.g, lineColor?.b);
  pdf?.setLineWidth(0.2);
  for (let i = 0; i < 7; i++) {
    const lineY = 220 + (i * 7);
    pdf?.line(leftMargin, lineY, pageWidth - rightMargin, lineY);
  }
};

/**
 * Generates a single week page (for preview mode)
 * UPDATED: Use setFontWithFallback for all font operations
 */
const generateSingleWeekPage = async (pdf, year, weekData, pageLinks, themeColors, fontConfig, mode = 'full', iconAliasManager) => {
  const pageWidth = pdf?.internal?.pageSize?.getWidth();
  const pageHeight = pdf?.internal?.pageSize?.getHeight();
  
  const bgColor = themeColors?.accent;
  pdf?.setFillColor(bgColor?.r, bgColor?.g, bgColor?.b);
  pdf?.rect(0, 0, pageWidth, pageHeight, 'F');
  
  generateBackgroundText(pdf, themeColors, fontConfig, backgroundText);

  const titleColor = themeColors?.primary;
  pdf?.setTextColor(titleColor?.r, titleColor?.g, titleColor?.b);
  pdf?.setFontSize(18);
  setFontWithFallback(pdf, fontConfig?.title, 'bold');
  
  const weekStart = weekData?.start;
  const weekEnd = weekData?.end;
  
  const startMonth = format(weekStart, 'MMM');
  const startDay = format(weekStart, 'd');
  const endMonth = format(weekEnd, 'MMM');
  const endDay = format(weekEnd, 'd');
  
  const dateRange = startMonth === endMonth 
    ? `${startMonth} ${startDay} - ${endDay}`
    : `${startMonth} ${startDay} - ${endMonth} ${endDay}`;
  
  const weekHeader = `Week ${weekData?.weekNumber} | ${dateRange} | ${year}`;
  const headerWidth = pdf?.getTextWidth(weekHeader);
  const headerX = (pageWidth - headerWidth) / 2;
  
  // Add clickable link to month page for the start date - DISABLED IN PREVIEW MODE
  const startMonthIndex = getMonth(weekStart);
  const monthPageNum = pageLinks?.get(`month-${startMonthIndex}`);
  if (monthPageNum && mode !== 'preview') {
    pdf?.link(headerX, 20 - 14, headerWidth, 18, { pageNumber: monthPageNum });
  }
  
  pdf?.text(weekHeader, headerX, 20);
  
  generateWeekDaySections(pdf, weekData, year, 20, 35, pageWidth - 40, pageHeight - 70, pageLinks, themeColors, fontConfig, mode);
  await generateWeekFooter(pdf, weekData, [weekData], pageLinks, pageWidth, pageHeight, themeColors, fontConfig, mode, iconAliasManager);
};

/**
 * Generates a single daily schedule page (for preview mode)
 * UPDATED: Replaced manual header code with generateDailyPageHeader function
 * FIXED: Corrected navigation icon positioning for right alignment
 * FIXED: Filter out disabled page types from navigation icons
 */

// ==================== DAILY NAV ICONS CONFIGURATION ====================
const DAILY_NAV_LAYOUT_CONFIG = {
  orientation: 'horizontal',
  spacing: 1,
  iconSize: 4.2,
  fontSize: 8,
  alignment: 'right'
};

const DAILY_NAV_STYLING_CONFIG = {
  type: 'none',
  width: 'consistent',
  padding: 1.2,
  borderRadius: 1.2,
  borderWidth: 0.5
};

// ==================== DAILY HEADER CONFIGURATION ====================
const DAILY_HEADER_LAYOUT_CONFIG = {
  yPosition: 20,
  dayNameOffset: 9,
  lineVerticalSpacing: 5,
  weekPosition: 'auto-right',
  weekRightMargin: 20,
  weekYOffset: 0
};

const DAILY_HEADER_STYLING_CONFIG = {
  style: 'title-bg',
  dateFormat: 'US',
  dateFontSize: 18,
  dateFontWeight: 'bold',
  dayNameFontSize: 18,
  dayNameFontWeight: 'bold',
  weekFontSize: 12,
  weekFontWeight: 'bold',
  lineStyle: 'gradient',
  lineThickness: 0.5,
  backgroundPadding: 3
};

const DAILY_HEADER_STYLE_CONFIGS = {
  'modern-border': {
    backgroundColor: 'none',
    textColor: 'primary',
    showDecorateLine: true,
    showWatermark: false
  },
  'title-bg': {
    backgroundColor: 'primary',
    textColor: 'white',
    showDecorateLine: false,
    showWatermark: false
  },
  'minimalist-accent': {
    backgroundColor: 'none',
    textColor: 'primary',
    showDecorateLine: true,
    showWatermark: false,
    accentSize: 3,
    accentOffset: 20
  },
  'watermark-overlay': {
    backgroundColor: 'none',
    textColor: 'primary',
    showDecorateLine: true,
    showWatermark: true,
    watermarkFontSize: 80,
    watermarkOpacity: 0.05,
    watermarkOffset: 60
  }
};

const generateSingleDailySchedulePage = async (pdf, day, dayIndex, allDays, year, pageLinks, themeColors, fontConfig, mode = 'full', timeFormat = '12h-long', timeRange = { start: 6, end: 18 }, iconAliasManager) => {
  const pageWidth = pdf?.internal?.pageSize?.getWidth();
  const pageHeight = pdf?.internal?.pageSize?.getHeight();
  
  const bgColor = themeColors?.accent;
  pdf?.setFillColor(bgColor?.r, bgColor?.g, bgColor?.b);
  pdf?.rect(0, 0, pageWidth, pageHeight, 'F');
  
  //background
  generateBackgroundText(pdf, themeColors, fontConfig, backgroundText);

  let monthIndex = getMonth(day);
  const dayOfMonth = getDate(day);
  
  const titleColor = themeColors?.primary;
  const secondaryColor = themeColors?.secondary;
  
  // STANDARDIZED NAVIGATION: All daily pages use same icons in same order
  const schedulePageNum = pageLinks?.get(`day-schedule-${monthIndex}-${dayOfMonth}`);
  const journalPageNum = pageLinks?.get(`day-journal-${monthIndex}-${dayOfMonth}`);
  const habitPageNum = pageLinks?.get(`habit-0`);
  const lifeHackPageNum = pageLinks?.get(`day-lifehack-${monthIndex}-${dayOfMonth}`);
  const taskPageNum = pageLinks?.get(`day-tasks-${monthIndex}-${dayOfMonth}`);
  
  // FIXED: Only include icons for page types that actually exist (have valid pageNum)
  // Current page (schedule) should always be included but disabled
  const iconConfigs = [
    {
      icon: 'calendar',
      label: 'Schedule',
      pageNum: null, // No link - current page
      enabled: false // Disabled/grayed out (current page)
    }
  ];
  
  // Only add journal icon if journal pages exist
  if (journalPageNum) {
    iconConfigs?.push({
      icon: 'book-open',
      label: 'Journal',
      pageNum: journalPageNum,
      enabled: true
    });
  }
  
  // Only add tasks icon if task pages exist
  if (taskPageNum) {
    iconConfigs?.push({
      icon: 'clipboard-check',
      label: 'Tasks',
      pageNum: taskPageNum,
      enabled: true
    });
  }
  
  // Only add life hack icon if life hack pages exist
  if (lifeHackPageNum) {
    iconConfigs?.push({
      icon: 'user',
      label: 'Life Hack',
      pageNum: lifeHackPageNum,
      enabled: true
    });
  }
  
  // Only add habits icon if habit pages exist
  if (habitPageNum) {
    iconConfigs?.push({
      icon: 'target',
      label: 'Habits',
      pageNum: habitPageNum,
      enabled: true
    });
  }
  
    iconConfigs?.push({
      icon: 'menu',
      label: 'Index',
      pageNum: pageLinks?.get('index'),
      enabled: true
    });
  
  // REPLACED: Use new generateDailyPageHeader function
  const headerMetadata = await generateDailyPageHeader({
    pdf,
    day,
    year,
    pageLinks,
    themeColors,
    fontConfig,
    iconAliasManager,
    mode
  });
  
  // FIXED: Call generateDailyNavIcons with corrected startX calculation
  // For right alignment, startX should be the right edge of the page
  await generateDailyNavIcons({
    pdf,
    iconAliasManager,
    iconConfigs,
    layout: {
      startX: 20, // FIXED: Use left margin for proper right alignment calculation
      startY: 25 + rightHeaderY,
      orientation: DAILY_NAV_LAYOUT_CONFIG?.orientation,
      spacing: DAILY_NAV_LAYOUT_CONFIG?.spacing,
      iconSize: DAILY_NAV_LAYOUT_CONFIG?.iconSize,
      fontSize: DAILY_NAV_LAYOUT_CONFIG?.fontSize,
      alignment: DAILY_NAV_LAYOUT_CONFIG?.alignment
    },
    styling: {
      type: DAILY_NAV_STYLING_CONFIG?.type,
      width: DAILY_NAV_STYLING_CONFIG?.width,
      padding: DAILY_NAV_STYLING_CONFIG?.padding,
      borderRadius: DAILY_NAV_STYLING_CONFIG?.borderRadius,
      borderWidth: DAILY_NAV_STYLING_CONFIG?.borderWidth
    },
    colors: {
      enabled: titleColor,
      disabled: secondaryColor,
      background: bgColor
    },
    fontConfig,
    mode
  });
  
  generateDailyPageContent(pdf, day, dayIndex, allDays, 20, headerMetadata?.bottomY + 10, pageWidth - 40, pageHeight - 90, pageLinks, themeColors, fontConfig, timeFormat, timeRange);
  await generateDailyScheduleFooter(pdf, day, dayIndex, allDays, pageLinks, pageWidth, pageHeight, themeColors, fontConfig, mode, iconAliasManager);
};

/**
 * Generates a single daily tasks page (for preview mode)
 * UPDATED: Replaced manual header code with generateDailyPageHeader function
 * FIXED: Corrected navigation icon positioning for right alignment
 * FIXED: Filter out disabled page types from navigation icons
 */
const generateSingleTasksPage = async (pdf, day, dayIndex, allDays, year, pageLinks, themeColors, fontConfig, mode = 'full', iconAliasManager) => {
  const pageWidth = pdf?.internal?.pageSize?.getWidth();
  const pageHeight = pdf?.internal?.pageSize?.getHeight();
  
  const bgColor = themeColors?.accent;
  pdf?.setFillColor(bgColor?.r, bgColor?.g, bgColor?.b);
  pdf?.rect(0, 0, pageWidth, pageHeight, 'F');
  
  let monthIndex = getMonth(day);
  const dayOfMonth = getDate(day);
  
  const titleColor = themeColors?.primary;
  const secondaryColor = themeColors?.secondary;
  
  // STANDARDIZED NAVIGATION: All daily pages use same icons in same order
  const schedulePageNum = pageLinks?.get(`day-schedule-${monthIndex}-${dayOfMonth}`);
  const journalPageNum = pageLinks?.get(`day-journal-${monthIndex}-${dayOfMonth}`);
  const habitPageNum = pageLinks?.get(`habit-0`);
  const lifeHackPageNum = pageLinks?.get(`day-lifehack-${monthIndex}-${dayOfMonth}`);
  const taskPageNum = pageLinks?.get(`day-tasks-${monthIndex}-${dayOfMonth}`);
  
  // FIXED: Only include icons for page types that actually exist (have valid pageNum)
  const iconConfigs = [];
  
  // Only add schedule icon if schedule pages exist
  if (schedulePageNum) {
    iconConfigs?.push({
      icon: 'calendar',
      label: 'Schedule',
      pageNum: schedulePageNum,
      enabled: true
    });
  }
  
  // Only add journal icon if journal pages exist
  if (journalPageNum) {
    iconConfigs?.push({
      icon: 'book-open',
      label: 'Journal',
      pageNum: journalPageNum,
      enabled: true
    });
  }
  
  // Current page (tasks) should always be included but disabled
  iconConfigs?.push({
    icon: 'clipboard-check',
    label: 'Tasks',
    pageNum: null, // No link - current page
    enabled: false // Disabled/grayed out (current page)
  });
  
  // Only add life hack icon if life hack pages exist
  if (lifeHackPageNum) {
    iconConfigs?.push({
      icon: 'user',
      label: 'Life Hack',
      pageNum: lifeHackPageNum,
      enabled: true
    });
  }
  
  // Only add habits icon if habit pages exist
  if (habitPageNum) {
    iconConfigs?.push({
      icon: 'target',
      label: 'Habits',
      pageNum: habitPageNum,
      enabled: true
    });
  }
  
      iconConfigs?.push({
      icon: 'menu',
      label: 'Index',
      pageNum: pageLinks?.get('index'),
      enabled: true
    });
	
	
  
  // REPLACED: Use new generateDailyPageHeader function
  const headerMetadata = await generateDailyPageHeader({
    pdf,
    day,
    year,
    pageLinks,
    themeColors,
    fontConfig,
    iconAliasManager,
    mode
  });
  
  // FIXED: Call generateDailyNavIcons with corrected startX calculation
  await generateDailyNavIcons({
    pdf,
    iconAliasManager,
    iconConfigs,
    layout: {
      startX: 20, // FIXED: Use left margin for proper right alignment calculation
      startY: 25 + rightHeaderY,
      orientation: DAILY_NAV_LAYOUT_CONFIG?.orientation,
      spacing: DAILY_NAV_LAYOUT_CONFIG?.spacing,
      iconSize: DAILY_NAV_LAYOUT_CONFIG?.iconSize,
      fontSize: DAILY_NAV_LAYOUT_CONFIG?.fontSize,
      alignment: DAILY_NAV_LAYOUT_CONFIG?.alignment
    },
    styling: {
      type: DAILY_NAV_STYLING_CONFIG?.type,
      width: DAILY_NAV_STYLING_CONFIG?.width,
      padding: DAILY_NAV_STYLING_CONFIG?.padding,
      borderRadius: DAILY_NAV_STYLING_CONFIG?.borderRadius,
      borderWidth: DAILY_NAV_STYLING_CONFIG?.borderWidth
    },
    colors: {
      enabled: titleColor,
      disabled: secondaryColor,
      background: bgColor
    },
    fontConfig,
    mode
  });
  
  generateTasksPageContent(pdf, day, dayIndex, allDays, 20, headerMetadata?.bottomY + 10, pageWidth - 40, pageHeight - 90, pageLinks, themeColors, fontConfig, iconAliasManager);
  await generateTasksFooter(pdf, day, dayIndex, allDays, pageLinks, pageWidth, pageHeight, themeColors, fontConfig, mode, iconAliasManager);
};

/**
 * Generates a single daily journal page (for preview mode)
 * UPDATED: Replaced manual header code with generateDailyPageHeader function
 * FIXED: Corrected navigation icon positioning for right alignment
 * FIXED: Filter out disabled page types from navigation icons
 */
const generateSingleDailyJournalPage = async (pdf, day, dayIndex, allDays, year, pageLinks, themeColors, fontConfig, mode = 'full', iconAliasManager, journalConfig = null) => {
  const pageWidth = pdf?.internal?.pageSize?.getWidth();
  const pageHeight = pdf?.internal?.pageSize?.getHeight();
  
  const bgColor = themeColors?.accent;
  const titleColor = themeColors?.primary;
  const secondaryColor = themeColors?.secondary;
  pdf?.setFillColor(bgColor?.r, bgColor?.g, bgColor?.b);
  pdf?.rect(0, 0, pageWidth, pageHeight, 'F');

generateBackgroundText(pdf, themeColors, fontConfig, backgroundText);
  
  let monthIndex = getMonth(day);
  const dayOfMonth = getDate(day);
  
   // STANDARDIZED NAVIGATION: All daily pages use same icons in same order
  const schedulePageNum = pageLinks?.get(`day-schedule-${monthIndex}-${dayOfMonth}`);
  const journalPageNum = pageLinks?.get(`day-journal-${monthIndex}-${dayOfMonth}`);
  const habitPageNum = pageLinks?.get(`habit-0`);
  const lifeHackPageNum = pageLinks?.get(`day-lifehack-${monthIndex}-${dayOfMonth}`);
  const taskPageNum = pageLinks?.get(`day-tasks-${monthIndex}-${dayOfMonth}`);
  
  // FIXED: Only include icons for page types that actually exist (have valid pageNum)
  const iconConfigs = [];
  
  // Only add schedule icon if schedule pages exist
  if (schedulePageNum) {
    iconConfigs?.push({
      icon: 'calendar',
      label: 'Schedule',
      pageNum: schedulePageNum,
      enabled: true
    });
  }
  
  // Current page (journal) should always be included but disabled
  iconConfigs?.push({
    icon: 'book-open',
    label: 'Journal',
    pageNum: null, // No link - current page
    enabled: false // Disabled/grayed out (current page)
  });
  
  // Only add tasks icon if task pages exist
  if (taskPageNum) {
    iconConfigs?.push({
      icon: 'clipboard-check',
      label: 'Tasks',
      pageNum: taskPageNum,
      enabled: true
    });
  }
  
  // Only add life hack icon if life hack pages exist
  if (lifeHackPageNum) {
    iconConfigs?.push({
      icon: 'user',
      label: 'Life Hack',
      pageNum: lifeHackPageNum,
      enabled: true
    });
  }
  
  // Only add habits icon if habit pages exist
  if (habitPageNum) {
    iconConfigs?.push({
      icon: 'target',
      label: 'Habits',
      pageNum: habitPageNum,
      enabled: true
    });
  }
  
  iconConfigs?.push({
      icon: 'menu',
      label: 'Index',
      pageNum: pageLinks?.get('index'),
      enabled: true
    });
  
  // REPLACED: Use new generateDailyPageHeader function
  const headerMetadata = await generateDailyPageHeader({
    pdf,
    day,
    year,
    pageLinks,
    themeColors,
    fontConfig,
    iconAliasManager,
    mode
  });
  
  // FIXED: Call generateDailyNavIcons with corrected startX calculation
  await generateDailyNavIcons({
    pdf,
    iconAliasManager,
    iconConfigs,
    layout: {
      startX: 20, // FIXED: Use left margin for proper right alignment calculation
      startY: 25 + rightHeaderY,
      orientation: DAILY_NAV_LAYOUT_CONFIG?.orientation,
      spacing: DAILY_NAV_LAYOUT_CONFIG?.spacing,
      iconSize: DAILY_NAV_LAYOUT_CONFIG?.iconSize,
      fontSize: DAILY_NAV_LAYOUT_CONFIG?.fontSize,
      alignment: DAILY_NAV_LAYOUT_CONFIG?.alignment
    },
    styling: {
      type: DAILY_NAV_STYLING_CONFIG?.type,
      width: DAILY_NAV_STYLING_CONFIG?.width,
      padding: DAILY_NAV_STYLING_CONFIG?.padding,
      borderRadius: DAILY_NAV_STYLING_CONFIG?.borderRadius,
      borderWidth: DAILY_NAV_STYLING_CONFIG?.borderWidth
    },
    colors: {
      enabled: titleColor,
      disabled: secondaryColor,
      background: bgColor
    },
    fontConfig,
    mode
  });
  
  // Generate journal page content with config
  generateJournalPageContent(pdf, day, dayIndex, allDays, 20, headerMetadata?.bottomY + 10, pageWidth - 40, pageHeight - 90, pageLinks, themeColors, fontConfig, journalConfig);
  await generateDailyJournalFooter(pdf, day, dayIndex, allDays, pageLinks, pageWidth, pageHeight, themeColors, fontConfig, mode, iconAliasManager);
};


const generateSingleScheduleOnlyPage = async (pdf, day, dayIndex, allDays, year, pageLinks, themeColors, fontConfig, mode = 'full', timeFormat = '12h-long', timeRange = { start: 6, end: 18 }, iconAliasManager) => {
  const pageWidth = pdf?.internal?.pageSize?.getWidth();
  const pageHeight = pdf?.internal?.pageSize?.getHeight();
  
  const bgColor = themeColors?.accent;
  pdf?.setFillColor(bgColor?.r, bgColor?.g, bgColor?.b);
  pdf?.rect(0, 0, pageWidth, pageHeight, 'F');
  
  // Background
  generateBackgroundText(pdf, themeColors, fontConfig, backgroundText);
  
  let monthIndex = getMonth(day);
  const dayOfMonth = getDate(day);
  
  const titleColor = themeColors?.primary;
  const secondaryColor = themeColors?.secondary;
  
  // STANDARDIZED NAVIGATION: All daily pages use same icons in same order
  // BUT schedule-only uses CLOCK icon instead of calendar
  const scheduleOnlyPageNum = pageLinks?.get(`day-scheduleonly-${monthIndex}-${dayOfMonth}`);
  const journalPageNum = pageLinks?.get(`day-journal-${monthIndex}-${dayOfMonth}`);
  const habitPageNum = pageLinks?.get(`habit-0`);
  const lifeHackPageNum = pageLinks?.get(`day-lifehack-${monthIndex}-${dayOfMonth}`);
  const taskPageNum = pageLinks?.get(`day-tasks-${monthIndex}-${dayOfMonth}`);
  
  // Icon configs - current page (schedule-only) with CLOCK icon
  const iconConfigs = [
    {
      icon: 'clock',
      label: 'Schedule',
      pageNum: null,
      enabled: false
    }
  ];
  
  // Only add journal icon if journal pages exist
  if (journalPageNum) {
    iconConfigs?.push({
      icon: 'book-open',
      label: 'Journal',
      pageNum: journalPageNum,
      enabled: true
    });
  }
  
  // Only add tasks icon if task pages exist
  if (taskPageNum) {
    iconConfigs?.push({
      icon: 'clipboard-check',
      label: 'Tasks',
      pageNum: taskPageNum,
      enabled: true
    });
  }
  
  
  // Only add life hack icon if life hack pages exist
  if (lifeHackPageNum) {
    iconConfigs?.push({
      icon: 'user',
      label: 'Life Hack',
      pageNum: lifeHackPageNum,
      enabled: true
    });
  }
  
  // Only add habits icon if habit pages exist
  if (habitPageNum) {
    iconConfigs?.push({
      icon: 'target',
      label: 'Habits',
      pageNum: habitPageNum,
      enabled: true
    });
  }
  
  iconConfigs?.push({
      icon: 'menu',
      label: 'Index',
      pageNum: pageLinks?.get('index'),
      enabled: true
    });
  
  
  // Generate header
  const headerMetadata = await generateDailyPageHeader({
    pdf,
    day,
    year,
    pageLinks,
    themeColors,
    fontConfig,
    iconAliasManager,
    mode
  });
  
  // Generate navigation icons
  await generateDailyNavIcons({
    pdf,
    iconAliasManager,
    iconConfigs,
    layout: {
      startX: 20,
      startY: 25 + rightHeaderY,
      orientation: DAILY_NAV_LAYOUT_CONFIG?.orientation,
      spacing: DAILY_NAV_LAYOUT_CONFIG?.spacing,
      iconSize: DAILY_NAV_LAYOUT_CONFIG?.iconSize,
      fontSize: DAILY_NAV_LAYOUT_CONFIG?.fontSize,
      alignment: DAILY_NAV_LAYOUT_CONFIG?.alignment
    },
    styling: {
      type: DAILY_NAV_STYLING_CONFIG?.type,
      width: DAILY_NAV_STYLING_CONFIG?.width,
      padding: DAILY_NAV_STYLING_CONFIG?.padding,
      borderRadius: DAILY_NAV_STYLING_CONFIG?.borderRadius,
      borderWidth: DAILY_NAV_STYLING_CONFIG?.borderWidth
    },
    colors: {
      enabled: titleColor,
      disabled: secondaryColor,
      background: bgColor
    },
    fontConfig,
    mode
  });
  
  // Generate schedule-only content
  generateScheduleOnlyPageContent(pdf, day, dayIndex, allDays, 20, headerMetadata?.bottomY + 10, pageWidth - 40, pageHeight - 90, pageLinks, themeColors, fontConfig, timeFormat, timeRange);
  
  // Generate footer
  await generateScheduleOnlyFooter(pdf, day, dayIndex, allDays, pageLinks, pageWidth, pageHeight, themeColors, fontConfig, mode, iconAliasManager);
};



const generateScheduleOnlyPageContent = (pdf, day, dayIndex, allDays, x, y, width, height, pageLinks, themeColors, fontConfig, timeFormat = '12h-long', timeRange = { start: 6, end: 18 }) => {
  const titleColor = themeColors?.primary;
  const secondaryColor = themeColors?.secondary;
  const accentColor = themeColors?.accent;
  
  // Get quote for this day
  const dayOfYear = getDayOfYear(day);
  const quote = getQuoteByDayOfYear(dayOfYear, ['motivational']);
  
  const quoteStartY = y - 15;
  const pageWidth = pdf?.internal?.pageSize?.getWidth();
  const pageHeight = pdf?.internal?.pageSize?.getHeight();
  
  // Quote text
  pdf?.setTextColor(titleColor?.r, titleColor?.g, titleColor?.b);
  pdf?.setFontSize(10);
  setFontWithFallback(pdf, fontConfig?.content, 'italic');
  
  const maxQuoteWidth = width - 20;
  const quoteLines = pdf?.splitTextToSize(`"${quote?.text}"`, maxQuoteWidth);
  
  let currentQuoteY = quoteStartY + 8;
  quoteLines?.forEach((line, index) => {
    const lineWidth = pdf?.getTextWidth(line);
    const lineX = x + (width - lineWidth) / 2;
    pdf?.text(line, lineX, currentQuoteY + (index * 4));
  });
  
  // Author text
  pdf?.setFontSize(8);
  const authorText = `- ${quote?.author}`;
  const authorWidth = pdf?.getTextWidth(authorText);
  const authorX = x + (width - authorWidth) / 2;
  const authorY = currentQuoteY + (quoteLines?.length * 4) + 2;
  pdf?.text(authorText, authorX, authorY);
  
  // Adjust content start position
  const contentStartY = authorY + 5;
  const adjustedHeight = pageHeight - contentStartY ;
  const calendarSizeX = 38; // ~1.5 inches (38mm)
  const calendarSizeY = 38*0.9; // also adjust the "adjusted Height"
  
  // Calculate section heights (70% schedule, 25% calendars, 5% margin)
  const scheduleHeight = adjustedHeight - calendarSizeY-25;
  const calendarHeight = calendarSizeY; // redundant declaration?
  
  // Schedule section header
  pdf?.setTextColor(titleColor?.r, titleColor?.g, titleColor?.b);
  pdf?.setFontSize(headerTextSize);
  setFontWithFallback(pdf, fontConfig?.title, 'bold');
  pdf?.text('Schedule', x, contentStartY);
  
  // Generate time slots with 30-minute markings
  const timeSlots = generateTimeSlots(timeFormat, timeRange?.start, timeRange?.end, false);
  
  setFontWithFallback(pdf, fontConfig?.content, 'normal');
  pdf?.setFontSize(10);
  
  const lineColor = themeColors?.secondary;
  const halfLineColor = themeColors?.accent;
  pdf?.setDrawColor(lineColor?.r, lineColor?.g, lineColor?.b);
  
  // Calculate variable line spacing to fill schedule section
  const numFullHours = Math.ceil((timeRange?.end - timeRange?.start + 1));
  const totalSlots = timeSlots?.length;
  const lineSpacing = scheduleHeight / (totalSlots + 1);
  
  // Draw time slots with variable spacing
  timeSlots?.forEach((slot, index) => {
    const slotY = contentStartY + 10 + (index * lineSpacing);
    
    if (!slot?.isHalfHour) {
      // Full hour - draw time label and full line
      // FIXED: Validate that slot.time is a string before rendering
      const timeText = typeof slot?.time === 'string' ? slot?.time : String(slot?.time || '');
      pdf?.text(timeText, x + 11, slotY - 4, {
        align: 'right'
      });
	  pdf?.setDrawColor(lineColor?.r, lineColor?.g, lineColor?.b);
      pdf?.setLineWidth(0.3);
      pdf?.line(x + 8, slotY - 3, x + width - 5, slotY - 3);
	  
	  //pdf?.setDrawColor(255, 255, 255);
	  if(index < timeSlots?.length-1){
		pdf?.setLineWidth(0.1);
	  
		  pdf?.line(	
						x + 10, 
						(slotY - 3) + (lineSpacing/2), 
						x + width - 5 , 
						(slotY - 3) + (lineSpacing/2)
					);
	  }
    } else {
      // 30-minute marking - draw lighter, shorter line (50% width)
      const lineStartX = x + 8;
      const lineEndX = x + 8 + ((width - 13) * 0.5);
      
      // Use lighter color (white)
      pdf?.saveGraphicsState();
      pdf?.setDrawColor(255, 255, 255);
      pdf?.setLineWidth(0.1);
      pdf?.line(lineStartX, slotY - 3, lineEndX, slotY - 3);
      pdf?.restoreGraphicsState();
      
      // Reset to normal line color for next slot
      pdf?.setDrawColor(lineColor?.r, lineColor?.g, lineColor?.b);
    }
  });
  
  // Calculate calendar section position
  const calendarStartY = contentStartY + scheduleHeight + 10;
  
  // Mini calendars section (3 calendars: previous, current, next month)
  

  const totalCalendarWidth = calendarSizeX * 3;
  const calendarSpacing = (width - totalCalendarWidth) / 2;
  
  // Get month dates
  const currentMonth = startOfMonth(day);
  const prevMonth = new Date(currentMonth);
  prevMonth?.setMonth(prevMonth?.getMonth() - 1);
  const nextMonth = new Date(currentMonth);
  nextMonth?.setMonth(nextMonth?.getMonth() + 1);
  
  // Handle year boundary cases
  if (getMonth(day) === 0) {
    // January - previous month is December of previous year
    prevMonth?.setFullYear(prevMonth?.getFullYear() - 1);
  }
  if (getMonth(day) === 11) {
    // December - next month is January of next year
    nextMonth?.setFullYear(nextMonth?.getFullYear() + 1);
  }
  
  // Get month indices for generateMiniCalendar
  const currentMonthIndex = getMonth(currentMonth);
  const prevMonthIndex = getMonth(prevMonth);
  const nextMonthIndex = getMonth(nextMonth);
  
  // Previous Month Calendar (left aligned)
  const prevCalX = x;
  const miniCalStartY = pageHeight -10- calendarSizeY; // start from the bottom
  generateMiniCalendar(pdf, prevMonth, prevMonthIndex, prevCalX, miniCalStartY, calendarSizeX, calendarSizeY, pageLinks, themeColors, fontConfig, true, 'preview', true, day);
  
  // Current Month Calendar (center aligned)
  const currentCalX = x + (width - calendarSizeX) / 2;
  generateMiniCalendar(pdf, currentMonth, currentMonthIndex, currentCalX, miniCalStartY, calendarSizeX, calendarSizeY, pageLinks, themeColors, fontConfig, true, 'preview', true, day);
  
  // Next Month Calendar (right aligned)
  const nextCalX = x + width - calendarSizeX;
  generateMiniCalendar(pdf, nextMonth, nextMonthIndex, nextCalX, miniCalStartY, calendarSizeX, calendarSizeY, pageLinks, themeColors, fontConfig, true, 'preview', true, day);
};


/**
 * Generates footer for schedule-only page with navigation
 * NEW FUNCTION for schedule-only page
 */
const generateScheduleOnlyFooter = async (pdf, currentDay, dayIndex, allDays, pageLinks, pageWidth, pageHeight, themeColors, fontConfig, mode = 'full', iconAliasManager) => {
  const footerY = pageHeight - 10;
  const titleColor = themeColors?.primary;
  const iconSize = 5;
  const bgColor = themeColors?.accent;
  
  pdf?.setFontSize(10);
  setFontWithFallback(pdf, fontConfig?.content, 'normal');
  
  // Previous Day Link - USE ALIAS
  if (dayIndex > 0) {
    const prevDay = allDays?.[dayIndex - 1];
    const prevDayText = 'Previous Day';
    const prevMonthIndex = getMonth(prevDay);
    const prevDayOfMonth = getDate(prevDay);
    const prevPageNum = pageLinks?.get(`day-scheduleonly-${prevMonthIndex}-${prevDayOfMonth}`);
    
    if (prevPageNum) {
      let iconX = 20;
      let iconY = footerY - 4;
      const textX = iconX + iconSize + 2;
      
      pdf?.setTextColor(titleColor?.r, titleColor?.g, titleColor?.b);
      
      const aliasKey = iconAliasManager?.getCacheKey('arrow-left-circle', ICON_SIZES?.dailyIcons, titleColor, bgColor);
      iconAliasManager?.addToPage(aliasKey, iconX, iconY, iconSize, iconSize);
      
      const prevTextWidth = pdf?.getTextWidth(prevDayText);
      if (mode !== 'preview') {
        pdf?.link(iconX, footerY - 8, iconSize + prevTextWidth + 6, 10, { pageNumber: prevPageNum });
      }
      pdf?.text(prevDayText, textX, footerY);
    }
  }
  
  // Next Day Link - USE ALIAS
  if (dayIndex < allDays?.length - 1) {
    const nextDay = allDays?.[dayIndex + 1];
    const nextDayText = 'Next Day';
    const nextMonthIndex = getMonth(nextDay);
    const nextDayOfMonth = getDate(nextDay);
    const nextPageNum = pageLinks?.get(`day-scheduleonly-${nextMonthIndex}-${nextDayOfMonth}`);
    
    if (nextPageNum) {
      pdf?.setTextColor(titleColor?.r, titleColor?.g, titleColor?.b);
      const nextTextWidth = pdf?.getTextWidth(nextDayText);
      
      const textX = pageWidth - 20 - nextTextWidth - iconSize - 2;
      let iconX = pageWidth - 20 - iconSize;
      let iconY = footerY - 4;
      
      if (mode !== 'preview') {
        pdf?.link(textX, footerY - 8, nextTextWidth + iconSize + 6, 10, { pageNumber: nextPageNum });
      }
      pdf?.text(nextDayText, textX, footerY);
      
      const aliasKey = iconAliasManager?.getCacheKey('arrow-right-circle', ICON_SIZES?.dailyIcons, titleColor, bgColor);
      iconAliasManager?.addToPage(aliasKey, iconX, iconY, iconSize, iconSize);
    }
  }
};





/**
 * Generates a single daily life hack page
 * UPDATED: Replaced manual header code with generateDailyPageHeader function
 * FIXED: Corrected navigation icon positioning for right alignment
 * FIXED: Filter out disabled page types from navigation icons
 */
const generateSingleLifeHackPage = async (pdf, day, dayIndex, allDays, year, pageLinks, themeColors, fontConfig, mode = 'full', iconAliasManager) => {
  const pageWidth = pdf?.internal?.pageSize?.getWidth();
  const pageHeight = pdf?.internal?.pageSize?.getHeight();
  
  const bgColor = themeColors?.accent;
  const titleColor = themeColors?.primary;
  const secondaryColor = themeColors?.secondary;
  pdf?.setFillColor(bgColor?.r, bgColor?.g, bgColor?.b);
  pdf?.rect(0, 0, pageWidth, pageHeight, 'F');

  generateBackgroundText(pdf, themeColors, fontConfig, backgroundText);
  
  let monthIndex = getMonth(day);
  const dayOfMonth = getDate(day);
  
  // STANDARDIZED NAVIGATION: All daily pages use same icons in same order
  const schedulePageNum = pageLinks?.get(`day-schedule-${monthIndex}-${dayOfMonth}`);
  const journalPageNum = pageLinks?.get(`day-journal-${monthIndex}-${dayOfMonth}`);
  const habitPageNum = pageLinks?.get(`habit-0`);
  const lifeHackPageNum = pageLinks?.get(`day-lifehack-${monthIndex}-${dayOfMonth}`);
  const taskPageNum = pageLinks?.get(`day-tasks-${monthIndex}-${dayOfMonth}`);
  
  // FIXED: Only include icons for page types that actually exist (have valid pageNum)
  const iconConfigs = [];
  
  // Only add schedule icon if schedule pages exist
  if (schedulePageNum) {
    iconConfigs?.push({
      icon: 'calendar',
      label: 'Schedule',
      pageNum: schedulePageNum,
      enabled: true
    });
  }
  
  // Only add journal icon if journal pages exist
  if (journalPageNum) {
    iconConfigs?.push({
      icon: 'book-open',
      label: 'Journal',
      pageNum: journalPageNum,
      enabled: true
    });
  }
  
  // Only add tasks icon if task pages exist
  if (taskPageNum) {
    iconConfigs?.push({
      icon: 'clipboard-check',
      label: 'Tasks',
      pageNum: taskPageNum,
      enabled: true
    });
  }
  
  // Current page (life hack) should always be included but disabled
  iconConfigs?.push({
    icon: 'user',
    label: 'Life Hack',
    pageNum: null, // No link - current page
    enabled: false // Disabled/grayed out (current page)
  });
  
  // Only add habits icon if habit pages exist
  if (habitPageNum) {
    iconConfigs?.push({
      icon: 'target',
      label: 'Habits',
      pageNum: habitPageNum,
      enabled: true
    });
  }
  
  iconConfigs?.push({
      icon: 'menu',
      label: 'Index',
      pageNum: pageLinks?.get('index'),
      enabled: true
    });
  
  // REPLACED: Use new generateDailyPageHeader function
  const headerMetadata = await generateDailyPageHeader({
    pdf,
    day,
    year,
    pageLinks,
    themeColors,
    fontConfig,
    iconAliasManager,
    mode
  });
  
  // FIXED: Call generateDailyNavIcons with corrected startX calculation
  await generateDailyNavIcons({
    pdf,
    iconAliasManager,
    iconConfigs,
    layout: {
      startX: 20, // FIXED: Use left margin for proper right alignment calculation
      startY: 25 + rightHeaderY,
      orientation: DAILY_NAV_LAYOUT_CONFIG?.orientation,
      spacing: DAILY_NAV_LAYOUT_CONFIG?.spacing,
      iconSize: DAILY_NAV_LAYOUT_CONFIG?.iconSize,
      fontSize: DAILY_NAV_LAYOUT_CONFIG?.fontSize,
      alignment: DAILY_NAV_LAYOUT_CONFIG?.alignment
    },
    styling: {
      type: DAILY_NAV_STYLING_CONFIG?.type,
      width: DAILY_NAV_STYLING_CONFIG?.width,
      padding: DAILY_NAV_STYLING_CONFIG?.padding,
      borderRadius: DAILY_NAV_STYLING_CONFIG?.borderRadius,
      borderWidth: DAILY_NAV_STYLING_CONFIG?.borderWidth
    },
    colors: {
      enabled: titleColor,
      disabled: secondaryColor,
      background: bgColor
    },
    fontConfig,
    mode
  });
  
  // Generate life hack page content
  await generateLifeHackPageContent(pdf, day, dayIndex, allDays, 20, headerMetadata?.bottomY + 10, pageWidth - 40, pageHeight - 90, pageLinks, themeColors, fontConfig, iconAliasManager);
  await generateLifeHackFooter(pdf, day, dayIndex, allDays, pageLinks, pageWidth, pageHeight, themeColors, fontConfig, mode, iconAliasManager);
};

/**
 * Generates footer for life hack page with navigation
 * NEW FUNCTION
 * FIXED: Increase icon pixel resolution from 16 to 64 for crisp rendering
 */
const generateLifeHackFooter = async (pdf, currentDay, dayIndex, allDays, pageLinks, pageWidth, pageHeight, themeColors, fontConfig, mode = 'full', iconAliasManager) => {
  const footerY = pageHeight - 10;
  const titleColor = themeColors?.primary;
  const iconSize = 5;
  const bgColor = themeColors?.accent;
  
  pdf?.setFontSize(10);
  setFontWithFallback(pdf, fontConfig?.content, 'normal');
  
  // Previous Page Link (points to current day's journal page)
  let monthIndex = getMonth(currentDay);
  const dayOfMonth = getDate(currentDay);
  const prevPageNum = pageLinks?.get(`day-journal-${monthIndex}-${dayOfMonth}`);
  
  if (prevPageNum) {
    // FIXED: Increase pixel size from 16 to 64 for crisp rendering
    const leftArrowIconPng = await getIcon('arrow-left-circle', titleColor, ICON_SIZES?.dailyIcons, bgColor);
    
    const prevPageText = 'Previous Page';
    let iconX = 20;
    let iconY = footerY - 4;
    const textX = iconX + iconSize + 2;
    
    pdf?.setTextColor(titleColor?.r, titleColor?.g, titleColor?.b);
    
    // FIXED: Changed getAliasKey to getCacheKey
    const aliasKey = iconAliasManager?.getCacheKey('arrow-left-circle', ICON_SIZES?.dailyIcons, titleColor, bgColor);
    iconAliasManager?.addToPage(aliasKey, iconX, iconY, iconSize, iconSize);
    
    const prevTextWidth = pdf?.getTextWidth(prevPageText);
    if (mode !== 'preview') {
      pdf?.link(iconX, footerY - 8, iconSize + prevTextWidth + 6, 10, { pageNumber: prevPageNum });
    }
    pdf?.text(prevPageText, textX, footerY);
  }
  
  // Next Day Link (points to next day's schedule page)
  if (dayIndex < allDays?.length - 1) {
    const nextDay = allDays?.[dayIndex + 1];
    const nextDayText = 'Next Day';
    const nextMonthIndex = getMonth(nextDay);
    const nextDayOfMonth = getDate(nextDay);
    const nextPageNum = pageLinks?.get(`day-schedule-${nextMonthIndex}-${nextDayOfMonth}`);
    
    if (nextPageNum) {
      // FIXED: Increase pixel size from 16 to 64 for crisp rendering
      const rightArrowIconPng = await getIcon('arrow-right-circle', titleColor, ICON_SIZES?.dailyIcons, bgColor);
      
      pdf?.setTextColor(titleColor?.r, titleColor?.g, titleColor?.b);
      const nextTextWidth = pdf?.getTextWidth(nextDayText);
      
      const textX = pageWidth - 20 - nextTextWidth - iconSize - 2;
      let iconX = pageWidth - 20 - iconSize;
      let iconY = footerY - 4;
      
      if (mode !== 'preview') {
        pdf?.link(textX, footerY - 8, nextTextWidth + iconSize + 6, 10, { pageNumber: nextPageNum });
      }
      pdf?.text(nextDayText, textX, footerY);
      
      // FIXED: Changed getAliasKey to getCacheKey
      const aliasKey = iconAliasManager?.getCacheKey('arrow-right-circle', ICON_SIZES?.dailyIcons, titleColor, bgColor);
      iconAliasManager?.addToPage(aliasKey, iconX, iconY, iconSize, iconSize);
    }
  }
};


/**
 * Generates a single habit tracker page with quarterly grids
 * NEW FUNCTION
 */
const generateSingleHabitTrackerPage = async (pdf, habit, habitIndex, year, pageLinks, themeColors, fontConfig, mode = 'full') => {
  const pageWidth = pdf?.internal?.pageSize?.getWidth();
  const pageHeight = pdf?.internal?.pageSize?.getHeight();
  
  const bgColor = themeColors?.accent;
  pdf?.setFillColor(bgColor?.r, bgColor?.g, bgColor?.b);
  pdf?.rect(0, 0, pageWidth, pageHeight, 'F');
  
  // Header
  const titleColor = themeColors?.primary;
  pdf?.setTextColor(titleColor?.r, titleColor?.g, titleColor?.b);
  pdf?.setFontSize(20);
  setFontWithFallback(pdf, fontConfig?.title, 'bold');
  
  const headerText = `${habitIndex + 1}. ${habit?.name || " "} Habit Tracker`;
  const headerWidth = pdf?.getTextWidth(headerText);
  pdf?.text(headerText, (pageWidth - headerWidth) / 2, 15);
  
  // Two-column info section at top (1/3 of page)
  const infoSectionHeight = pageHeight * 0.25;
  const columnWidth = (pageWidth - 40) / 2;
  const leftColumnX = 20;
  const rightColumnX = leftColumnX + columnWidth + 10;
  const topSectionY = 25;
  
  // Left Column: How to Create Good Habits
  pdf?.setFontSize(12);
  setFontWithFallback(pdf, fontConfig?.title, 'bold');
  pdf?.text('How to Create Good Habits', leftColumnX, topSectionY);
  
  pdf?.setFontSize(8);
  setFontWithFallback(pdf, fontConfig?.content, 'normal');
  const tips = [
    '• Be specific and measurable',
    '• Start small and build gradually',
    '• Stack habits with existing routines',
    '• Track progress daily',
    '• Stay consistent and patient',
    '• Reward yourself for milestones'
  ];
  
  tips?.forEach((tip, index) => {
    pdf?.text(tip, leftColumnX + 2, topSectionY + 8 + (index * 5));
  });
  
  // Right Column: Habit Details (fillable lines)
  pdf?.setFontSize(11);
  setFontWithFallback(pdf, fontConfig?.title, 'bold');
  pdf?.text('Habit Details', rightColumnX, topSectionY);
  /*
  pdf?.setFontSize(9);
  pdf?.setFont(fontConfig?.title, 'bold');
  pdf?.text(`Habit: ${habit?.name || ''}`, rightColumnX, topSectionY + 8);
  */
  pdf?.setFontSize(9);
  setFontWithFallback(pdf, fontConfig?.content, 'normal');
  const lineColor = themeColors?.secondary;
  pdf?.setDrawColor(lineColor?.r, lineColor?.g, lineColor?.b);
  pdf?.setLineWidth(0.2);
  
  const detailLabels = ['When:', 'Where:', 'Measurement:', 'Reward:', 'Trigger:'];
  detailLabels?.forEach((label, index) => {
    const lineY = topSectionY + 8 + (index * 8);
    pdf?.text(label, rightColumnX, lineY);
    pdf?.line(rightColumnX + 25, lineY, rightColumnX + columnWidth - 5, lineY);
  });
  
  // Quarterly grids section (2/3 of page) - 2 quarters per row
  const gridSectionY = topSectionY + infoSectionHeight + 10;
  const gridWidth = (pageWidth - 50) / 2; // Two grids per row with 25mm spacing
  const gridHeight = (pageHeight - gridSectionY - 15) / 2;
  
  const quarters = getYearQuarters(year);
  
  for (let q = 0; q < 4; q++) {
    const quarter = quarters?.[q];
    const row = Math.floor(q / 2);
    const col = q % 2;
    
    const gridX = 20 + (col * (gridWidth + 20));
    const gridY = gridSectionY + (row * (gridHeight + 10));
    
    await generateQuarterlyHabitGrid(pdf, quarter, q + 1, gridX, gridY, gridWidth, gridHeight - 10, themeColors, fontConfig, year, pageLinks, mode);
  }
};

/**
 * Generates a single quarterly habit grid
 * NEW FUNCTION
 */
const generateQuarterlyHabitGrid = async (pdf, quarter, quarterNum, x, y, width, height, themeColors, fontConfig, year, pageLinks, mode = 'full') => {
  const titleColor = themeColors?.primary;
  const lineColor = themeColors?.secondary;
  
  // Quarter header
  pdf?.setFontSize(11);
  setFontWithFallback(pdf, fontConfig?.title, 'bold');
  pdf?.setTextColor(titleColor?.r, titleColor?.g, titleColor?.b);
  
  const headerText = `Quarter ${quarterNum} (Weeks ${quarter?.startWeek}-${quarter?.endWeek}${quarter?.endWeek === 53 ? '' : ''})`;
  pdf?.text(headerText, x, y-15);
  
  // Grid structure
  const daysOfWeek = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
  const numWeeks = quarter?.weeks?.length;
  const checkboxSize = 3;
  const dayColumnWidth = 8;
  const weekColumnWidth = (width - dayColumnWidth) / numWeeks;
  
  // Draw day of week labels (left column)
  pdf?.setFontSize(7);
  setFontWithFallback(pdf, fontConfig?.content, 'normal');
  
  daysOfWeek?.forEach((day, dayIndex) => {
    const labelY = y + 10 + (dayIndex * (checkboxSize + 1));
    pdf?.text(day, x + 2, labelY + 2);
  });
  
  // Draw week headers (rotated 90 degrees counterclockwise) and checkboxes
  quarter?.weeks?.forEach((weekData, weekIndex) => {
    const weekX = x + dayColumnWidth + (weekIndex * weekColumnWidth) ;
    const headerY = y + 8;
    
    // Week label (e.g., "Jan 7 W1")
    pdf?.saveGraphicsState();
    pdf?.setFontSize(6);
    setFontWithFallback(pdf, fontConfig?.content, 'bold');
    pdf?.setTextColor(titleColor?.r, titleColor?.g, titleColor?.b);
    
    const weekLabel = formatWeekLabel(weekData?.start, weekData?.weekNumber, year);
    
    pdf?.text(weekLabel, weekX+ 5 + (weekColumnWidth / 2) + 1, headerY, {
      angle: 90,
      align: 'right'
    });
    pdf?.restoreGraphicsState();
    
    // Draw checkboxes for each day of the week
    pdf?.setDrawColor(lineColor?.r, lineColor?.g, lineColor?.b);
    pdf?.setLineWidth(0.3);
    
    daysOfWeek?.forEach((day, dayIndex) => {
      const boxY = y + 10 + (dayIndex * (checkboxSize + 1));
      const boxX = weekX + (weekColumnWidth - checkboxSize) / 2;
      
      // Check if this day should be grayed out (outside year boundaries)
      const dayOfWeek = weekData?.start?.getDay() + dayIndex;
      const actualDate = new Date(weekData?.start);
      actualDate?.setDate(actualDate?.getDate() + dayIndex);
      
      const isOutsideYear = actualDate?.getFullYear() !== year;
      
      if (isOutsideYear) {
        // Gray out checkbox for days outside year
        //pdf?.setFillColor(200, 200, 200);
        //pdf?.rect(boxX, boxY, checkboxSize, checkboxSize, 'FD');
      } else {
        // Normal checkbox
        pdf?.rect(boxX, boxY, checkboxSize, checkboxSize);
      }
    });
  });
};

/**
 * Calculates quarters with week data for the year
 * NEW FUNCTION
 */
const getYearQuarters = (year) => {
  const yearStart = new Date(year, 0, 1);
  const yearEnd = new Date(year, 11, 31);
  
  // Calculate all weeks in the year
  const weeks = [];
  let currentWeekStart = startOfWeek(yearStart, { weekStartsOn: 0 });
  let weekNumber = 1;
  
  while (currentWeekStart <= yearEnd || weekNumber <= 53) {
    const weekEnd = endOfWeek(currentWeekStart, { weekStartsOn: 0 });
    
    // Include week if any day falls in target year
    if (isSameYear(currentWeekStart, yearStart) || isSameYear(weekEnd, yearStart)) {
      weeks?.push({
        weekNumber,
        start: currentWeekStart,
        end: weekEnd
      });
    }
    
    currentWeekStart = addWeeks(currentWeekStart, 1);
    weekNumber++;
    
    if (weekNumber > 54) break; // Safety limit
  }
  
  // Divide into quarters (13 weeks each, Q4 may have 14 if week 53 exists)
  const quarters = [];
  const weeksPerQuarter = Math.ceil(weeks?.length / 4);
  
  for (let q = 0; q < 4; q++) {
    const startIdx = q * 13;
    const endIdx = q === 3 ? weeks?.length : Math.min((q + 1) * 13, weeks?.length);
    const quarterWeeks = weeks?.slice(startIdx, endIdx);
    
    if (quarterWeeks?.length > 0) {
      quarters?.push({
        startWeek: quarterWeeks?.[0]?.weekNumber,
        endWeek: quarterWeeks?.[quarterWeeks?.length - 1]?.weekNumber,
        weeks: quarterWeeks
      });
    }
  }
  
  return quarters;
};

/**
 * Formats week label for habit tracker (e.g., "Jan 7 W1")
 * NEW FUNCTION
 */
const formatWeekLabel = (weekStart, weekNumber, year) => {
  // For Week 1, use Jan 1 regardless of actual start date
  if (weekNumber === 1) {
    return 'Jan 1';
  }
  
  const month = format(weekStart, 'MMM');
  const day = format(weekStart, 'd');
  //return `${month} ${day} W${weekNumber}`;
  return `${month} ${day}`;
};

/**
 * Generates daily page header with date, day name, week number, and navigation links
 * 
 * FUNCTION STRUCTURE:
 * - Draws date header (Month | Day | Year) with clickable links
 * - Displays day name below date
 * - Shows week number with clickable link
 * - Applies selected header style (title-bg, modern-border, minimalist-accent, watermark-overlay)
 * - Draws decorative line with selected style (solid, dotted, dashed, gradient)
 * - Internally calls navigation icon function for page navigation
 * - Returns metadata including bottom Y position for subsequent content positioning
 * 
 * @param {Object} params - Function parameters
 * @param {Object} params.pdf - jsPDF instance
 * @param {Date} params.day - Current day date object
 * @param {number} params.year - Year for the planner
 * @param {Map} params.pageLinks - Map of page references for link generation
 * @param {Object} params.themeColors - Theme color object with primary, secondary, accent
 * @param {Object} params.fontConfig - Font configuration object with title and content fonts
 * @param {Object} params.iconAliasManager - Icon alias manager for navigation icons
 * @param {string} params.mode - Generation mode ('preview' or 'full')
 * @param {Object} params.layoutOverride - Optional layout configuration override
 * @param {Object} params.stylingOverride - Optional styling configuration override
 * 
 * @returns {Object} Metadata object with:
 *   - bottomY: Bottom Y position after header (for content positioning)
 *   - dateHeaderBounds: Bounds of date header {x, y, width, height}
 *   - dayNameBounds: Bounds of day name {x, y, width, height}
 *   - weekBounds: Bounds of week number {x, y, width, height}
 *   - lineBounds: Bounds of decorative line {x, y, width, height}
 *   - totalHeight: Total height consumed by header
 */
const generateDailyPageHeader = async ({
  pdf,
  day,
  year,
  pageLinks,
  themeColors,
  fontConfig,
  iconAliasManager,
  mode = 'full',
  layoutOverride = {},
  stylingOverride = {}
}) => {
  // Merge configurations with overrides
  const layout = { ...DAILY_HEADER_LAYOUT_CONFIG, ...layoutOverride };
  const styling = { ...DAILY_HEADER_STYLING_CONFIG, ...stylingOverride };
  const styleConfig = DAILY_HEADER_STYLE_CONFIGS?.[styling?.style] || DAILY_HEADER_STYLE_CONFIGS?.['modern-border'];
  
  const pageWidth = pdf?.internal?.pageSize?.getWidth();
  const pageHeight = pdf?.internal?.pageSize?.getHeight();
  
  // Extract date components
  const monthName = format(day, 'MMMM');
  const dayNum = format(day, 'dd');
  const yearText = year?.toString();
  let monthIndex = getMonth(day);
  const dayOfMonth = getDate(day);
  const dayName = format(day, 'EEEE');
  
  // Calculate week number
  const weekStart = startOfWeek(day, { weekStartsOn: 0 });
  let weekNumber = 1;
  const yearStart = new Date(year, 0, 1);
  let currentWeekStart = startOfWeek(yearStart, { weekStartsOn: 0 });
  let weekCount = 1;
  
  while (currentWeekStart <= day) {
    if (isSameYear(currentWeekStart, yearStart) || isSameYear(day, yearStart)) {
      weekNumber = weekCount;
    }
    currentWeekStart = addWeeks(currentWeekStart, 1);
    weekCount++;
    if (weekCount > 54) break;
  }
  
  // Initialize metadata tracking
  const metadata = {
    dateHeaderBounds: {},
    dayNameBounds: {},
    weekBounds: {},
    lineBounds: {},
    totalHeight: 0,
    bottomY: 0
  };
  
  // Determine colors based on style
  const titleColor = themeColors?.primary;
  const secondaryColor = themeColors?.secondary;
  const accentColor = themeColors?.accent;
  const bgColor = themeColors?.accent;
  
  let headerTextColor = titleColor;
  if (styleConfig?.textColor === 'white') {
    headerTextColor = { r: 255, g: 255, b: 255 };
  } else if (styleConfig?.textColor === 'secondary') {
    headerTextColor = secondaryColor;
  }
  
  // STEP 1: Draw watermark (if watermark-overlay style)
  if (styleConfig?.showWatermark) {
    pdf?.saveGraphicsState();
    
    // Set watermark opacity
    const gState = new pdf.GState({ opacity: styleConfig?.watermarkOpacity });
    pdf?.setGState(gState);
    
    // Draw watermark day name
    pdf?.setTextColor(titleColor?.r, titleColor?.g, titleColor?.b);
    pdf?.setFontSize(styleConfig?.watermarkFontSize);
    setFontWithFallback(pdf, fontConfig?.title, styling?.dateFontWeight);
    
    const watermarkX = pageWidth / 2;
    const watermarkY = layout?.yPosition + styleConfig?.watermarkOffset;
    const watermarkWidth = pdf?.getTextWidth(dayName);
    
    pdf?.text(dayName, watermarkX - (watermarkWidth / 2), watermarkY);
    
    pdf?.restoreGraphicsState();
  }
  
  // STEP 2: Draw background (if title-bg style)
  if (styleConfig?.backgroundColor !== 'none') {
    // Calculate background dimensions
    pdf?.setFontSize(styling?.dateFontSize);
    setFontWithFallback(pdf, fontConfig?.title, styling?.dateFontWeight);
    
    const monthWidth = pdf?.getTextWidth(monthName);
    const pipeWidth = pdf?.getTextWidth(' ');
    const dayWidth = pdf?.getTextWidth(dayNum);
    const yearWidth = pdf?.getTextWidth(yearText);
    const totalDateWidth = monthWidth + pipeWidth + dayWidth + pipeWidth + yearWidth;
    
    pdf?.setFontSize(styling?.dayNameFontSize);
    setFontWithFallback(pdf, fontConfig?.title, styling?.dayNameFontWeight);
    const dayNameWidth = pdf?.getTextWidth(dayName);
    
    const maxTextWidth = Math.max(totalDateWidth, dayNameWidth);
    const bgX = 20 - styling?.backgroundPadding;
    const bgY = layout?.yPosition - styling?.dateFontSize - styling?.backgroundPadding;
    let bgWidth = maxTextWidth + (styling?.backgroundPadding * 2);
    let bgHeight = styling?.dateFontSize + layout?.dayNameOffset + styling?.dayNameFontSize + (styling?.backgroundPadding * 2) -18;
    
    // Draw background
    let bgColorObj = titleColor;
    if (styleConfig?.backgroundColor === 'secondary') {
      bgColorObj = secondaryColor;
    } else if (styleConfig?.backgroundColor === 'accent') {
      bgColorObj = accentColor;
    }
    
    pdf?.setFillColor(bgColorObj?.r, bgColorObj?.g, bgColorObj?.b);
    pdf?.rect(bgX, bgY, bgWidth, bgHeight, 'F');
    pdf?.setLineWidth(2);
    //pdf?.setDrawColor(bgColorObj?.r, bgColorObj?.g, bgColorObj?.b);
    //pdf?.setFillColor(bgColorObj?.r, bgColorObj?.g, bgColorObj?.b);
    //pdf?.line(bgX, bgY+bgHeight, pageWidth-20, bgY+bgHeight, "F");
    pdf?.rect(bgX, bgY+bgHeight, pageWidth-37, 0.4, 'F');
    
  }
  
  // STEP 3: Draw date header (Month | Day | Year)
  const startX = 20;
  const dateHeaderY = layout?.yPosition;
  
  pdf?.setFontSize(styling?.dateFontSize);
  setFontWithFallback(pdf, fontConfig?.title, styling?.dateFontWeight);
  pdf?.setTextColor(headerTextColor?.r, headerTextColor?.g, headerTextColor?.b);
  
  const monthWidth = pdf?.getTextWidth(monthName);
  const pipeWidth = pdf?.getTextWidth(' ');
  const pipeWidth2 = pdf?.getTextWidth(', ');
  const dayWidth = pdf?.getTextWidth(dayNum);
  const yearWidth = pdf?.getTextWidth(yearText);
  
  // Format date based on dateFormat setting
  let dateElements = [];
  if (styling?.dateFormat === 'International') {
    // International: Day Month Year
    dateElements = [
      { text: dayNum, width: dayWidth, linkType: null },
      { text: ' ', width: pipeWidth, linkType: null },
      { text: monthName, width: monthWidth, linkType: 'month' },
      { text: ', ', width: pipeWidth2, linkType: null },
      { text: yearText, width: yearWidth, linkType: 'year' }
    ];
  } else {
    // US: Month Day Year
     dateElements= [
      { text: monthName, width: monthWidth, linkType: 'month' },
      { text: ' ', width: pipeWidth, linkType: null },
      { text: dayNum, width: dayWidth, linkType: null },
      { text: ', ', width: pipeWidth2, linkType: null },
      { text: yearText, width: yearWidth, linkType: 'year' }
    ];
  }
  
  let currentX = startX;
  
  // Draw date elements with links
  for (const element of dateElements) {
    if (element?.linkType === 'month') {
      const monthPageNum = pageLinks?.get(`month-${monthIndex}`);
      if (monthPageNum && mode !== 'preview') {
        pdf?.link(currentX, dateHeaderY - 14, element?.width, 18, { pageNumber: monthPageNum });
      }
    } else if (element?.linkType === 'year') {
      const yearOverviewPage = pageLinks?.get('year-overview');
      if (yearOverviewPage && mode !== 'preview') {
        pdf?.link(currentX, dateHeaderY - 14, element?.width, 18, { pageNumber: yearOverviewPage });
      }
    }
    
    pdf?.text(element?.text, currentX, dateHeaderY);
    currentX += element?.width;
  }
  
  const totalDateWidth = currentX - startX;
  
  metadata.dateHeaderBounds = {
    x: startX,
    y: dateHeaderY - styling?.dateFontSize,
    width: totalDateWidth,
    height: styling?.dateFontSize
  };
  
  // STEP 4: Draw day name below date
  const dayNameY = dateHeaderY + layout?.dayNameOffset;
  
  pdf?.setFontSize(styling?.dayNameFontSize);
  setFontWithFallback(pdf, fontConfig?.title, styling?.dayNameFontWeight);
  pdf?.setTextColor(headerTextColor?.r, headerTextColor?.g, headerTextColor?.b);
  
  const dayNameWidth = pdf?.getTextWidth(dayName);
  pdf?.text(dayName, startX, dayNameY);
  
  metadata.dayNameBounds = {
    x: startX,
    y: dayNameY - styling?.dayNameFontSize,
    width: dayNameWidth,
    height: styling?.dayNameFontSize
  };
  
  // STEP 5: Draw week number at top right
  const weekText = `Week ${weekNumber}`;
  
  pdf?.setFontSize(styling?.weekFontSize);
  setFontWithFallback(pdf, fontConfig?.title, styling?.weekFontWeight);
  pdf?.setTextColor(secondaryColor?.r, secondaryColor?.g, secondaryColor?.b);
  ////----
  
  const weekTextWidth = pdf?.getTextWidth(weekText);
  const weekTextX = layout?.weekPosition === 'auto-right' 
    ? pageWidth - layout?.weekRightMargin - weekTextWidth 
    : layout?.weekPosition;
  const weekTextY = layout?.yPosition + layout?.weekYOffset;
  
  // Add clickable link to week overview page
  const weekPageNum = pageLinks?.get(`week-${weekNumber}`);
  if (weekPageNum && mode !== 'preview') {
    pdf?.link(weekTextX, weekTextY - 10, weekTextWidth, 12, { pageNumber: weekPageNum });
  }
  
  pdf?.text(weekText, weekTextX, weekTextY);
  
  metadata.weekBounds = {
    x: weekTextX,
    y: weekTextY - styling?.weekFontSize,
    width: weekTextWidth,
    height: styling?.weekFontSize
  };
  
  // STEP 6: Draw decorative line (if applicable)
  if (styleConfig?.showDecorateLine) {
    const lineY = dayNameY + layout?.lineVerticalSpacing;
    const lineStartX = startX;
    const lineEndX = pageWidth - 20;
    
    // Draw accent dot for minimalist-accent style
    if (styling?.style === 'minimalist-accent') {
      pdf?.setFillColor(titleColor?.r, titleColor?.g, titleColor?.b);
      const accentX = styleConfig?.accentOffset;
      const accentY = lineY - (styleConfig?.accentSize / 2);
      pdf?.circle(accentX, accentY, styleConfig?.accentSize / 2, 'F');
    }
    
    // Draw decorative line with selected style
    pdf?.setLineWidth(styling?.lineThickness);
    
    if (styling?.lineStyle === 'solid') {
      pdf?.setDrawColor(titleColor?.r, titleColor?.g, titleColor?.b);
      pdf?.line(lineStartX, lineY, lineEndX, lineY);
    } else if (styling?.lineStyle === 'dotted') {
      pdf?.setLineDash([0.5, 2], 0);
      pdf?.setDrawColor(titleColor?.r, titleColor?.g, titleColor?.b);
      pdf?.line(lineStartX, lineY, lineEndX, lineY);
      pdf?.setLineDash([]);
    } else if (styling?.lineStyle === 'dashed') {
      pdf?.setLineDash([3, 2], 0);
      pdf?.setDrawColor(titleColor?.r, titleColor?.g, titleColor?.b);
      pdf?.line(lineStartX, lineY, lineEndX, lineY);
      pdf?.setLineDash([]);
    } else if (styling?.lineStyle === 'gradient') {
      // Gradient line: multiple segments with varying opacity
      const segments = 10;
      const segmentWidth = (lineEndX - lineStartX) / segments;
      
      for (let i = 0; i < segments; i++) {
        const opacity = 1 - (i / segments);
        pdf?.saveGraphicsState();
        const gState = new pdf.GState({ opacity });
        pdf?.setGState(gState);
        pdf?.setDrawColor(titleColor?.r, titleColor?.g, titleColor?.b);
        pdf?.line(
          lineStartX + (i * segmentWidth),
          lineY,
          lineStartX + ((i + 1) * segmentWidth),
          lineY
        );
        pdf?.restoreGraphicsState();
      }
    }
    
    metadata.lineBounds = {
      x: lineStartX,
      y: lineY - styling?.lineThickness,
      width: lineEndX - lineStartX,
      height: styling?.lineThickness
    };
    
    metadata.bottomY = lineY + layout?.lineVerticalSpacing;
  } else {
    metadata.bottomY = dayNameY + layout?.lineVerticalSpacing;
  }
  
  // Calculate total height
  metadata.totalHeight = metadata?.bottomY - (layout?.yPosition - styling?.dateFontSize);
  
  return metadata;
};

/**
 * Generates daily navigation icons with flexible layout and styling options
 * 
 * FIXED: Corrected icon-bg rendering issue where background color matched icon color
 * causing icons to be invisible. Background now uses white color for contrast.
 */
const generateDailyNavIcons = async ({
  pdf,
  iconAliasManager,
  iconConfigs,
  layout = {
    startX: 20,
    startY: 38,
    orientation: 'horizontal',
    spacing: 45,
    iconSize: 5,
    fontSize: 10,
    alignment: 'right'
  },
  styling = {
    type: 'none',
    width: 'consistent',
    padding: 2,
    borderRadius: 2,
    borderWidth: 0.5
  },
  colors = {
    enabled: { r: 30, g: 58, b: 95 },
    disabled: { r: 200, g: 200, b: 200 },
    background: { r: 226, g: 232, b: 240 }
  },
  fontConfig = {
    title: 'Roboto',
    content: 'Roboto'
  },
  mode = 'full'
}) => {
  // Validation: Check icon names exist in IconAliasManager
  for (const config of iconConfigs) {
    const cacheKey = iconAliasManager?.getCacheKey(
      config?.icon,
      ICON_SIZES?.dailyIcons,
      config?.enabled !== false ? colors?.enabled : colors?.disabled,
      colors?.background
    );
    
    if (!cacheKey) {
      throw new Error(`Icon '${config?.icon}' not found in IconAliasManager. Available icons must be loaded before calling generateDailyNavIcons.`);
    }
  }
  
  // Validation: Check page numbers are valid
  for (const config of iconConfigs) {
    if (config?.pageNum !== null && config?.pageNum !== undefined) {
      if (typeof config?.pageNum !== 'number' || config?.pageNum < 1) {
        throw new Error(`Invalid page number '${config?.pageNum}' for icon '${config?.icon}'. Page numbers must be positive integers or null.`);
      }
    }
  }
  
  // Get page dimensions for overflow checking
  const pageWidth = pdf?.internal?.pageSize?.getWidth();
  const pageHeight = pdf?.internal?.pageSize?.getHeight();
  
  // Calculate consistent width if needed
  let consistentWidth = 0;
  if (styling?.width === 'consistent') {
    // Find the widest icon+text combination
    pdf?.setFontSize(layout?.fontSize);
    setFontWithFallback(pdf, fontConfig?.content, 'normal');
    
    for (const config of iconConfigs) {
      const textWidth = pdf?.getTextWidth(config?.label || '');
      let totalWidth = layout?.iconSize + 2 + textWidth; // icon + gap + text
      consistentWidth = Math.max(consistentWidth, totalWidth);
    }
    
    // Add padding for button styling
    if (styling?.type !== 'none') {
      consistentWidth += (styling?.padding * 2);
    }
  }
  
  // Initialize metadata tracking
  const iconPositions = [];
  let currentX = layout?.startX;
  let currentY = layout?.startY;
  let maxWidth = 0;
  let maxHeight = 0;
  
  // Calculate starting position for right alignment (horizontal only)
  if (layout?.orientation === 'horizontal' && layout?.alignment === 'right') {
    // Calculate total width needed
    let totalWidth = 0;
    for (let i = 0; i < iconConfigs?.length; i++) {
      const config = iconConfigs?.[i];
      let itemWidth = 0;
      
      if (styling?.width === 'consistent') {
        itemWidth = consistentWidth;
      } else {
        pdf?.setFontSize(layout?.fontSize);
        setFontWithFallback(pdf, fontConfig?.content, 'normal');
        const textWidth = pdf?.getTextWidth(config?.label || '');
        itemWidth = layout?.iconSize + 2 + textWidth;
        
        if (styling?.type !== 'none') {
          itemWidth += (styling?.padding * 2);
        }
      }
      
      totalWidth += itemWidth;
      if (i < iconConfigs?.length - 1) {
        totalWidth += layout?.spacing;
      }
    }
    
    currentX = pageWidth - layout?.startX - totalWidth;
  } else if (layout?.orientation === 'horizontal' && layout?.alignment === 'center') {
    // Calculate total width needed
    let totalWidth = 0;
    for (let i = 0; i < iconConfigs?.length; i++) {
      const config = iconConfigs?.[i];
      let itemWidth = 0;
      
      if (styling?.width === 'consistent') {
        itemWidth = consistentWidth;
      } else {
        pdf?.setFontSize(layout?.fontSize);
        setFontWithFallback(pdf, fontConfig?.content, 'normal');
        const textWidth = pdf?.getTextWidth(config?.label || '');
        itemWidth = layout?.iconSize + 2 + textWidth;
        
        if (styling?.type !== 'none') {
          itemWidth += (styling?.padding * 2);
        }
      }
      
      totalWidth += itemWidth;
      if (i < iconConfigs?.length - 1) {
        totalWidth += layout?.spacing;
      }
    }
    
    currentX = (pageWidth - totalWidth) / 2;
  }
  
  // Store initial position for bounds calculation
  const startX = currentX;
  const startY = currentY;
  
  // Render each icon
  for (let i = 0; i < iconConfigs?.length; i++) {
    const config = iconConfigs?.[i];
    const isEnabled = config?.enabled !== false;
    
    // Determine colors
    const iconColor = isEnabled ? colors?.enabled : colors?.disabled;
    
    // Set font for text measurement
    pdf?.setFontSize(layout?.fontSize);
    setFontWithFallback(pdf, fontConfig?.content, 'normal');
    pdf?.setTextColor(iconColor?.r, iconColor?.g, iconColor?.b);
    
    // Calculate dimensions
    const textWidth = pdf?.getTextWidth(config?.label || '');
    let itemWidth = 0;
    let itemHeight = layout?.iconSize;
    
    if (styling?.width === 'consistent') {
      itemWidth = consistentWidth;
    } else {
      itemWidth = layout?.iconSize + 2 + textWidth;
      if (styling?.type !== 'none') {
        itemWidth += (styling?.padding * 2);
      }
    }
    
    // Calculate button background dimensions based on styling type
    let bgX = currentX;
    let bgY = currentY - layout?.iconSize;
    let bgWidth = itemWidth;
    let bgHeight = layout?.iconSize + (styling?.padding * 2);
    
    if (styling?.type === 'icon-bg') {
      // Background only around icon
      bgWidth = layout?.iconSize + (styling?.padding * 2);
      bgHeight = layout?.iconSize + (styling?.padding * 2);
    } else if (styling?.type === 'icon-text-bg' || styling?.type === 'filled' || styling?.type === 'border') {
      // Background around icon + text
      bgWidth = itemWidth;
      bgHeight = Math.max(layout?.iconSize, layout?.fontSize * 0.35) + (styling?.padding * 2);
    }
    
    // FIXED: Draw button background with proper color contrast
    if (styling?.type !== 'none') {
      pdf?.setDrawColor(iconColor?.r, iconColor?.g, iconColor?.b);
      pdf?.setLineWidth(styling?.borderWidth);
      
      if (styling?.type === 'filled') {
        // Filled style: background in icon color, text in background color (white)
        pdf?.setFillColor(iconColor?.r, iconColor?.g, iconColor?.b);
        pdf?.roundedRect(bgX, bgY, bgWidth, bgHeight, styling?.borderRadius, styling?.borderRadius, 'FD');
        // Change text color to background for filled style
        pdf?.setTextColor(colors?.background?.r, colors?.background?.g, colors?.background?.b);
      } else if (styling?.type === 'border') {
        // Border style: no fill, just outline
        pdf?.roundedRect(bgX, bgY, bgWidth, bgHeight, styling?.borderRadius, styling?.borderRadius, 'S');
      } else {
        // CRITICAL FIX: icon-bg or icon-text-bg - use WHITE background for contrast
        // This ensures the colored icon is visible against the background
        pdf?.setFillColor(255, 255, 255); // White background
        pdf?.roundedRect(bgX, bgY, bgWidth, bgHeight, styling?.borderRadius, styling?.borderRadius, 'FD');
      }
    }
    
    // Get icon alias key
    const aliasKey = iconAliasManager?.getCacheKey(
      config?.icon,
      ICON_SIZES?.dailyIcons,
      iconColor,
      colors?.background
    );
    
    // Calculate icon position (with padding if button styling)
    let iconX = currentX;
    let iconY = currentY - layout?.iconSize;
    
    if (styling?.type !== 'none') {
      iconX += styling?.padding;
      iconY += styling?.padding;
    }
    
    // Draw icon - NOW VISIBLE because background is white, icon is colored
    iconAliasManager?.addToPage(aliasKey, iconX, iconY, layout?.iconSize, layout?.iconSize);
    
    // Calculate text position
    const textX = iconX + layout?.iconSize + 2;
    const textY = currentY - (layout?.iconSize / 2) + (layout?.fontSize * 0.15);
    
    // Draw text
    if (styling?.type === 'filled') {
      pdf?.setTextColor(colors?.background?.r, colors?.background?.g, colors?.background?.b);
    } else {
      pdf?.setTextColor(iconColor?.r, iconColor?.g, iconColor?.b);
    }
    pdf?.text(config?.label || '', textX, textY);
    
    // Add clickable link if page number exists and mode is not preview
    if (config?.pageNum && mode !== 'preview' && isEnabled) {
      const linkX = bgX;
      const linkY = bgY;
      const linkWidth = bgWidth;
      const linkHeight = bgHeight;
      
      pdf?.link(linkX, linkY, linkWidth, linkHeight, { pageNumber: config?.pageNum });
    }
    
    // Track position metadata
    iconPositions?.push({
      icon: config?.icon,
      label: config?.label,
      x: currentX,
      y: currentY,
      width: itemWidth,
      height: itemHeight,
      enabled: isEnabled
    });
    
    // Update position for next icon
    if (layout?.orientation === 'horizontal') {
      currentX += itemWidth + layout?.spacing;
      maxWidth = currentX - startX;
      maxHeight = Math.max(maxHeight, itemHeight);
    } else {
      // Vertical stacking
      currentY += itemHeight + layout?.spacing;
      maxWidth = Math.max(maxWidth, itemWidth);
      maxHeight = currentY - startY;
    }
  }
  
  // Calculate final bounds
  const endX = layout?.orientation === 'horizontal' ? currentX - layout?.spacing : startX + maxWidth;
  const endY = layout?.orientation === 'vertical' ? currentY - layout?.spacing : startY + maxHeight;
  
  // Warning: Check for layout conflicts (overflow)
  const overflowRight = endX > pageWidth - 10; // 10mm margin
  const overflowBottom = endY > pageHeight - 10; // 10mm margin
  
  if (overflowRight || overflowBottom) {
    const overflowMsg = [];
    if (overflowRight) overflowMsg?.push(`horizontal overflow by ${(endX - (pageWidth - 10))?.toFixed(2)}mm`);
    if (overflowBottom) overflowMsg?.push(`vertical overflow by ${(endY - (pageHeight - 10))?.toFixed(2)}mm`);
    
    console.warn(`⚠️ Layout conflict detected in generateDailyNavIcons: ${overflowMsg?.join(', ')}. Consider reducing spacing, icon size, or number of icons.`);
  }
  
  // Return metadata
  return {
    bounds: {
      startX,
      startY,
      endX,
      endY,
      width: maxWidth,
      height: maxHeight
    },
    iconPositions,
    totalWidth: maxWidth,
    totalHeight: maxHeight
  };
};

const generateBackgroundText = async (pdf, themeColors, fontConfig, backgroundText = "", backgroundTextX = 220, backgroundTextY = 220, backgroundTextSize = 150, angle=90) => {
  pdf?.saveGraphicsState();
  pdf?.setTextColor(255, 255, 255);
  pdf?.setFontSize(backgroundTextSize);
  setFontWithFallback(pdf, fontConfig?.title, 'bold');
  
  //let backgroundText = getFontFamily(plannerData?.personalization?.titleFont || plannerData?.personalization?.font, 'title') || " ";

  pdf?.text(backgroundText, backgroundTextX, backgroundTextY, {
    angle: angle,
    align: 'center'
  });
  pdf?.restoreGraphicsState();
}

/**
 * Generates the index page with dynamic content listing
 * IMPORTANT: This page comes after cover page but before habit trackers
 */
const generateIndexPage = async (pdf, plannerData, year, pageLinks, themeColors, fontConfig, selectedHabits, parsedLayoutConfig, iconAliasManager, mode = 'full') => {
  const pageWidth = pdf?.internal?.pageSize?.getWidth();
  const pageHeight = pdf?.internal?.pageSize?.getHeight();
  
  // Background color (secondary)
  const bgColor = themeColors?.accent;
  pdf?.setFillColor(bgColor?.r, bgColor?.g, bgColor?.b);
  pdf?.rect(0, 0, pageWidth, pageHeight, 'F');
  
  const titleColor = themeColors?.primary;
  const accentColor = themeColors?.secondary;
  const circleTextColor = themeColors?.accent;
  const userName = plannerData?.personalization?.name || 'User';
  
  let currentY = 20;
  const leftMargin = 20;
  const rightMargin = 20;
  const contentWidth = pageWidth - leftMargin - rightMargin;
  const fontSizing = 0.89; //Size Factor for Fonts > 1 = larger < 1 = shrink

  // Header: "Custom Planner for [Name] Index"
  pdf?.setTextColor(titleColor?.r, titleColor?.g, titleColor?.b);
  pdf?.setFontSize(14 * fontSizing);
  setFontWithFallback(pdf, fontConfig?.content, 'normal');
  const headerText = `A Customized Planner for ${userName}`;
  pdf?.text(headerText, leftMargin, currentY);
  currentY += 5 * fontSizing;
  
  // Horizontal line in accent color
  pdf?.setDrawColor(accentColor?.r, accentColor?.g, accentColor?.b);
  pdf?.setLineWidth(0.5);
  pdf?.line(leftMargin, currentY, pageWidth - rightMargin, currentY);
  currentY += 10 * fontSizing;
  
  // Sub Header: "Index & How to Use"
  pdf?.setFontSize(24* fontSizing);
  setFontWithFallback(pdf, fontConfig?.title, 'bold');
  const subHeaderText = 'Index';
  const subHeaderWidth = pdf?.getTextWidth(subHeaderText);
  pdf?.text(subHeaderText, (pageWidth - subHeaderWidth) / 2, currentY);
  currentY += 12 * fontSizing;
 
  // Index content with clickable links
  pdf?.setFontSize(11 * fontSizing);
  setFontWithFallback(pdf, fontConfig?.content, 'normal');
  
  let dotArray = [0.3, 1];
  let dotsStartX =0;
 
  // Cover Page
  const coverPageNum = pageLinks?.get('title');
  if (coverPageNum) {
    const coverText = 'Cover Page';
    const coverPageText = `${coverPageNum}`;
    const coverTextWidth = pdf?.getTextWidth(coverText);
    const pageNumWidth = pdf?.getTextWidth(coverPageText);
    
    pdf?.text('• ', leftMargin, currentY);
    pdf?.text(coverText, leftMargin + 5, currentY);
    
    // Dotted line
    dotsStartX = leftMargin + 5 + coverTextWidth + 5;
    let dotsEndX = pageWidth - rightMargin - 5;
    pdf?.setLineDash(dotArray, 0);
    pdf?.setDrawColor(titleColor?.r, titleColor?.g, titleColor?.b);
    pdf?.setLineWidth(0.2);
    pdf?.line(dotsStartX, currentY, dotsEndX, currentY);
    pdf?.setLineDash([]);
    
    pdf?.text(coverPageText, pageWidth - rightMargin - pageNumWidth, currentY);
    
    if (mode !== 'preview') {
      pdf?.link(leftMargin + 5, currentY - 4, pageWidth-rightMargin-leftMargin, 6, { pageNumber: coverPageNum });
    }
    
    currentY += 6 * fontSizing;
  }
  
  // Index (no link)
  const indexPageNum = pageLinks?.get('index');
  let dotsEndX = pageWidth - rightMargin - 5;
  pdf?.text('• ', leftMargin, currentY);
  pdf?.text('Index', leftMargin+5, currentY);
  const indexTextWidth = pdf?.getTextWidth('Index') + 5;
  dotsStartX = leftMargin + 5 + indexTextWidth + 5;
  pdf?.setLineDash(dotArray, 0);
  pdf?.line(dotsStartX, currentY, dotsEndX, currentY);
  pdf?.setLineDash([]);
  let indexPageNumTextWidth = indexPageNum?.toString();
  if (mode !== 'preview') {

    pdf?.text(indexPageNum?.toString(), pageWidth - rightMargin - indexPageNumTextWidth, currentY);
    pdf?.link(leftMargin + 5, currentY - 4, pageWidth-rightMargin - leftMargin, 6, { pageNumber: indexPageNum });
  }
  currentY += 6 * fontSizing;

    
  // Instruction
  const instructionPageNum = pageLinks?.get('instruction');
  dotsEndX = pageWidth - rightMargin - 5;
  pdf?.text('• ', leftMargin, currentY);
  pdf?.text('Instructions', leftMargin+5, currentY);
  const instructionTextWidth = pdf?.getTextWidth('Instructions') + 5;
  dotsStartX = leftMargin + 5 + instructionTextWidth + 5;
  pdf?.setLineDash(dotArray, 0);
  pdf?.line(dotsStartX, currentY, dotsEndX, currentY);
  pdf?.setLineDash([]);
  if (mode !== 'preview') {
    let instructionPageNumTextWidth =  pdf?.getTextWidth(instructionPageNum?.toString());
    pdf?.text(instructionPageNum?.toString(), pageWidth - rightMargin - instructionPageNumTextWidth, currentY);
    pdf?.link(leftMargin + 5, currentY - 4, pageWidth-rightMargin-leftMargin, 6, { pageNumber: instructionPageNum });
  }
  currentY += 6 * fontSizing;
  
  // Habit Pages (with numbered list)
  if (selectedHabits?.length > 0) {
    pdf?.text('• ', leftMargin, currentY);
    pdf?.text('Habit Pages', leftMargin+5, currentY);
    currentY += 6 * fontSizing;
    
    selectedHabits?.forEach((habit, index) => {
      const habitPageNum = pageLinks?.get(`habit-${index}`);
      if (habitPageNum) {
        const habitText = `${index + 1}. ${habit?.name} Habit`;
        const habitPageText = `${habitPageNum}`;
        const habitTextWidth = pdf?.getTextWidth(habitText);
        const pageNumWidth = pdf?.getTextWidth(habitPageText);
        
        pdf?.text('  ◦ ', leftMargin, currentY);
        pdf?.text(habitText, leftMargin + 10, currentY);
        
        // Dotted line
        dotsStartX = leftMargin + 10 + habitTextWidth + 5;
        //const dotsEndX = pageWidth - rightMargin - pageNumWidth - 5;
        pdf?.setLineDash(dotArray, 0);
        pdf?.line(dotsStartX, currentY, dotsEndX, currentY);
        pdf?.setLineDash([]);
        
        pdf?.text(habitPageText, pageWidth - rightMargin - pageNumWidth, currentY);
        
        if (mode !== 'preview') {
          pdf?.link(leftMargin + 10, currentY - 4, pageWidth-rightMargin - leftMargin, 6, { pageNumber: habitPageNum });
        }
        
        currentY += 6 * fontSizing;
      }
    });
  }
  
  // Year Calendar
  const yearPageNum = pageLinks?.get('year-overview');
  if (yearPageNum) {
    const yearText = `${year} Calendar`;
    const yearPageText = `${yearPageNum}`;
    const yearTextWidth = pdf?.getTextWidth(yearText);
    const pageNumWidth = pdf?.getTextWidth(yearPageText);
    
    pdf?.text('• ', leftMargin, currentY);
    pdf?.text(yearText, leftMargin + 5, currentY);
    
    // Dotted line
    dotsStartX = leftMargin + 5 + yearTextWidth + 5;
    let dotsEndX = pageWidth - rightMargin - pageNumWidth - 5;
    pdf?.setLineDash(dotArray, 0);
    pdf?.line(dotsStartX, currentY, dotsEndX, currentY );
    pdf?.setLineDash([]);
    
    pdf?.text(yearPageText, pageWidth - rightMargin - pageNumWidth, currentY);
    
    if (mode !== 'preview') {
      pdf?.link(leftMargin + 5, currentY - 4, pageWidth-rightMargin-leftMargin, 6, { pageNumber: yearPageNum });
    }
    
    currentY += 8  * fontSizing;
  }
  
  // Monthly Calendars
  if (parsedLayoutConfig?.monthPlannerEnabled) {
    for (let monthIndex = 0; monthIndex < 12; monthIndex++) {
      const monthDate = new Date(year, monthIndex, 1);
      const monthName = format(monthDate, 'MMMM');
      const monthPageNum = pageLinks?.get(`month-${monthIndex}`);
      
      if (monthPageNum) {
        const monthText = `${monthName}`;
        const monthPageText = `${monthPageNum}`;
        const monthTextWidth = pdf?.getTextWidth(monthText);
        const pageNumWidth = pdf?.getTextWidth(monthPageText);
        
        pdf?.text('• ', leftMargin, currentY);
        pdf?.text(monthText, leftMargin + 5, currentY);
        
        // Dotted line
        dotsStartX = leftMargin + 5 + monthTextWidth + 5;
        let dotsEndX = pageWidth - rightMargin - pageNumWidth - 5;
        pdf?.setLineDash(dotArray, 0);
        pdf?.line(dotsStartX, currentY, dotsEndX, currentY);
        pdf?.setLineDash([]);
        
        pdf?.text(monthPageText, pageWidth - rightMargin - pageNumWidth, currentY);
        
        if (mode !== 'preview') {
          pdf?.link(leftMargin + 5, currentY - 4, pageWidth-rightMargin-leftMargin, 6, { pageNumber: monthPageNum });
        }
        
        currentY += 6 * fontSizing;
      }
    }
  }
  // ################################################################
  // Daily Pages (organized by month in 2 columns)
  pdf?.setFontSize(11 * fontSizing);
  
  if (parsedLayoutConfig?.dailyPlannerEnabled) {
	currentY += 6 * fontSizing;
    pdf?.text('• ', leftMargin, currentY);
	pdf?.text('Daily Pages:', leftMargin +5, currentY);
    currentY += 5;
    
    // Two-column layout for daily pages
    const columnWidth = (contentWidth - 10) / 2;
    let leftColumnY = currentY;
    let rightColumnY = currentY;
    let grouper = 1;
	
    for (let monthIndex = 0; monthIndex < 12; monthIndex++) {
      const monthDate = new Date(year, monthIndex, 1);
	  const lastDay = getDate(endOfMonth(monthDate)); 
      const monthName = format(monthDate, 'MMM');
	  const firstDay = 1;
	  const linkCircleRadius = 2.15;
	  const linkCircleSpacing = 4.9;
      pdf?.setLineWidth(0.2);

      // Determine which column to use (Jan-Jun left, Jul-Dec right)
      const isLeftColumn = monthIndex < 13;
      const columnX = isLeftColumn ? leftMargin : leftMargin + columnWidth + 15;
      let columnY = isLeftColumn ? leftColumnY : rightColumnY;
      
      // Month bullet point
      pdf?.setFontSize(11 * fontSizing);
      setFontWithFallback(pdf, fontConfig?.content, 'normal');
      pdf?.text(`${monthName}`, columnX +6, columnY);
      //columnY += 5;
      
      // Daily page types (as sub-bullets)
      pdf?.setFontSize(11 * fontSizing);
      setFontWithFallback(pdf, fontConfig?.content, 'normal');
      
      if (parsedLayoutConfig?.scheduleEnabled || parsedLayoutConfig?.combinationEnabled) {
        const schedulePageNum = pageLinks?.get(`day-schedule-${monthIndex}-${firstDay}`);
        if (schedulePageNum) {
          //pdf?.text(`    ${scheduleText}`, columnX, columnY);
          
		  const bgColor = themeColors?.accent;
      

			
			for (let i = 1; i <= lastDay; i++){
				let dayPageNum = pageLinks?.get(`day-schedule-${monthIndex}-${i}`);
				//pdf?.setDrawColor(accentColor?.r+50, accentColor?.g+50, accentColor?.b+50);
        pdf?.setDrawColor(190, 190, 190);
        
				pdf?.setFillColor(circleTextColor?.r, circleTextColor?.g, circleTextColor?.b);
        pdf?.setFillColor(220, 220, 220);
				const circleX = columnX + 14.5 + i*linkCircleSpacing *1.08;
				
				pdf?.roundedRect(circleX - 5, columnY-2.7, linkCircleRadius *2, linkCircleRadius *2, 0.8, 0.8, 'DF');
				
				pdf?.setTextColor(titleColor?.r, titleColor?.g, titleColor?.b);
				pdf?.text(`${i}`, circleX-linkCircleRadius+4.2 - 5, columnY+linkCircleRadius-1.8,{
					align: 'center'
				});
        if (mode !== 'preview') {
				pdf?.link(circleX-5, columnY-2.7, linkCircleRadius *2.1, linkCircleRadius *2.1, { pageNumber: dayPageNum });
        }
				pdf?.setTextColor(titleColor?.r, titleColor?.g, titleColor?.b);
			}
          
		  grouper+=1;
		  
          if (grouper > 3){
			columnY += 10  * fontSizing;
			grouper = 1;
		  }else{
			columnY += 6  * fontSizing;
		  }
        }
      }
      
	  /*
      if (parsedLayoutConfig?.taskEnabled) {
        const taskPageNum = pageLinks?.get(`day-tasks-${monthIndex}-${firstDay}`);
        if (taskPageNum) {
          const taskText = '[Tasks]';
          pdf?.text(`    ${taskText}`, columnX, columnY);
          
          if (mode !== 'preview') {
            const textWidth = pdf?.getTextWidth(taskText);
            pdf?.link(columnX + 10, columnY - 3, textWidth, 5, { pageNumber: taskPageNum });
          }
          
          columnY += 4 * fontSizing;
        }
      }
      
      if (parsedLayoutConfig?.journalEnabled) {
        const journalPageNum = pageLinks?.get(`day-journal-${monthIndex}-${firstDay}`);
        if (journalPageNum) {
          const journalText = '[Journal]';
          pdf?.text(`    ${journalText}`, columnX, columnY);
          
          if (mode !== 'preview') {
            const textWidth = pdf?.getTextWidth(journalText);
            pdf?.link(columnX + 10, columnY - 3, textWidth, 5, { pageNumber: journalPageNum });
          }
          
          columnY += 4 * fontSizing;
        }
      }
      
      if (parsedLayoutConfig?.lifeHackEnabled) {
        const lifeHackPageNum = pageLinks?.get(`day-lifehack-${monthIndex}-${firstDay}`);
        if (lifeHackPageNum) {
          const lifeHackText = '[Life Hack]';
          pdf?.text(`    ${lifeHackText}`, columnX, columnY);
          
          if (mode !== 'preview') {
            const textWidth = pdf?.getTextWidth(lifeHackText);
            pdf?.link(columnX + 10, columnY - 3, textWidth, 5, { pageNumber: lifeHackPageNum });
          }
          
          columnY += 4 * fontSizing;
        }
      }
      */
      columnY += 2 * fontSizing; // Extra spacing between months
      
      // Update column Y position
      if (isLeftColumn) {
        leftColumnY = columnY;
      } else {
        rightColumnY = columnY;
      }
    }
    
    // Move currentY to the bottom of the tallest column
    currentY = Math.max(leftColumnY, rightColumnY) + 10;
  }
  
  // Footer
  pdf?.setFontSize(8 * fontSizing);
  setFontWithFallback(pdf, fontConfig?.content, 'normal');
  pdf?.setTextColor(100, 100, 100);
  pdf?.setFontSize(8.5);
  const footerWidth = pdf?.getTextWidth(footerText);
  pdf?.text(footerText, (pageWidth - footerWidth) / 2, pageHeight - 10);
};



// ####################################################

const generateInstructionPage = async (pdf, plannerData, year, pageLinks, themeColors, fontConfig, selectedHabits, parsedLayoutConfig, iconAliasManager, mode = 'full') => {
  const pageWidth = pdf?.internal?.pageSize?.getWidth();
  const pageHeight = pdf?.internal?.pageSize?.getHeight();
  
  // Background color (secondary)
  const bgColor = themeColors?.accent;
  pdf?.setFillColor(bgColor?.r, bgColor?.g, bgColor?.b);
  pdf?.rect(0, 0, pageWidth, pageHeight, 'F');
  
  const titleColor = themeColors?.primary;
  const accentColor = themeColors?.secondary;
  const userName = plannerData?.personalization?.name || 'User';
  
  let currentY = 20;
  const leftMargin = 20;
  const rightMargin = 20;
  const contentWidth = pageWidth - leftMargin - rightMargin;
  const fontSizing = 0.8; //Size Factor for Fonts > 1 = larger < 1 = shrink

  // Header: "Custom Planner for [Name] Index"
  pdf?.setTextColor(titleColor?.r, titleColor?.g, titleColor?.b);
  pdf?.setFontSize(14 * fontSizing);
  setFontWithFallback(pdf, fontConfig?.content, 'normal');
  const headerText = `Planner for ${userName} Index`;
  pdf?.text(headerText, leftMargin, currentY);
  currentY += 5 * fontSizing;
  
  // Horizontal line in accent color
  pdf?.setDrawColor(accentColor?.r, accentColor?.g, accentColor?.b);
  pdf?.setLineWidth(0.5);
  pdf?.line(leftMargin, currentY, pageWidth - rightMargin, currentY);
  currentY += 10 * fontSizing;
  
  // Sub Header: "How to Use"
  pdf?.setFontSize(30* fontSizing);
  setFontWithFallback(pdf, fontConfig?.title, 'bold');
  const subHeaderText = 'How to Use';
  const subHeaderWidth = pdf?.getTextWidth(subHeaderText);
  pdf?.text(subHeaderText, (pageWidth - subHeaderWidth) / 2, currentY);
  currentY += 15 * fontSizing;
  
  /* Section Header: "How to Use"
  pdf?.setFontSize(16 * fontSizing);
  setFontWithFallback(pdf, fontConfig?.content, 'bold');
  pdf?.text('Index', leftMargin, currentY);
  currentY += 8 * fontSizing;
  */
  // Index content with clickable links
  pdf?.setFontSize(15 * fontSizing);
  setFontWithFallback(pdf, fontConfig?.content, 'normal');
   
  // Instructions Section
  pdf?.setFontSize(16 * fontSizing);
  setFontWithFallback(pdf, fontConfig?.content, 'bold');
  pdf?.setTextColor(titleColor?.r, titleColor?.g, titleColor?.b);
  pdf?.text('Instructions', leftMargin, currentY);
  currentY += 10 * fontSizing;
  
  // Thank you message
  pdf?.setFontSize(14 * fontSizing);
  setFontWithFallback(pdf, fontConfig?.content, 'normal');
  //const thankYouText = `Thank you, ${userName}, for choosing our planner system! This planner has been designed specifically for your needs and preferences.`;
  const thankYouText = `Thank you for choosing our planner system! This planner has been designed specifically for your needs and preferences.`;
  const thankYouLines = pdf?.splitTextToSize(thankYouText, contentWidth);
  
  thankYouLines?.forEach((line) => {
    pdf?.text(line, leftMargin, currentY);
    currentY += 5 * fontSizing;
  });
  
  currentY += 5 * fontSizing;
  
  // Instructions copy
  const instructions = `This digital planner features interactive navigation to help you move seamlessly between sections:

• Clickable Links: Dates are clickable and will take you to specific pages

• Navigation Icons: Use the icons at the top of daily pages to jump between different page types

• Date Navigation: Click on week numbers to access weekly views, and month names for monthly overviews (if included in your customized planner)

• Footer Navigation: Use 'Previous' and 'Next' buttons to move between days of the same page type`;

  const instructionLines = pdf?.splitTextToSize(instructions, contentWidth);
  
  instructionLines?.forEach((line) => {
    pdf?.text(line, leftMargin, currentY);
    currentY += 5 * fontSizing;
  });
  
  // Footer
  pdf?.setFontSize(8 * fontSizing);
  setFontWithFallback(pdf, fontConfig?.content, 'normal');
  pdf?.setTextColor(100, 100, 100);
  pdf?.setFontSize(8.5);
  const footerWidth = pdf?.getTextWidth(footerText);
  pdf?.text(footerText, (pageWidth - footerWidth) / 2, pageHeight - 10);
};

export default generatePlannerPDF;